import { buildChillingCheck } from '@trackeat/shared';
import { usePrinterPreview } from '../../lib/printers/usePrinterPreview.js';
import { useHistory, useLocation } from 'react-router-dom';
import { applyLotCorrections, buildLotCorrectionIndex, labelStockIdentity, type LotCorrectionPrintRequest } from '../../lib/labels/lotCorrections.js';
import { LABEL_HISTORY_PAGE_SIZE, type LabelHistoryHeader, type LabelHistoryEvent } from '../../lib/labels/history.js';
import { useLabelHistory, useLabelHistoryEvents, fetchLabelHistoryEvent } from '../../lib/labels/useLabelHistory.js';
import { recipeFinishedFoodEffect } from '../../lib/labels/recipeFinishedFood.js';
import { toUserMessage } from '../../lib/errors.js';
import { dbg } from '../../lib/debug/debugLog.js';
import { serializePrintError } from '../../lib/labels/printError.js';
import { getDeviceId } from '../../lib/db/local.js';
// LabelsPage — Etichettatura preparazioni cucina (HACCP).
//
// Logica concettuale TrackEAT:
//   1. Una "preparazione" è ciò che il cuoco prepara volta per volta — può partire da una
//      ricetta salvata (template) o essere libera ("oggi faccio la pasta al pomodoro").
//   2. Nel flusso manuale l'utente sceglie i lotti; nel flusso foto apre una
//      sola sessione, scatta tutte le confezioni e prosegue subito. OCR, match
//      e alert vengono elaborati successivamente senza bloccare la stampa.
//   3. Alla conferma: stock_out per ogni lotto consumato, productions row con trace_token,
//      etichetta stampata con QR alla pagina di tracciabilità.
//   4. Apertura confezione e scongelamento consumano il lotto origine e creano
//      un lotto AP/SC su un articolo operativo distinto, senza confonderlo con la materia prima.
//
// Questa pagina sostituisce la precedente griglia 6-tile.

import {
  IonAlert,
  IonBackButton,
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonDatetime,
  IonDatetimeButton,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonNote,
  IonPage,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import {
  add,
  checkmarkCircle,
  closeOutline,
  copyOutline,
  eyeOutline,
  openOutline,
  printOutline,
  qrCodeOutline,
  refreshOutline,
  restaurantOutline,
  cubeOutline,
  snowOutline,
  star,
  archiveOutline,
  trashOutline,
  cameraOutline,
} from 'ionicons/icons';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { useBackGuard } from '../../lib/hooks/useBackGuard.js';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createPrinterAdapter, PREVIEW_PRINTER_ID, PREVIEW_PRINTER_NAME } from '../../lib/adapters/printers/index.js';
import { CAMPIONE_TEMPLATE, buildCampioneLabelData } from '../../lib/labels/campioneTemplate.js';
import {
  DurableLabelPrintExecutionError as DurableLabelPrintError,
  runDurableLabelPrintFlow,
  isLabelPrintIntentActive,
  withActiveLabelPrintIntent,
} from '../../lib/sync/durableLabelPrintFlow.js';
import { useDevicePrinters } from '../../lib/printers/devicePrinters.js';
import { resolveDefaultPrinterId } from '../../lib/printers/device-printers-core.js';
import { filterMaterialFoodsByName } from '../../lib/labels/materialFoodSearch.js';
import {
  isAutoMaterialDerivedFood,
  materialDerivedFoodName,
  materialTransformationFromDerivedFood,
  planMaterialDerivedFood,
  type MaterialTransformation,
} from '../../lib/labels/materialDerivedFood.js';
import {
  collectThawedProductionIds,
  selectFrozenProductions,
} from '../../lib/labels/thawProduction.js';
import { resolveMaterialOperationQuantity } from '../../lib/labels/materialOperationQuantity.js';
import { parseCopyCountInput } from '../../lib/labels/copyCountInput.js';
import {
  StockCommandV1Schema,
  buildConsumeStockCommandV1,
  collettivaLotCode,
  convertQty,
  foodLabelName,
  foodOperationShelfLifeHours,
  generateLpCode,
  generateMaterialLotCode,
  menuSlotForDate,
  recipeOperationShelfLifeHours,
  recipeShelfLifeMinutes,
  type CollettivaPrelabelRow,
  type ReversibleStockMovementV1,
  type StockCommandMasterEffectV1,
  type StockCommandV1,
  type StockConversionSource,
  type StockSourceLineV1,
} from '@trackeat/shared';
import { bluecoopMenuDishes } from '../../lib/collettiva/bluecoopMenu.js';
import {
  useCollettivaEnabled,
  useCollettivaPrelabelList,
  useSetCollettivaPrelabelList,
  useEnqueueMaster,
  useEnqueueStockCommand,
  useEvents,
  useMasters,
  useStockProjection,
  useWarehouseEnabled,
  useProductionLotPhotoEnabled,
} from '../../lib/data/queries.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { canCreateQuickLabelFood } from '../../lib/auth/capabilities.js';
import { tsplProfile } from '../../lib/adapters/printers/printerProfile.js';
import type { RasterOptions } from '../../lib/adapters/printers/labelRaster.js';
import { TsplLabelPreview } from '../../components/TsplLabelPreview.js';
import { FoodEditorModal, type FoodEditorValue } from '../../components/editors/FoodEditorModal.js';
import { useCelebrate } from '../../components/CelebrationToast.js';
import { focusInput, fieldClass } from '../../components/forms.js';
import { SearchPickerModal, PickerTriggerField } from '../../components/SearchPickerModal.js';
import {
  LabelEditorModal,
  clearCustomTemplate,
  loadCustomTemplate,
  saveCustomTemplate,
} from '../../components/LabelEditorModal.js';
import { saveGlobalPreset, loadGlobalPresets } from '../../lib/labelPresets.js';
import { TRACE_BASE_URL, getSupabase } from '../../lib/supabase.js';
import { projectedCarryingCost } from '../../lib/stockCarryingCost.js';
import { stockEventMatchesBusiness } from '../../lib/stockEventBusinessScope.js';
import { hasSufficientLabelStock } from './labelStockGate.js';
import {
  getSyncEngine,
  type PendingProductionLabelPrintOutcome,
  type ProductionLabelPrintReconciliation,
  type ProductionLabelPrintIntent,
} from '../../lib/sync/engine.js';
import { scanBarcode, isBarcodeScanSupported } from '../../lib/barcode/scan.js';
import {
  ProductionLotCaptureModal,
  type CapturedBatchPhoto,
} from '../../components/ProductionLotCaptureModal.js';
import {
  commitStagedProductionLotPhotos,
  discardStagedProductionLotCapture,
  discardStagedProductionLotPhotos,
  drainProductionLotPhotoOutbox,
  stageProductionLotPhotoBatch,
  type StagedProductionLotPhotoBatch,
} from '../../lib/labels/productionLotPhotoOutbox.js';
import { useRecipeFavorites } from '../../lib/labels/recipeFavorites.js';
import { selectPrintableFavoriteRecipes } from '../../lib/labels/recipeFavoritesCore.js';
import {
  lotActivityText,
  materialLotOperationLabel,
} from '../../lib/labels/lotPickerPresentation.js';

// Payload di esempio con la lunghezza del token reale: evita una densità QR
// diversa dalla stampa. Il token di esempio non viene registrato come produzione.
const PREVIEW_TRACE_PLACEHOLDER =
  `${TRACE_BASE_URL}/t/abcdefabcdefabcdefabcdefabcdefab`;

// T-303: la "Pre-stampa collettiva" è una feature dedicata al pilota Bluecoop
// (ristorazione collettiva). Gating per tenant UUID + modulo collettiva attivo.
// In futuro promovibile a flag di tenant se serve ad altri clienti collettiva.
const BLUECOOP_TENANT_ID = 'eeac7f42-0a9e-4db3-b1a2-ef8933ab492d';

// T-303: i 3 "ordinari" della scheda ordine preposto che vengono sostituiti dai
// piatti del giorno (rotazione settimanale). SOLO i 3 generici: le varianti dieta
// ("1° Ordinario diabetico", "iposodico", "no latticini") restano col loro nome.
type OrdCat = 'PRIMI' | 'SECONDI' | 'CONTORNI';
function ordinarioCategory(name: string): OrdCat | null {
  const n = name.toLowerCase().replace(/[°º]/g, '').replace(/\s+/g, ' ').trim();
  if (n === '1 ordinario' || n === 'primo ordinario') return 'PRIMI';
  if (n === '2 ordinario' || n === 'secondo ordinario') return 'SECONDI';
  if (n === 'contorno ordinario') return 'CONTORNI';
  return null;
}

// T-380: normalizzazione tollerante (accenti/maiuscole/spazi) per il match
// nome piatto ↔ ricetta nella pre-stampa recipe-based.
function normDishName(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

// T-303: stile dello stepper [−]/[+] del tab pre-stampa collettiva.
const COLL_STEP_BTN_STYLE: React.CSSProperties = {
  width: 34, height: 34, borderRadius: 8, border: '1px solid #cbd5e1',
  background: '#f8fafc', fontSize: 20, fontWeight: 700, color: '#0f172a',
  cursor: 'pointer', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
};

// ───────── tipi locali ─────────
interface Printer {
  id: string;
  name: string;
  // T-272/T-273/T-281: include 'niimbot'/'system'/'qz' e connection 'ble'.
  driver: 'brother_ql' | 'zpl' | 'escpos' | 'tspl' | 'cpcl' | 'niimbot' | 'system' | 'qz' | 'preview';
  connection: 'bluetooth' | 'ble' | 'wifi' | 'usb';
  address: string;
  business_id: string;
}

interface LabelTemplate {
  id: string;
  name: string;
  preset: string;
  width_mm: number;
  height_mm: number;
  elements: unknown[];
  business_id: string | null;
  required_allergens?: boolean;
}

interface RecipeRow {
  id: string;
  name: string;
  yield_qty: number;
  yield_uom: string;
  ingredients: Array<{ food_id: string; qty: number; uom: string }>;
  allergens: string[];
  shelf_life_days: number | null;
  temp_end_c?: number | null;
  chilling_minutes?: number | null;
  chilling_enabled: boolean;
  chilling_sign: 'positive' | 'negative' | null;
  freezing_enabled?: boolean;
  default_shelf_life_minutes: number | null;
  shelf_life_minutes_opened?: number | null;
  shelf_life_minutes_thawed?: number | null;
  is_favorite?: boolean;
  // T-337: ricetta di solo calcolo costo → esclusa dai picker di
  // stampa/preparazione (per tutti i ruoli).
  is_food_cost_only?: boolean;
  archived_at?: string | null;
  // T-441 (collettiva): se valorizzato, questa ricetta è una VARIANTE dieta →
  // va nascosta da ogni enumerazione/picker (ma resta risolvibile per id).
  variant_of_recipe_id?: string | null;
  // T-229 (collettiva): porzioni base prodotte dalla ricetta → flusso a
  // porzioni (scaling per porzione, 1 etichetta per porzione) + voce di menù
  // per la scheda di produzione giornaliera.
  yield_portions?: number | null;
  menu_label?: string | null;
  // T-259: ricette base (semilavorati) usate come ingrediente. parent_recipes
  // porta la quantità (sorgente di verità); parent_recipe_ids è la lista id
  // legacy (fallback per ricette salvate prima del campo quantità).
  parent_recipe_ids?: string[];
  parent_recipes?: Array<{ recipe_id: string; qty: number; uom: string }>;
}

interface FoodRow {
  id: string;
  name: string;
  creation_source?: string | null;
  description?: string | null;
  category?: string | null;
  // T-419: nome corto per la stampa in etichetta (fallback a name).
  label_alias?: string | null;
  default_uom: string;
  allergens?: string[];
  storage_type?: string;
  shelf_life_days_opened?: number | null;
  shelf_life_hours_thawed?: number | null;
  current_batch_lot?: string | null;
  current_expiration_date?: string | null;
  current_document_id?: string | null;
  current_supplier_id?: string | null;
  current_lot_received_at?: string | null;
  // T-221: food "prodotto finito" generato automaticamente da una ricetta
  // (collettiva). Permette il lookup ricetta → food per lo stock_in.
  recipe_id?: string | null;
  archived_at?: string | null;
  version?: number;
  last_unit_cost_currency?: string | null;
}

interface SupplierRow {
  id: string;
  name: string;
  piva: string | null;
}

interface StockInEvent {
  id: string;
  client_ts: string;
  // T-221: sede del movimento (events.business_id). NULL sugli eventi storici
  // pre-migrazione locale → trattati come "validi per ogni sede".
  business_id?: string | null;
  payload: {
    business_id?: string | null;
    food_id: string;
    qty: number;
    batch_lot: string | null;
    expiration_date: string | null;
    supplier_id?: string | null;
    document_id?: string | null;
    uom?: string;
    stock_qty?: number;
    storage_unit_id?: string | null;
    unit_cost?: number | null;
    currency?: string | null;
    stock_uom?: string;
    stock_unit_cost?: number | null;
    source_currency?: string;
    // T-259: per i lotti "prodotto finito" (carico da produzione) punta alla
    // production che li ha generati → propaga la tracciabilità a chi li consuma.
    source_production_id?: string | null;
    finished_good?: boolean;
    command_id?: string;
    movement_class?: string;
  };
}
interface LocalStockCommandEvent {
  id?: string | null;
  server_id?: string | null;
  business_id?: string | null;
  kind: string;
  rev?: number | null;
  client_ts?: string | null;
  server_ts?: string | null;
  payload: Record<string, unknown>;
}

// T-095: production master per il flusso "Materia prima → Scongelamento da
// preparazione congelata". Subset dei campi davvero usati qui (la pagina
// ProductionsPage ha l'interfaccia completa). La ricetta serve
// a derivare `frozenProductions` dal flag Congelamento della ricetta,
// mantenendo soltanto i lotti vivi nel flusso scongelamento
// con i lotti del cuoco invece dei soli alimenti crudi surgelati.
interface ProductionInputSaved {
  food_id?: string | null;
  food_name?: string;
  batch_lot?: string | null;
  supplier_id?: string | null;
  supplier_name?: string | null;
  document_id?: string | null;
  qty_used?: number;
  uom?: string;
  allergens?: string[];
  source_production_id?: string | null;
}
interface ProductionRow {
  id: string;
  business_id?: string | null;
  output_name: string;
  output_qty: number | null;
  output_uom: string | null;
  produced_at: string;
  expires_at?: string | null;
  shelf_life_minutes?: number | null;
  recipe_id?: string | null;
  inputs?: ProductionInputSaved[];
  lot_photo_batch_v2?: unknown;
  allergens_aggregated?: string[];
  trace_token?: string | null;
  archived_at?: string | null;
  version?: number;
}

interface LabelEvent {
  id: string;
  business_id?: string | null;
  payload: {
    business_id?: string | null;
    template_id: string;
    printer_id?: string | null;
    printer_device?: {
      name: string;
      driver: Printer['driver'];
      connection: Printer['connection'];
    };
    preset: string;
    data: Record<string, unknown>;
    copies: number;
    lp_code?: string | null;
    expires_at?: string | null;
    produced_at?: string | null;
  };
  client_ts: string;
}

type LabelPrintedPayload = LabelEvent['payload'];

/** Device-local printers are audit snapshots, not tenant master references. */
function labelPrinterAudit(
  printer: Printer,
): Pick<LabelPrintedPayload, 'printer_id' | 'printer_device'> {
  return {
    printer_id: null,
    printer_device: {
      name: printer.name,
      driver: printer.driver,
      connection: printer.connection,
    },
  };
}

interface DurableLabelPrintOperation {
  key: string;
  command: StockCommandV1;
  masterEffects: readonly StockCommandMasterEffectV1[];
  intentId: string;
  productionId: string;
  tenantId: string;
  businessId: string;
  printer: Printer;
  template: LabelTemplate;
  labelData: Record<string, unknown>;
  labelPayload: LabelPrintedPayload;
  copies: number;
  afterPersist?: () => Promise<void>;
}

interface DurablePrintJobSnapshot {
  printer: Printer;
  template: LabelTemplate;
  label_data: Record<string, unknown>;
  copies: number;
}

interface DurableLabelVoidOperation {
  command: StockCommandV1;
  masterEffects: readonly StockCommandMasterEffectV1[];
  intentId: string;
  productionId: string;
  tenantId: string;
  businessId: string;
}

type RecoverableLabelPrintIntent = ProductionLabelPrintIntent & {
  state: 'pending' | 'failed';
  audit_kind: 'label_printed';
  print_job: DurablePrintJobSnapshot;
  label_payload: LabelPrintedPayload;
};

const PRINTER_DRIVERS = new Set<Printer['driver']>([
  'brother_ql',
  'zpl',
  'escpos',
  'tspl',
  'cpcl',
  'niimbot',
  'system',
  'qz',
  'preview',
]);
const PRINTER_CONNECTIONS = new Set<Printer['connection']>([
  'bluetooth',
  'ble',
  'wifi',
  'usb',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value != null && !Array.isArray(value);
}

function isDurablePrintJobSnapshot(value: unknown): value is DurablePrintJobSnapshot {
  if (!isRecord(value) || !isRecord(value.printer) || !isRecord(value.template)) return false;
  const printer = value.printer;
  const template = value.template;
  return (
    typeof printer.id === 'string'
    && typeof printer.name === 'string'
    && typeof printer.driver === 'string'
    && PRINTER_DRIVERS.has(printer.driver as Printer['driver'])
    && typeof printer.connection === 'string'
    && PRINTER_CONNECTIONS.has(printer.connection as Printer['connection'])
    && typeof printer.address === 'string'
    && typeof printer.business_id === 'string'
    && typeof template.id === 'string'
    && typeof template.name === 'string'
    && typeof template.preset === 'string'
    && typeof template.width_mm === 'number'
    && Number.isFinite(template.width_mm)
    && typeof template.height_mm === 'number'
    && Number.isFinite(template.height_mm)
    && Array.isArray(template.elements)
    && (template.business_id == null || typeof template.business_id === 'string')
    && isRecord(value.label_data)
    && typeof value.copies === 'number'
    && Number.isInteger(value.copies)
    && value.copies > 0
  );
}

function isLabelPrintedPayload(value: unknown): value is LabelPrintedPayload {
  const hasServerPrinter = isRecord(value) && typeof value.printer_id === 'string';
  const hasDevicePrinter = isRecord(value)
    && isRecord(value.printer_device)
    && typeof value.printer_device.name === 'string'
    && typeof value.printer_device.driver === 'string'
    && PRINTER_DRIVERS.has(value.printer_device.driver as Printer['driver'])
    && typeof value.printer_device.connection === 'string'
    && PRINTER_CONNECTIONS.has(value.printer_device.connection as Printer['connection']);
  return (
    isRecord(value)
    && typeof value.template_id === 'string'
    && (hasServerPrinter || hasDevicePrinter)
    && typeof value.preset === 'string'
    && isRecord(value.data)
    && typeof value.copies === 'number'
    && Number.isInteger(value.copies)
    && value.copies > 0
  );
}

function isRecoverableLabelPrintIntent(
  intent: ProductionLabelPrintIntent,
): intent is RecoverableLabelPrintIntent {
  return (
    intent.audit_kind === 'label_printed'
    && (intent.state === 'pending' || intent.state === 'failed')
    && isDurablePrintJobSnapshot(intent.print_job)
    && isLabelPrintedPayload(intent.label_payload)
  );
}

function shouldDiscardDurablePrintOperation(error: unknown): boolean {
  return error instanceof DurableLabelPrintError
    && error.physicalPrintCompleted;
}

interface LotRow {
  key: string;
  food_id: string;
  food_name: string;
  batch_lot: string | null;
  supplier_id: string | null;
  supplier_name: string | null;
  document_id: string | null;
  storage_unit_id: string | null;
  uom: string;
  available: number;
  expires: string | null;
  allergens: string[];
  unit_cost: number | null;
  currency: string;
  // T-259: production di origine per i lotti "prodotto finito" (semilavorati).
  // Consumandoli come ingrediente, l'input eredita questo id e la trace-page
  // risale la catena padre → semilavorato → ingredienti/fornitori.
  source_production_id?: string | null;
  command_id?: string | null;
  received_at?: string | null;
  material_source_food_id?: string | null;
  material_transformation?: MaterialTransformation | null;
  produced_at?: string | null;
  transformed_at?: string | null;
}

// Riga ingrediente nella preparazione: scegli food, scegli lotto, scegli qty.
// `base_qty` (se presente) è la quantità da ricetta riferita al `yield base` originale.
// Serve a riproporzionare tutte le righe quando l'utente cambia outputQty
// (es. raddoppia la preparazione → moltiplico tutte le qty_used per 2).
interface IngredientLine {
  uid: string;
  food_id: string;
  food_name: string;
  lot_key: string; // "<food_id>|<batch_lot>"
  batch_lot: string | null;
  supplier_id: string | null;
  supplier_name: string | null;
  document_id: string | null;
  uom: string;
  qty_used: string; // input raw
  available: number;
  allergens: string[];
  base_qty?: number; // qty originale da ricetta (per yield base)
  // T-259: se la riga è un semilavorato (lotto prodotto-finito), id della sua
  // production → finisce in productions.inputs[].source_production_id (trace).
  source_production_id?: string | null;
}

interface ProductionLotPhotoBatchDraft {
  manifest: StagedProductionLotPhotoBatch;
  preview_data_urls: string[];
}

// ───────── default template TrackEAT ─────────
// Template di default: tracciabilità preparazione 50×30 mm (Clabel).
// Layout aggiornato 2026-05-07 (feedback utente: QR a sinistra, font più
// piccoli per far entrare ingredienti e allergeni senza overlap):
//
//   ┌──────────────────────────────────────┐
//   │ ┌────────┐ NOME PRODOTTO              │
//   │ │   QR   │ Prod. dd/mm/yyyy           │
//   │ │ 22×22  │ Scad. dd/mm/yyyy (bold)    │
//   │ │  mm    │ Lotto: XXXXXXXX            │
//   │ └────────┘                            │
//   │ Ingr.: pollo, riso, …                 │
//   │ Allergeni: pesce, glutine             │
//   └──────────────────────────────────────┘
//
// QR a sinistra (x=1, y=2). Testo a destra in colonna stretta (x=25..49,
// 24mm utili) per nome/date/lotto. Sotto il QR (y>=25) full-width 50mm
// per ingredienti + allergeni con truncate filter (max 30 char).
const DEFAULT_TEMPLATE: LabelTemplate = {
  id: 'trackeat-prep-default',
  name: 'Preparazione 50×30 mm',
  preset: 'tracciabilita_lotto',
  width_mm: 50,
  height_mm: 30,
  business_id: null,
  required_allergens: false,
  // Layout predefinito tarato e confermato dall'utente (Consulente) il
  // 2026-05-18 via "Esporta" dell'editor. Reso default di fabbrica dell'app:
  // ogni installazione senza layout custom/DB parte da qui.
  elements: [
    { id: 'q', type: 'qr',   x_mm: 29,  y_mm: 0,    width_mm: 21, height_mm: 21, binding: '{{ trace_url }}' },
    { id: 'n', type: 'text', x_mm: 0.5, y_mm: 0,    width_mm: 25, height_mm: 4, font_size_pt: 6, font_weight: 'bold',   binding: '{{ product | upper }}' },
    { id: 'p', type: 'text', x_mm: 0.5, y_mm: 4,    width_mm: 25, height_mm: 3, font_size_pt: 6,                        binding: 'Prod. {{ prepared_at | date:DD/MM/YY }}' },
    { id: 'e', type: 'text', x_mm: 0.5, y_mm: 7.5,  width_mm: 25, height_mm: 3, font_size_pt: 6, font_weight: 'normal', binding: 'Scad. {{ expires_at | date:DD/MM/YY }}' },
    { id: 'l', type: 'text', x_mm: 0.5, y_mm: 11,   width_mm: 25, height_mm: 3, font_size_pt: 6, font_weight: 'bold',   binding: 'L: {{ lp_code | strip:LP- }}' },
    { id: 'i', type: 'text', x_mm: 0,   y_mm: 14.5, width_mm: 27, height_mm: 8, font_size_pt: 7,                        binding: '{{ ingredients | ingredients }}' },
    { id: 'a', type: 'text', x_mm: 0,   y_mm: 21.5, width_mm: 48, height_mm: 8, font_size_pt: 7,                        binding: '{{ allergens | allergens }}' },
  ] as unknown[],
};

// ───────── helper ─────────
function buildTraceUrl(token: string): string {
  return `${TRACE_BASE_URL}/t/${encodeURIComponent(token)}`;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const STOCK_CONVERSION_SOURCES = new Set<StockConversionSource>([
  'identity',
  'metric',
  'document_pack',
  'document_weight',
  'supplier_alias',
  'user_confirmed',
  'carried',
]);

function normalizedUom(value: string): string {
  return value.trim().toLowerCase();
}

function stockCurrency(value: string | null | undefined): string {
  const normalized = value?.trim().toUpperCase() ?? '';
  return /^[A-Z]{3}$/.test(normalized) ? normalized : 'EUR';
}

function productionStockLine(input: {
  foodId: string;
  batchLot: string | null;
  expirationDate: string | null;
  supplierId: string | null;
  documentId: string | null;
  storageUnitId: string | null;
  qty: number;
  sourceUom: string;
  stockUom: string;
  unitCost: number | null;
  sourceLineTotal?: number | null;
  currency: string;
  lineRef: string;
  operatorName: string | null;
  lotAction: 'updated_active' | 'recorded_only';
}): StockSourceLineV1 {
  const sourceUom = input.sourceUom.trim();
  const stockUom = input.stockUom.trim();
  const factor = convertQty(1, sourceUom, stockUom);
  if (factor == null || factor <= 0) {
    throw new Error(`UdM incompatibili per il movimento: ${sourceUom} → ${stockUom}`);
  }
  const explicitTotal = input.sourceLineTotal;
  if (
    explicitTotal != null
    && (!Number.isFinite(explicitTotal) || explicitTotal < 0)
  ) {
    throw new Error('Valore totale del movimento non valido');
  }
  const sourceUnitPrice = explicitTotal != null
    ? explicitTotal / input.qty
    : input.unitCost == null
      ? null
      : input.unitCost * factor;
  return {
    food_id: input.foodId,
    batch_lot: input.batchLot,
    expiration_date: input.expirationDate,
    supplier_id: input.supplierId,
    document_id: input.documentId,
    storage_unit_id: input.storageUnitId,
    line_ref: input.lineRef,
    lot_action: input.lotAction,
    operator_name: input.operatorName,
    source_qty: input.qty,
    source_uom: sourceUom,
    source_unit_price: sourceUnitPrice,
    source_line_total: explicitTotal ?? (
      sourceUnitPrice == null ? null : sourceUnitPrice * input.qty
    ),
    currency: stockCurrency(input.currency),
    stock_uom: stockUom,
    conversion: {
      factor,
      source: normalizedUom(sourceUom) === normalizedUom(stockUom) ? 'identity' : 'metric',
    },
  };
}

function materialDerivedFoodInsertEffect(input: {
  foodId: string;
  tenantId: string;
  name: string;
  sourceFood: FoodRow;
  action: 'open' | 'thaw';
  shelfHours: number;
  allergens: string[];
}): StockCommandMasterEffectV1 {
  return {
    table: 'foods',
    id: input.foodId,
    op: 'insert',
    base_version: 0,
    patch: {
      id: input.foodId,
      tenant_id: input.tenantId,
      name: input.name,
      description: `Prodotto ${input.action === 'thaw' ? 'scongelato' : 'aperto'} — generato automaticamente dalle etichette`,
      category: input.sourceFood.category ?? 'altro',
      default_uom: input.sourceFood.default_uom,
      storage_type: 'fresco',
      allergens: input.allergens,
      recipe_id: null,
      creation_source: 'quick_label',
      ...(input.action === 'thaw'
        ? { shelf_life_hours_thawed: Math.round(input.shelfHours) }
        : { shelf_life_days_opened: Math.max(1, Math.ceil(input.shelfHours / 24)) }),
    },
  };
}

function labelPrintIntentEffect(input: {
  intentId: string;
  tenantId: string;
  businessId: string;
  productionId: string;
  commandId: string;
  auditClientUuid: string;
  auditKind?: 'label_printed' | 'label_voided';
  labelPayload: LabelPrintedPayload | {
    original_event_id: string;
    reason: 'user_cancel';
    voided_at: string;
  };
  printJob?: DurablePrintJobSnapshot | null;
  requestedAt: string;
}): StockCommandMasterEffectV1 {
  return {
    table: 'production_label_print_intents_v1',
    id: input.intentId,
    op: 'insert',
    base_version: 0,
    patch: {
      id: input.intentId,
      tenant_id: input.tenantId,
      business_id: input.businessId,
      production_id: input.productionId,
      command_id: input.commandId,
      audit_client_uuid: input.auditClientUuid,
      audit_kind: input.auditKind ?? 'label_printed',
      label_payload: input.labelPayload,
      print_job: input.printJob ?? null,
      state: 'pending',
      requested_at: input.requestedAt,
    },
  };
}

function eventBusinessId(event: LocalStockCommandEvent): string | null {
  const payloadBusinessId = event.payload.business_id;
  return event.business_id
    ?? (typeof payloadBusinessId === 'string' ? payloadBusinessId : null);
}

function requiredPositiveNumber(payload: Record<string, unknown>, key: string): number {
  const value = payload[key];
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    throw new Error(`Movimento originale incompleto: ${key}`);
  }
  return value;
}

function nullableNonNegativeNumber(
  payload: Record<string, unknown>,
  key: string,
): number | null {
  const value = payload[key];
  if (value == null) return null;
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    throw new Error(`Movimento originale non valido: ${key}`);
  }
  return value;
}

function nullableUuid(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  if (value == null) return null;
  if (typeof value !== 'string' || !UUID_PATTERN.test(value)) {
    throw new Error(`Movimento originale non valido: ${key}`);
  }
  return value;
}

function originalCommandEvents(
  events: readonly LocalStockCommandEvent[],
  commandId: string,
  businessId: string,
  movementClass: 'production' | 'lot_state',
  expectedCount: number,
  productionId: string | null,
): LocalStockCommandEvent[] {
  if (!UUID_PATTERN.test(commandId) || !Number.isInteger(expectedCount) || expectedCount <= 0) {
    throw new Error('Metadati StockCommand dell’etichetta non validi');
  }
  const originals = events
    .filter((event) => (
      eventBusinessId(event) === businessId
      && event.payload.command_id === commandId
      && event.payload.movement_class === movementClass
    ))
    .sort((left, right) => (
      Number(left.payload.command_seq) - Number(right.payload.command_seq)
    ));
  if (originals.length !== expectedCount) {
    throw new Error(
      `Comando originale incompleto: trovati ${originals.length} movimenti su ${expectedCount}. Sincronizza prima di annullare.`,
    );
  }
  originals.forEach((event, index) => {
    if (event.payload.command_seq !== index + 1) {
      throw new Error('Comando originale incompleto: sequenza movimenti non continua');
    }
    if (productionId != null && event.payload.source_production_id !== productionId) {
      throw new Error('Comando originale non coerente con la lavorazione selezionata');
    }
  });
  return originals;
}

function reverseMovementFromEvent(
  event: LocalStockCommandEvent,
): ReversibleStockMovementV1 {
  const payload = event.payload;
  const originalSeq = payload.command_seq;
  const foodId = payload.food_id;
  const sourceUom = payload.source_uom;
  const stockUom = payload.stock_uom;
  const sourceCurrency = payload.source_currency;
  const conversionFactor = payload.conversion_factor;
  const conversionSource = payload.conversion_source;
  const businessId = eventBusinessId(event);
  if (
    (event.kind !== 'stock_in' && event.kind !== 'stock_out')
    || typeof originalSeq !== 'number'
    || !Number.isInteger(originalSeq)
    || originalSeq <= 0
    || typeof foodId !== 'string'
    || !UUID_PATTERN.test(foodId)
    || typeof sourceUom !== 'string'
    || typeof stockUom !== 'string'
    || typeof sourceCurrency !== 'string'
    || typeof conversionFactor !== 'number'
    || !Number.isFinite(conversionFactor)
    || conversionFactor <= 0
    || typeof conversionSource !== 'string'
    || !STOCK_CONVERSION_SOURCES.has(conversionSource as StockConversionSource)
    || businessId == null
    || !UUID_PATTERN.test(businessId)
  ) {
    throw new Error('Il comando originale non contiene dati sufficienti per uno storno sicuro');
  }
  const clientEventId = event.id;
  if (typeof clientEventId !== 'string' || !UUID_PATTERN.test(clientEventId)) {
    throw new Error('Identificativo locale del movimento originale non valido');
  }
  const serverEventId = event.server_id;
  if (typeof serverEventId !== 'string' || !UUID_PATTERN.test(serverEventId)) {
    throw new Error('Sincronizza i movimenti prima di annullare questa etichetta');
  }
  const sourceLineRef = payload.source_line_ref;
  if (sourceLineRef != null && typeof sourceLineRef !== 'string') {
    throw new Error('Riferimento del movimento originale non valido');
  }
  const referenceId = payload.conversion_reference_id;
  if (referenceId != null && typeof referenceId !== 'string') {
    throw new Error('Riferimento di conversione originale non valido');
  }
  return {
    food_id: foodId,
    batch_lot: typeof payload.batch_lot === 'string' ? payload.batch_lot : null,
    expiration_date: typeof payload.expiration_date === 'string' ? payload.expiration_date : null,
    supplier_id: nullableUuid(payload, 'supplier_id'),
    document_id: nullableUuid(payload, 'document_id'),
    storage_unit_id: nullableUuid(payload, 'storage_unit_id'),
    line_ref: sourceLineRef ?? null,
    original_event_id: serverEventId,
    original_event_client_uuid: clientEventId,
    original_seq: originalSeq,
    kind: event.kind,
    business_id: businessId,
    source_qty: requiredPositiveNumber(payload, 'source_qty'),
    source_uom: sourceUom,
    source_unit_price: nullableNonNegativeNumber(payload, 'source_unit_price'),
    source_line_total: nullableNonNegativeNumber(payload, 'source_line_total'),
    currency: sourceCurrency,
    stock_qty: requiredPositiveNumber(payload, 'stock_qty'),
    stock_uom: stockUom,
    unit_cost: nullableNonNegativeNumber(payload, 'stock_unit_cost'),
    conversion: {
      factor: conversionFactor,
      source: conversionSource as StockConversionSource,
      ...(referenceId == null ? {} : { reference_id: referenceId }),
    },
  };
}

function reversedCommandEventCount(
  events: readonly LocalStockCommandEvent[],
  commandId: string,
  businessId: string,
): number {
  return events.filter((event) => (
    eventBusinessId(event) === businessId
    && event.payload.movement_class === 'reverse'
    && event.payload.reverses_command_id === commandId
  )).length;
}

function shelfLifeDaysFromRecipe(r: RecipeRow | undefined): number {
  return Math.max(1, Math.round(recipeShelfLifeMinutes(r) / (24 * 60)));
}

// T-242: porzioni base della ricetta per il flusso collettiva "a porzioni".
// Le ricette con unità "porzioni" hanno yield_qty = numero di porzioni (campo
// unico in RecipesPage) → niente più doppio campo. yield_portions resta
// valorizzato esplicitamente solo per ricette in kg/l/... Fallback su yield_qty
// per le ricette in porzioni salvate prima del consolidamento (yield_portions
// null ma yield_uom = porzioni).
function recipeBasePortions(r: RecipeRow | undefined): number {
  if (!r) return 0;
  const explicit = Number(r.yield_portions ?? 0);
  if (Number.isFinite(explicit) && explicit > 0) return Math.round(explicit);
  if ((r.yield_uom ?? '') === 'porzioni') {
    const q = Number(r.yield_qty ?? 0);
    if (Number.isFinite(q) && q > 0) return Math.round(q);
  }
  return 0;
}

function pickTemplate(
  templates: LabelTemplate[],
  tenantId: string | null | undefined,
  globalPresets?: Record<string, LabelTemplate>,
): LabelTemplate {
  // 1) Variante PER RISTORANTE salvata via Editor (localStorage, scoped per tenant)
  //    → priorità massima, vince su tutto.
  const custom = loadCustomTemplate(DEFAULT_TEMPLATE.id, tenantId);
  if (custom) return custom;
  // 2) Template DB del tenant (label_templates master)
  const dbTpl = templates.find((t) => t.preset === 'tracciabilita_lotto' || t.preset === 'lavorato');
  if (dbTpl) return dbTpl;
  // 3) Default GLOBALE gestito dal platform owner (label_presets). Se l'owner ha
  //    pubblicato un default (es. 60×40) e il ristorante non ha una variante, è
  //    quello a vincere sul fallback hardcoded. Preferiamo il 50×30 se presente
  //    (formato storico), altrimenti l'unico globale disponibile.
  if (globalPresets) {
    const keys = Object.keys(globalPresets);
    if (globalPresets['50x30']) return globalPresets['50x30'];
    if (keys.length === 1) return globalPresets[keys[0]!]!;
  }
  // 4) Default hardcoded.
  return DEFAULT_TEMPLATE;
}

// ───────── anteprima testuale etichetta ─────────
// L'utente vuole vedere "un po' di testo" leggibile di ciò che finirà
// sull'etichetta, non il rendering termico minuscolo. Questa card grande
// resta fissa in cima al wizard e si aggiorna mentre si compila.
const fmtDateTime = (d: Date): string =>
  d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

const ALLERGEN_IT: Record<string, string> = {
  gluten: 'glutine', crustaceans: 'crostacei', eggs: 'uova', fish: 'pesce',
  peanuts: 'arachidi', soybeans: 'soia', milk: 'latte', nuts: 'frutta a guscio',
  celery: 'sedano', mustard: 'senape', sesame: 'sesamo', sulphites: 'solfiti',
  lupin: 'lupini', molluscs: 'molluschi',
};

interface LabelTextPreviewProps {
  product: string;
  producedAt: Date;
  expiresAt: Date;
  lot?: string | null;
  allergens: string[];
  ingredients: string[];
  copies: number;
}

// T-104: banner rosso visibile quando l'utente ha selezionato la stampante
// "Anteprima a schermo". Senza questo avviso ci sono stati casi reali in cui
// il cliente premeva "Conferma e stampa" pensando di stampare fisicamente,
// non capiva perché non usciva l'etichetta, e si lamentava (mentre invece
// l'app aveva aperto solo l'anteprima in-app). Compare ovunque ci sia
// selezione stampante (preparazione, materia prima, modal ristampa).
function PreviewWarningBanner(props: { visible: boolean; compact?: boolean }): JSX.Element | null {
  if (!props.visible) return null;
  return (
    <div style={{
      margin: props.compact ? '6px 0' : '8px 0 4px',
      padding: '10px 14px',
      background: '#fef2f2',
      border: '1.5px solid #dc2626',
      borderRadius: 10,
      fontSize: 12,
      color: '#991b1b',
      fontWeight: 600,
      display: 'flex', alignItems: 'flex-start', gap: 8,
      lineHeight: 1.4,
    }}>
      <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>⚠️</span>
      <span>
        <b>Stampante: Anteprima a schermo.</b> Non stampa fisicamente —
        ti mostra solo come verrà l'etichetta. Per stampare davvero,
        scegli una stampante reale dal selettore.
      </span>
    </div>
  );
}

// T-253: data di produzione modificabile (etichette retrodatate).
// Data odierna in formato YYYY-MM-DD (locale), default del campo data di produzione.
function todayDateStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Compone la data di produzione: giorno scelto dall'utente + ora corrente.
// Se la data è oggi, equivale a new Date() → comportamento di default invariato.
// La scadenza (produced_at + shelf_life) si retrodata di conseguenza.
function composeProducedAt(dateStr: string): Date {
  const now = new Date();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!m) return now;
  return new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds(),
    now.getMilliseconds(),
  );
}

// T-243: anteprima REALE (raster TSPL, WYSIWYG) mostrata direttamente in cima
// allo step Stampa, al posto della vecchia card "mock" + bottone separato. Tap
// → apre lo zoom fullscreen (stesso modal del vecchio bottone "Vedi come verrà
// stampata"). Mostra ESATTAMENTE ciò che esce dalla stampante.
function InlineLabelPreview(props: {
  template: LabelTemplate;
  data: Record<string, unknown>;
  copies: number;
  onZoom: () => void;
  rasterOptions?: RasterOptions;
}): JSX.Element {
  const { template, data, copies, onZoom } = props;
  return (
    <button
      type="button"
      onClick={onZoom}
      style={{
        display: 'block', width: '100%', textAlign: 'left',
        background: '#fff', borderRadius: 16, padding: '12px 14px',
        boxShadow: '0 2px 8px rgba(15,23,42,.10)',
        border: '1px solid #e2e8f0', cursor: 'pointer',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 10, fontWeight: 800, color: '#94a3b8',
        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8,
      }}>
        <span style={{ fontSize: 14 }}>🏷️</span> Anteprima etichetta
        <span style={{ marginLeft: 'auto', color: '#0066cc', fontWeight: 700 }}>{copies}× cop{copies > 1 ? 'ie' : 'ia'}</span>
      </div>
      <div style={{ maxHeight: 240, overflow: 'auto', display: 'flex', justifyContent: 'center' }}>
        <TsplLabelPreview template={template} data={data} rasterOptions={props.rasterOptions} />
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        marginTop: 8, fontSize: 12, fontWeight: 600, color: '#475569',
      }}>
        <IonIcon icon={eyeOutline} style={{ fontSize: 16 }} />
        Tocca per ingrandire
      </div>
    </button>
  );
}

function LabelTextPreview(props: LabelTextPreviewProps): JSX.Element {
  const { product, producedAt, expiresAt, lot, allergens, ingredients, copies } = props;
  const name = product.trim() || '(da compilare)';
  return (
    <div style={{
      background: '#fff', borderRadius: 16, padding: '16px 18px',
      boxShadow: '0 2px 8px rgba(15,23,42,.10)',
      border: '1px solid #e2e8f0',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 10, fontWeight: 800, color: '#94a3b8',
        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10,
      }}>
        <span style={{ fontSize: 14 }}>🏷️</span> Anteprima etichetta
        <span style={{ marginLeft: 'auto', color: '#0066cc', fontWeight: 700 }}>{copies}× cop{copies > 1 ? 'ie' : 'ia'}</span>
      </div>
      <div style={{
        fontSize: 22, fontWeight: 800, color: '#0f172a',
        lineHeight: 1.15, textTransform: 'uppercase', letterSpacing: '-0.01em',
        wordBreak: 'break-word',
      }}>
        {name}
      </div>
      <div style={{ display: 'flex', gap: 20, marginTop: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Prodotto</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#334155', marginTop: 2 }}>{fmtDateTime(producedAt)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: '#b45309', fontWeight: 800, textTransform: 'uppercase' }}>Scade</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#b45309', marginTop: 2 }}>{fmtDateTime(expiresAt)}</div>
        </div>
      </div>
      {lot && (
        <div style={{ marginTop: 10, fontSize: 13, color: '#475569' }}>
          Lotto: <b style={{ color: '#0f172a' }}>{lot}</b>
        </div>
      )}
      {ingredients.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 13, color: '#475569', lineHeight: 1.4 }}>
          <span style={{ color: '#94a3b8' }}>Ingr.:</span>{' '}
          {ingredients.join(', ')}
        </div>
      )}
      {allergens.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {allergens.map((a) => (
            <span key={a} style={{
              background: '#fef3c7', color: '#b45309',
              fontSize: 11, fontWeight: 700,
              padding: '3px 9px', borderRadius: 999,
            }}>
              {ALLERGEN_IT[a] ?? a}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Indicatore passi (pillole) + barra: orientamento immediato nel wizard.
function StepBar({ step, total, labels }: { step: number; total: number; labels: string[] }): JSX.Element {
  return (
    <div style={{ padding: '10px 16px 12px', background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
      <div style={{ display: 'flex', gap: 6 }}>
        {Array.from({ length: total }, (_, i) => (
          <div key={i} style={{
            flex: 1, height: 5, borderRadius: 999,
            background: i < step ? '#0066cc' : '#e2e8f0',
            transition: 'background .2s',
          }} />
        ))}
      </div>
      <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
        Passo {step} di {total} · <span style={{ color: '#64748b', fontWeight: 600 }}>{labels[step - 1]}</span>
      </div>
    </div>
  );
}

function lotTriggerValue(c: LotRow | undefined): string | undefined {
  if (!c) return undefined;
  return [c.batch_lot ?? '—', lotActivityText(c)].filter(Boolean).join(' · ');
}

function visibleLabelLotCode(payload: {
  lp_code?: string | null;
  data?: Record<string, unknown>;
}): string | null {
  const materialCode = payload.data?.material_lot_code;
  return typeof materialCode === 'string' && /^(?:SC|AP)-[A-Z0-9]+$/i.test(materialCode)
    ? materialCode
    : payload.lp_code ?? null;
}

const HISTORY_DAY_MS = 86_400_000;
function labelProducedTs(label: LabelHistoryHeader): number {
  const ts = Date.parse(label.payload.produced_at ?? label.client_ts);
  return Number.isFinite(ts) ? ts : 0;
}
function labelExpiryTs(label: LabelHistoryHeader): number {
  const ts = label.payload.expires_at ? Date.parse(label.payload.expires_at) : NaN;
  return Number.isFinite(ts) ? ts : NaN;
}
function labelHistoryStatus(label: LabelHistoryHeader, now: number): 'valid' | 'expiring' | 'expired' {
  const expiry = labelExpiryTs(label);
  if (!Number.isFinite(expiry)) return 'valid';
  if (expiry < now) return 'expired';
  return expiry - now <= 2 * HISTORY_DAY_MS ? 'expiring' : 'valid';
}

// Il codice resta il dato primario; badge e sottotitolo rendono distinguibili
// carico magazzino, preparazione, apertura e scongelamento senza aprire dettagli.
function lotToItem(c: LotRow): { id: string; label: string; details: string[]; badge?: string } {
  const parts: string[] = [];
  const operation = materialLotOperationLabel(c);
  const activity = lotActivityText(c);
  if (activity) parts.push(activity);
  if (c.supplier_name) parts.push(c.supplier_name);
  if (Number.isFinite(c.available)) parts.push(`${c.available.toFixed(2)} ${c.uom}`);
  if (c.expires) parts.push(`scad. ${new Date(c.expires).toLocaleDateString('it-IT')}`);
  return {
    id: c.key,
    label: c.batch_lot ?? '—',
    details: parts,
    badge: operation?.toUpperCase() ?? (c.source_production_id ? 'PRODUZIONE' : 'CARICO'),
  };
}

// Form "primo lotto" inline, riusabile: compare quando un alimento non ha
// alcun lotto (nel picker o su una riga ingrediente). Stesso schema ovunque.
// Autofocus + tastiera aperta sul codice lotto appena montato.
function FirstLotForm(props: {
  foodName: string;
  busy: boolean;
  // T-221: con magazzino ON il "primo lotto" deve portare anche la QUANTITÀ
  // caricata, altrimenti il disponibile resta 0 e il blocco giacenza (10%)
  // impedirebbe comunque la stampa. askQty mostra il campo; uom è l'unità
  // di carico del food per il badge.
  askQty?: boolean;
  uom?: string;
  onSubmit: (batch: string, expiry: string, qty: number | null) => void;
}): JSX.Element {
  const { foodName, busy, askQty, uom, onSubmit } = props;
  const [batch, setBatch] = useState('');
  const [expiry, setExpiry] = useState('');
  const [qty, setQty] = useState('');
  const batchRef = useRef<HTMLIonInputElement>(null);
  useEffect(() => { focusInput(batchRef.current); }, []);
  const qtyNum = Number(qty.replace(',', '.'));
  const qtyOk = !askQty || (Number.isFinite(qtyNum) && qtyNum > 0);
  return (
    <div className="te-card-missing" style={{ margin: '8px 0 4px', borderRadius: 12, padding: '12px 14px' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#b45309', marginBottom: 2 }}>
        ⚠️ Nessun lotto per “{foodName}”
      </div>
      <div style={{ fontSize: 12, color: '#92733a', marginBottom: 10 }}>
        Registra il primo lotto per poterlo usare nella preparazione.
      </div>
      <IonItem
        lines="full"
        className={batch.trim() ? 'te-field-ok' : 'te-field-missing'}
        style={{ '--background': '#fff', '--border-radius': '10px' } as React.CSSProperties}
      >
        <IonInput
          ref={batchRef}
          label="Codice lotto *"
          labelPlacement="stacked"
          placeholder="es. L-2026-001"
          value={batch}
          onIonInput={(e) => setBatch(String(e.detail.value ?? ''))}
        />
      </IonItem>
      {askQty && (
        <IonItem lines="full" className={qtyOk && qty.trim() ? 'te-field-ok' : 'te-field-missing'} style={{ '--background': '#fff', '--border-radius': '10px', marginTop: 8 } as React.CSSProperties}>
          <IonInput
            label="Quantità caricata *"
            labelPlacement="stacked"
            type="number"
            inputmode="decimal"
            value={qty}
            onIonInput={(e) => setQty(String(e.detail.value ?? ''))}
          />
          {uom && (
            <IonBadge color="primary" slot="end" style={{ fontSize: 13, padding: '6px 10px', alignSelf: 'flex-end' }}>
              {uom}
            </IonBadge>
          )}
        </IonItem>
      )}
      <IonItem lines="none" style={{ '--background': '#fff', '--border-radius': '10px', marginTop: 8 } as React.CSSProperties}>
        <IonInput
          label="Scadenza (opzionale)"
          labelPlacement="stacked"
          type="date"
          value={expiry}
          onIonInput={(e) => setExpiry(String(e.detail.value ?? ''))}
        />
      </IonItem>
      <IonButton
        expand="block"
        color="warning"
        disabled={busy || !batch.trim() || !qtyOk}
        onClick={() => onSubmit(batch.trim(), expiry, askQty && Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : null)}
        style={{ '--border-radius': '12px', height: 46, marginTop: 12, fontWeight: 700 } as React.CSSProperties}
      >
        {busy ? <IonSpinner name="dots" /> : (<><IonIcon icon={add} slot="start" />Registra lotto</>)}
      </IonButton>
    </div>
  );
}

// ───────── componente ─────────
export function LabelsPage(): JSX.Element {
  const navigation = useHistory();
  const location = useLocation<{ lotCorrection?: LotCorrectionPrintRequest }>();
  const handledCorrectionPrint = useRef<string | null>(null);
  const { t } = useTranslation();
  const { profile } = useAuth();
  const canCreateLabelFood = canCreateQuickLabelFood(profile);
  // Solo il Consulente HACCP può modificare/salvare il layout etichetta:
  // il ristoratore è esonerato da questo tipo di configurazione.
  const isConsultant = profile?.role === 'consulente';
  const queryClient = useQueryClient();
  const { data: dbPrinters = [] } = useMasters<Printer>('printers');
  // T-281: le stampanti sono DEVICE-LOCAL (periferiche del dispositivo:
  // indirizzo BLE / nome QZ / driver Windows), disponibili in TUTTI i locali.
  // I master legacy tenant-scoped restano visibili come fallback finché non
  // vengono importati nel device store (PrinterConfigPage import-on-sight).
  // `business_id` è iniettato dal locale ATTIVO (l'evento di stampa registra
  // dove sei, non un business "della stampante") → i commit-site invariati.
  const { printers: devicePrinters, defaultId: deviceDefaultId, loaded: devicePrintersLoaded } = useDevicePrinters();
  const printers = useMemo<Printer[]>(() => {
    const biz = profile?.active_business_id ?? '';
    // system/qz sono web-only (niente stampanti USB su smartphone).
    const usable = (d: string): boolean =>
      (d !== 'system' && d !== 'qz') || !Capacitor.isNativePlatform();
    const fromDevice: Printer[] = devicePrinters
      .filter((p) => p.driver !== 'preview' && usable(p.driver))
      .map((p) => ({
        id: p.id, name: p.name, driver: p.driver,
        connection: p.connection, address: p.address, business_id: biz,
      }));
    const seen = new Set(fromDevice.map((p) => `${p.driver}:${p.address}`));
    const fromLegacy: Printer[] = dbPrinters
      .filter((p) => p.driver !== 'preview' && usable(p.driver) && !seen.has(`${p.driver}:${p.address}`))
      .map((p) => ({ ...p, business_id: biz }));
    const preview: Printer = {
      id: PREVIEW_PRINTER_ID,
      name: PREVIEW_PRINTER_NAME,
      driver: 'preview',
      connection: 'usb',
      address: 'preview://canvas',
      business_id: biz,
    };
    return [...fromDevice, ...fromLegacy, preview];
  }, [devicePrinters, dbPrinters, profile?.active_business_id]);
  const { data: realTemplates = [], isLoading: templatesLoading } = useMasters<LabelTemplate>('label_templates');
  const { data: recipes = [] } = useMasters<RecipeRow>('recipes');
  const { favoriteIds, ownerLabel: favoriteOwnerLabel } = useRecipeFavorites();
  const { data: foods = [] } = useMasters<FoodRow>('foods');
  const { data: suppliers = [] } = useMasters<SupplierRow>('suppliers');
  const {
    data: businesses = [],
    isLoading: businessesLoading,
    isError: businessesError,
  } = useMasters<{
    id: string;
    tenant_id?: string | null;
    archived_at?: string | null;
  }>('businesses');
  const activeTenantId = profile?.active_tenant_id ?? null;
  const activeBusinessId = profile?.active_business_id ?? null;
  const activeTenantBusinesses = useMemo(
    () => businesses.filter((business) =>
      !business.archived_at
      && (business.tenant_id == null || business.tenant_id === activeTenantId)),
    [businesses, activeTenantId],
  );
  const allowLegacyUnscopedBusiness = !businessesLoading
    && !businessesError
    && activeBusinessId !== null
    && activeTenantBusinesses.length === 1
    && activeTenantBusinesses[0]?.id === activeBusinessId;
  // T-419: nome CORTO da stampare per un ingrediente (alias || name). Usato in
  // TUTTE le liste ingredienti che finiscono in etichetta (pre-stampa + ricette
  // + materia prima), così il nome lungo da DDT non sfonda l'etichetta.
  const foodAliasById = useMemo(() => new Map(foods.map((f) => [f.id, f])), [foods]);
  const ingName = useCallback(
    (foodId: string | null | undefined, fallback = ''): string =>
      foodLabelName(foodAliasById.get(foodId ?? ''), fallback),
    [foodAliasById],
  );
  // T-095: productions servono al flusso "Materia prima → Scongelamento" per
  // poter selezionare una preparazione del cuoco già abbattuta+congelata
  // (oltre ai soli alimenti crudi surgelati). La chain trace-page walka
  // `inputs[].source_production_id` → la nuova etichetta scongelato resta
  // collegata al lotto figlio della preparazione padre.
  const { data: productions = [], isLoading: productionsLoading } = useMasters<ProductionRow>('productions');
  const scopedProductions = useMemo(
    () => productions.filter((production) =>
      stockEventMatchesBusiness(
        production,
        activeBusinessId,
        allowLegacyUnscopedBusiness,
      )),
    [productions, activeBusinessId, allowLegacyUnscopedBusiness],
  );
  const stockInQuery = useEvents('stock_in', null);
  const stockOutQuery = useEvents('stock_out', null);
  const { data: stockIn = [], isLoading: stockInLoading } = stockInQuery;
  const { data: stockOut = [] } = stockOutQuery;
  const canonicalUomByFood = useMemo(
    () => Object.fromEntries(
      foods
        .filter((food) => !!food.default_uom?.trim())
        .map((food) => [food.id, food.default_uom.trim()]),
    ),
    [foods],
  );
  const { data: stockProjection } = useStockProjection({ canonicalUomByFood });
  // T-721: the complete history remains available as lightweight headers.
  // Full immutable payloads are read only for visible rows or an explicit action.
  const historyScope = useMemo(() => ({
    tenantId: activeTenantId,
    businessId: activeBusinessId,
    allowLegacyUnscoped: allowLegacyUnscopedBusiness,
  }), [activeTenantId, activeBusinessId, allowLegacyUnscopedBusiness]);
  const historyQuery = useLabelHistory(historyScope,
    location.pathname === '/haccp/labels' && !businessesLoading && !businessesError);
  const correctionEvents = useMemo(() => [...stockIn, ...stockOut].filter((event) =>
    stockEventMatchesBusiness(event, activeBusinessId, allowLegacyUnscopedBusiness)),
    [stockIn, stockOut, activeBusinessId, allowLegacyUnscopedBusiness]);
  const correctionIndex = useMemo(() => buildLotCorrectionIndex(correctionEvents), [correctionEvents]);
  const scopedHistory = useMemo(
    () => (historyQuery.data?.headers ?? []).map((event) => applyLotCorrections(event, correctionIndex)),
    [historyQuery.data, correctionIndex],
  );
  const voidedIds = useMemo(() => new Set(historyQuery.data?.voidedIds ?? []), [historyQuery.data]);
  const historyDataMissing = !historyQuery.data || businessesLoading || businessesError
    || stockInQuery.data === undefined || stockOutQuery.data === undefined;
  const historyReadError = historyQuery.isError || businessesError || stockInQuery.isError || stockOutQuery.isError;

  // T-280: conteggio "etichette reali" dello Storico = LOTTI DI PRODUZIONE
  // DISTINTI, escludendo annullate (label_voided), ristampe identiche
  // (is_reprint) e l'etichetta del PASTO CAMPIONE (preset 'campione'/sample,
  // che è un secondo output HACCP dello stesso lotto, non un'etichetta a sé).
  // Dedup per lotto (lp_code → production_id → id). Usato sia dal badge tab sia
  // dalla nota in fondo, così i due numeri COINCIDONO (prima divergevano).
  const labelStats = useMemo(() => {
    const seen = new Set<string>();
    let distinctLots = 0;
    for (const l of scopedHistory) {
      if (voidedIds.has(l.id)) continue;
      if (l.payload?.data?.is_reprint === true) continue;
      const isSample = l.payload?.preset === 'campione' || l.payload?.data?.sample === true;
      if (isSample) continue;
      const key =
        (l.payload?.lp_code as string) ||
        (l.payload?.data?.production_id as string) ||
        (l.id as string);
      if (seen.has(key)) continue;
      seen.add(key);
      distinctLots += 1;
    }
    return { distinctLots };
  }, [scopedHistory, voidedIds]);

  const enqueueMaster = useEnqueueMaster();
  const enqueueStockCommand = useEnqueueStockCommand();
  const durablePrintOperations = useRef(new Map<string, DurableLabelPrintOperation>());
  const durableVoidOperations = useRef(new Map<string, DurableLabelVoidOperation>());
  const collPrintBatchId = useRef<string | null>(null);
  const completedCollPrintKeys = useRef(new Set<string>());
  const [recoverablePrintIntents, setRecoverablePrintIntents] = useState<
    RecoverableLabelPrintIntent[]
  >([]);
  const [pendingLabelSync, setPendingLabelSync] = useState<
    PendingProductionLabelPrintOutcome[]
  >([]);
  const [labelReconciliations, setLabelReconciliations] = useState<
    ProductionLabelPrintReconciliation[]
  >([]);
  const [recoveryBusyId, setRecoveryBusyId] = useState<string | null>(null);
  const [reconciliationBusyId, setReconciliationBusyId] = useState<string | null>(null);
  const stockCommandEvents = useMemo(
    () => [...stockIn, ...stockOut] as LocalStockCommandEvent[],
    [stockIn, stockOut],
  );
  // Piano base: il flusso "materia prima" usa solo il lotto attivo del food
  // (con possibilità di richiamare un lotto precedente per le rimanenze).
  // Nessun input quantità, nessuno scarico stock_out.
  const warehouseEnabled = useWarehouseEnabled();
  const productionLotPhotoEnabled = useProductionLotPhotoEnabled();
  const productionLotPhotoAvailable = productionLotPhotoEnabled;

  const printStatusRefresh = useRef(0);

  const refreshRecoverablePrintIntents = useCallback(async (fromServer = false): Promise<void> => {
    const refreshId = ++printStatusRefresh.current;
    const activeBusinessId = profile?.active_business_id;
    const activeTenantId = profile?.active_tenant_id;
    if (!activeBusinessId || !activeTenantId) {
      setRecoverablePrintIntents([]);
      setPendingLabelSync([]);
      setLabelReconciliations([]);
      return;
    }
    try {
      const engine = getSyncEngine();
      let intents = await engine.listProductionLabelPrintIntents({
        states: ['pending', 'failed'],
        localOnly: !fromServer,
      });
      // Un void non ha un esito fisico incerto: se l'app è terminata dopo il
      // compound ma prima della finalizzazione, completiamo automaticamente
      // l'audit append-only. Solo le stampe richiedono una scelta umana.
      const pendingVoids = intents.filter((intent) => (
        intent.tenant_id === activeTenantId
        && intent.business_id === activeBusinessId
        && intent.audit_kind === 'label_voided'
        && intent.state === 'pending'
      ));
      await Promise.allSettled(pendingVoids.map(async (intent) => {
        const voidedAt = isRecord(intent.label_payload)
          && typeof intent.label_payload.voided_at === 'string'
          ? intent.label_payload.voided_at
          : intent.requested_at;
        await engine.finalizeProductionLabelPrintIntent({
          intentId: intent.id,
          productionId: intent.production_id,
          tenantId: intent.tenant_id,
          businessId: intent.business_id,
          outcome: 'voided',
          occurredAt: voidedAt,
        });
      }));
      const syncState = await engine.listProductionLabelPrintSyncState({
        tenantId: activeTenantId,
        businessId: activeBusinessId,
      });
      // The network lookup above can outlive the physical print and its ACK.
      // Read the current local projection again; an older pending snapshot
      // must never resurrect a terminal print in the recovery banner.
      intents = await engine.listProductionLabelPrintIntents({
        states: ['pending', 'failed'],
        localOnly: true,
      });
      const withDurableOutcome = new Set(
        syncState.pendingSync.map((outcome) => outcome.intent_id),
      );
      const retryableFailedOutcome = new Set(
        syncState.pendingSync
          .filter((outcome) => (
            outcome.outcome === 'failed'
            && outcome.status === 'pending'
          ))
          .map((outcome) => outcome.intent_id),
      );
      if (refreshId !== printStatusRefresh.current) return;
      setRecoverablePrintIntents(
        intents.filter((intent): intent is RecoverableLabelPrintIntent => (
          intent.tenant_id === activeTenantId
          && intent.business_id === activeBusinessId
          && !isLabelPrintIntentActive(intent.id)
          // Dopo un riavvio offline, un esito `failed` durevole può essere
          // sostituito in sicurezza da `printed` nello stesso outcome row. Non
          // costringere l'operatore ad attendere la rete per riprovare una
          // stampa che sappiamo non essere uscita.
          && (
            !withDurableOutcome.has(intent.id)
            || retryableFailedOutcome.has(intent.id)
          )
          && isRecoverableLabelPrintIntent(intent)
        )),
      );
      setPendingLabelSync(syncState.pendingSync);
      setLabelReconciliations(syncState.reconciliationRequired);
    } catch {
      if (refreshId !== printStatusRefresh.current) return;
      // Il DB può non essere ancora pronto durante il bootstrap; il banner
      // verrà ricaricato al successivo cambio sede/remount.
      setRecoverablePrintIntents([]);
      setPendingLabelSync([]);
      setLabelReconciliations([]);
    }
  }, [profile?.active_business_id, profile?.active_tenant_id]);

  const executeDurableLabelPrint = useCallback(async (
    operation: DurableLabelPrintOperation,
  ): Promise<void> => {
    let stage = 'persist';
    let physicalPrintCompleted = false;
    try {
      await withActiveLabelPrintIntent(operation.intentId, () => runDurableLabelPrintFlow({
        persistCommandAndIntent: async () => {
          await enqueueStockCommand.mutateAsync({
            command: operation.command,
            master_effects: operation.masterEffects,
          });
          await operation.afterPersist?.();
        },
        printPhysicalLabel: async () => {
          stage = 'connect';
          const adapter = createPrinterAdapter(operation.printer.driver);
          const session = await adapter.connect({
            id: operation.printer.id,
            name: operation.printer.name,
            driver: operation.printer.driver,
            connection: operation.printer.connection,
            address: operation.printer.address,
          });
          stage = 'print';
          await adapter.printLabel(
            session,
            {
              id: operation.template.id,
              tenant_id: '',
              name: operation.template.name,
              preset: operation.template.preset,
              width_mm: operation.template.width_mm,
              height_mm: operation.template.height_mm,
              elements: operation.template.elements as never,
              required_allergens: operation.template.required_allergens ?? false,
            } as never,
            operation.labelData,
            operation.copies,
          );
        },
        finalizePrinted: async () => {
          physicalPrintCompleted = true;
          stage = 'finalize_printed';
          await getSyncEngine().finalizeProductionLabelPrintIntent({
            intentId: operation.intentId,
            productionId: operation.productionId,
            tenantId: operation.tenantId,
            businessId: operation.businessId,
            outcome: 'printed',
            occurredAt: new Date().toISOString(),
          });
        },
        finalizeFailed: async (message) => {
          await getSyncEngine().finalizeProductionLabelPrintIntent({
            intentId: operation.intentId,
            productionId: operation.productionId,
            tenantId: operation.tenantId,
            businessId: operation.businessId,
            outcome: 'failed',
            occurredAt: new Date().toISOString(),
            error: message,
          });
        },
        describeError: toUserMessage,
      }));
    } catch (error) {
      dbg.error('label-print', 'label_print_failed', {
        device_id: await getDeviceId().catch(() => null),
        intent_id: operation.intentId,
        production_id: operation.productionId,
        printer_driver: operation.printer.driver,
        template_id: operation.template.id,
        stage,
        physical_print_completed: physicalPrintCompleted,
        technical_error: serializePrintError(error),
      });
      await refreshRecoverablePrintIntents();
      throw error;
    }
    await refreshRecoverablePrintIntents();
  }, [enqueueStockCommand, refreshRecoverablePrintIntents]);

  useEffect(() => {
    const engine = getSyncEngine();
    let initialized = false;
    let lastPushAt: string | null = null;
    let lastTerminalStatus: string | null = null;
    return engine.subscribe((state) => {
      const terminalStatus = (
        state.status === 'idle'
        || state.status === 'offline'
        || state.status === 'error'
      ) ? state.status : null;
      const initialRefresh = !initialized;
      const shouldRefresh = initialRefresh
        || state.lastPushAt !== lastPushAt
        || (
          terminalStatus !== null
          && terminalStatus !== lastTerminalStatus
        );
      initialized = true;
      lastPushAt = state.lastPushAt;
      lastTerminalStatus = terminalStatus;
      if (shouldRefresh) {
        void refreshRecoverablePrintIntents(initialRefresh);
      }
    });
  }, [refreshRecoverablePrintIntents]);

  async function resolveLabelReconciliation(
    incident: ProductionLabelPrintReconciliation,
  ): Promise<void> {
    const tenantId = profile?.active_tenant_id;
    const businessId = profile?.active_business_id;
    if (
      !tenantId
      || !businessId
      || incident.tenant_id !== tenantId
      || incident.business_id !== businessId
    ) {
      setToast('L’incidente appartiene a un’altra sede: ricarica la pagina');
      return;
    }
    setReconciliationBusyId(incident.intent_id);
    try {
      await getSyncEngine().resolveProductionLabelPrintReconciliation({
        intentId: incident.intent_id,
        tenantId,
        businessId,
      });
      await refreshRecoverablePrintIntents();
      setToast('Incidente risolto · nessun movimento di magazzino aggiunto');
    } catch (error) {
      setToast(toUserMessage(error));
    } finally {
      setReconciliationBusyId(null);
    }
  }

  async function retryRecoverablePrintIntent(
    intent: RecoverableLabelPrintIntent,
  ): Promise<void> {
    if (isLabelPrintIntentActive(intent.id)) return;
    const tenantId = profile?.active_tenant_id;
    if (
      !tenantId
      || tenantId !== intent.tenant_id
      || profile?.active_business_id !== intent.business_id
    ) {
      setToast('L’intento appartiene a un’altra sede: ricarica la pagina');
      return;
    }
    setRecoveryBusyId(intent.id);
    setRecoverablePrintIntents((current) => current.filter((row) => row.id !== intent.id));
    await withActiveLabelPrintIntent(intent.id, async () => {
      let physicalPrintCompleted = false;
      let stage = 'resolve_printer';
      try {
        const { printer: snapshotPrinter, template, label_data: labelData, copies: retryCopies } = intent.print_job;
        const currentDefaultId = resolveDefaultPrinterId(
          printers,
          deviceDefaultId,
          PREVIEW_PRINTER_ID,
        );
        const printer = printers.find((candidate) => (
          candidate.driver === snapshotPrinter.driver
          && candidate.address === snapshotPrinter.address
        )) ?? printers.find((candidate) => candidate.id === currentDefaultId);
        if (!printer || printer.driver === 'preview') {
          throw new Error('Configura una stampante Bluetooth valida in Impostazioni → Stampante prima di ristampare.');
        }
        stage = 'connect';
        const adapter = createPrinterAdapter(printer.driver);
        const session = await adapter.connect({
          id: printer.id,
          name: printer.name,
          driver: printer.driver,
          connection: printer.connection,
          address: printer.address,
        });
        stage = 'print';
        await adapter.printLabel(
          session,
          {
            id: template.id,
            tenant_id: '',
            name: template.name,
            preset: template.preset,
            width_mm: template.width_mm,
            height_mm: template.height_mm,
            elements: template.elements as never,
            required_allergens: template.required_allergens ?? false,
          } as never,
          labelData,
          retryCopies,
        );
        physicalPrintCompleted = true;
        stage = 'finalize_printed';
        await getSyncEngine().finalizeProductionLabelPrintIntent({
          intentId: intent.id,
          productionId: intent.production_id,
          tenantId,
          businessId: intent.business_id,
          outcome: 'printed',
          occurredAt: new Date().toISOString(),
        });
        setToast(`Stampa recuperata · ${retryCopies}×`);
      } catch (error) {
        dbg.error('label-print', 'historical_label_retry_failed', {
          device_id: await getDeviceId().catch(() => null),
          intent_id: intent.id,
          physical_print_completed: physicalPrintCompleted,
          printer_driver: intent.print_job.printer.driver,
          stage,
          technical_error: serializePrintError(error),
        });
        if (!physicalPrintCompleted) {
          try {
            await getSyncEngine().finalizeProductionLabelPrintIntent({
              intentId: intent.id,
              productionId: intent.production_id,
              tenantId,
              businessId: intent.business_id,
              outcome: 'failed',
              occurredAt: new Date().toISOString(),
              error: toUserMessage(error),
            });
          } catch {
            // L'intent resta pending e continua a essere visibile.
          }
        }
        setToast(
          physicalPrintCompleted
            ? `Stampa completata, esito non confermato. Non ristampare: usa “Conferma già stampata”. ${toUserMessage(error)}`
            : toUserMessage(error),
        );
      } finally {
        setRecoveryBusyId(null);
      }
    });
    await refreshRecoverablePrintIntents();
  }

  async function resolveRecoverablePrintIntent(
    intent: RecoverableLabelPrintIntent,
  ): Promise<void> {
    const tenantId = profile?.active_tenant_id;
    if (
      !tenantId
      || tenantId !== intent.tenant_id
      || profile?.active_business_id !== intent.business_id
    ) {
      setToast('L’intento appartiene a un’altra sede: ricarica la pagina');
      return;
    }
    setRecoveryBusyId(intent.id);
    try {
      await getSyncEngine().finalizeProductionLabelPrintIntent({
        intentId: intent.id,
        productionId: intent.production_id,
        tenantId,
        businessId: intent.business_id,
        outcome: 'printed',
        occurredAt: new Date().toISOString(),
      });
      setToast('Stampa confermata senza ristampare');
    } catch (error) {
      setToast(toUserMessage(error));
    } finally {
      setRecoveryBusyId(null);
      await refreshRecoverablePrintIntents();
    }
  }
  // T-221 (collettiva): con collettiva+magazzino attivi la stampa etichetta
  // di una preparazione da ricetta CARICA a magazzino il prodotto finito
  // (stock_in con lotto = lp_code), che poi i DDT scaricano all'emissione.
  const collettivaEnabled = useCollettivaEnabled();
  // T-097: deep-link da TodayPage card "Tracciabilità" → apriamo direttamente
  // il tab Storico. Si controlla `?tab=history` nella query string del URL
  // al mount; chi naviga normalmente vede il tab "Nuova preparazione" di default.
  const [tab, setTab] = useState<'new' | 'history' | 'collprelabel'>(() => {
    const params = new URLSearchParams(location.search);
    return params.get('tab') === 'history' ? 'history' : 'new';
  });
  // T-096: testo di ricerca per filtrare lo storico etichette.
  // Filtra in-memory su `payload.data.product`, `payload.lp_code`, `payload.preset`
  // (case-insensitive, trim). Vuoto = mostra tutto.
  const [historySearch, setHistorySearch] = useState<string>('');
  // Storico etichette: ordinamento + filtri ("organico interessante").
  // Default: per DATA DI PRODUZIONE (più recente prima), raggruppato per giorno.
  const [historySort, setHistorySort] = useState<'produced' | 'expiry'>('produced');
  const [historyStatus, setHistoryStatus] = useState<'all' | 'valid' | 'expiring' | 'expired'>('all');
  const [historyPeriod, setHistoryPeriod] = useState<'all' | 'today' | '7d' | '30d'>('30d');
  const [historyNow, setHistoryNow] = useState(() => Date.now());
  useEffect(() => {
    if (location.pathname !== '/haccp/labels' || tab !== 'history') return;
    setHistoryNow(Date.now());
    const timer = setInterval(() => setHistoryNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, [location.pathname, tab]);
  const [historyPage, setHistoryPage] = useState<{ key: string; limit: number } | null>(null);
  useEffect(() => { setHistoryPage(null); }, [activeTenantId, activeBusinessId]);
  const historyPageKey = JSON.stringify([activeTenantId, activeBusinessId, historySearch, historySort, historyStatus, historyPeriod]);
  const historyLimit = historyPage?.key === historyPageKey ? historyPage.limit : LABEL_HISTORY_PAGE_SIZE;
  const visibleHistory = useMemo(() => scopedHistory.filter((label) =>
    !voidedIds.has(label.id) && label.payload.data.is_reprint !== true
    && label.payload.preset !== 'campione' && label.payload.data.sample !== true),
  [scopedHistory, voidedIds]);
  const historySelection = useMemo(() => {
    const now = historyNow;
    const search = historySearch.trim().toLowerCase();
    const matching = visibleHistory.filter((label) => {
      const payload = label.payload;
      return (!search || [payload.data.product, visibleLabelLotCode(payload), payload.preset]
        .some(value => String(value ?? '').toLowerCase().includes(search)))
        && (historyStatus === 'all' || labelHistoryStatus(label, now) === historyStatus);
    });
    const sorted = matching.filter((label) => {
      if (historyPeriod === 'all') return true;
      const produced = labelProducedTs(label);
      if (historyPeriod === 'today') return new Date(produced).toDateString() === new Date(now).toDateString();
      return now - produced <= (historyPeriod === '7d' ? 7 : 30) * HISTORY_DAY_MS;
    }).sort((a, b) => historySort === 'expiry'
      ? (labelExpiryTs(a) || Infinity) - (labelExpiryTs(b) || Infinity)
      : labelProducedTs(b) - labelProducedTs(a));
    return { sorted, hasEarlier: sorted.length < matching.length };
  }, [visibleHistory, historySearch, historyStatus, historyPeriod, historySort, historyNow]);
  const historyVisibleIds = useMemo(() => historyDataMissing ? []
    : historySelection.sorted.slice(0, historyLimit).map(label => label.id),
  [historyDataMissing, historySelection, historyLimit]);
  const historyPayloadQuery = useLabelHistoryEvents(historyScope, historyVisibleIds,
    location.pathname === '/haccp/labels' && tab === 'history' && !historyDataMissing);
  // Query placeholder data disappears if the next page fails. Keep the last
  // successful payloads for this scope, intersected with the current selection.
  const lastHistoryPayloads = useRef<{ scope: typeof historyScope; events: LabelHistoryEvent[] } | null>(null);
  useEffect(() => {
    if (historyPayloadQuery.data && !historyPayloadQuery.isPlaceholderData) {
      lastHistoryPayloads.current = { scope: historyScope, events: historyPayloadQuery.data };
    }
  }, [historyPayloadQuery.data, historyPayloadQuery.isPlaceholderData, historyScope]);
  const historyPayloads = historyPayloadQuery.data
    ?? (lastHistoryPayloads.current?.scope === historyScope ? lastHistoryPayloads.current.events : undefined);
  const historyRows = useMemo(() => {
    const byId = new Map(historyPayloads?.map(event => [event.id, event]));
    return historyVisibleIds.flatMap(id => {
      const event = byId.get(id);
      return event ? [applyLotCorrections(event, correctionIndex)] : [];
    });
  }, [historyPayloads, historyVisibleIds, correctionIndex]);
  const historyPayloadMissing = historyRows.length < historyVisibleIds.length;
  const historyPayloadError = historyPayloadQuery.isError
    || (historyPayloadQuery.isSuccess && !historyPayloadQuery.isFetching && historyPayloadMissing);
  const historyLoading = historyDataMissing || (historyPayloadMissing && !historyPayloadError);
  function retryHistoryRead(): void {
    void Promise.all([historyQuery.refetch(), stockInQuery.refetch(), stockOutQuery.refetch(),
      queryClient.invalidateQueries({ queryKey: ['masters', 'businesses'] }), historyPayloadQuery.refetch()]);
  }
  useEffect(() => {
    if (location.pathname === '/haccp/labels' && new URLSearchParams(location.search).get('tab') === 'history') {
      setTab('history');
    }
  }, [location.pathname, location.search]);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const celebrate = useCelebrate();

  // Modal preparazione
  const [open, setOpen] = useState(false);
  // Wizard a step: 1 = cosa prepari · 2 = ingredienti/lotti · 3 = stampa
  const [prepStep, setPrepStep] = useState<1 | 2 | 3>(1);
  // Anteprima visiva reale (render del template, niente stampa né scarico).
  const [showVisual, setShowVisual] = useState(false);

  // In-app preview: la stampante "anteprima" pubblica un evento window con
  // il PNG renderizzato; lo intercettiamo qui e mostriamo l'IonModal sotto.
  const [inAppPreview, setInAppPreview] = usePrinterPreview('/haccp/labels');
  // In-app trace page viewer: invece di aprire Chrome esterno con
  // window.open, mostriamo la pagina di tracciabilità in un IonModal con
  // iframe — l'utente non esce mai dall'app.
  const [inAppTraceUrl, setInAppTraceUrl] = useState<string | null>(null);

  // T-218: su web/PWA il modal+iframe rompe i link `target="_blank"` della
  // trace-page (es. "apri scansione" dei DDT: iOS Safari blocca i popup
  // generati dentro un iframe fuori dal gesto utente) → su piattaforma non
  // nativa apriamo la pagina in una nuova tab DENTRO il gesto utente, dove i
  // link ai documenti funzionano nativamente. Su nativo resta il modal in-app.
  const openTracePage = useCallback((url: string): void => {
    if (Capacitor.isNativePlatform()) {
      setInAppTraceUrl(url);
    } else {
      window.open(url, '_blank', 'noopener');
    }
  }, []);



  // Modal "Materia prima" (scongelamento / apertura confezione).
  // Flusso a 3 step: 1) scelta tipo azione, 2) scelta lotto food, 3) form scadenza.
  const [materialOpen, setMaterialOpen] = useState(false);
  const [materialAction, setMaterialAction] = useState<'thaw' | 'open' | null>(null);
  // T-095: per il flusso thaw, source = da quale catalogo prendiamo l'oggetto
  // da scongelare. 'production' = preparazione del cuoco già abbattuta+
  // congelata (productions). 'food' = alimento crudo surgelato (foods).
  // 'open' resta sempre su 'food' (non si "apre" una preparazione del cuoco).
  const [materialSource, setMaterialSource] = useState<'production' | 'food'>('food');
  const [materialProductionId, setMaterialProductionId] = useState<string>('');
  // T-099: nome del "gruppo articolo" su cui l'utente ha tappato nello step 2.
  // Pivot per il SearchPicker dei lotti specifici di quella preparazione.
  // Vuoto = nessun gruppo aperto.
  const [materialProductionGroupName, setMaterialProductionGroupName] = useState<string>('');
  const [materialFoodId, setMaterialFoodId] = useState<string>('');
  const [materialLotKey, setMaterialLotKey] = useState<string>('');
  const [materialFoodSearch, setMaterialFoodSearch] = useState<string>('');
  const [materialQty, setMaterialQty] = useState<string>('');
  const [materialShelfHours, setMaterialShelfHours] = useState<string>('');
  const [materialPrinterId, setMaterialPrinterId] = useState<string>('');
  const [materialCopies, setMaterialCopies] = useState<number>(1);
  const [materialReplacementPrompt, setMaterialReplacementPrompt] = useState<LotRow | null>(null);
  const confirmedMaterialReplacementKey = useRef<string | null>(null);
  // T-503 follow-up: sui telefoni la vecchia anteprima sticky occupava quasi
  // tutto lo step operativo e copriva validità, stampante e copie. L'anteprima
  // è ora secondaria e parte sempre chiusa; l'operatore la apre solo se serve.
  const [materialPreviewExpanded, setMaterialPreviewExpanded] = useState(false);
  // T-095: feedback "scan in corso" + ultimo errore di scan per non far perdere
  // di vista all'utente cosa è successo (la camera può essere lenta).
  const [materialScanBusy, setMaterialScanBusy] = useState(false);

  // Form state
  const [mode, setMode] = useState<'recipe' | 'free'>('recipe');
  const [freeLabelKind, setFreeLabelKind] = useState<'product' | 'preparation'>('product');
  const singleProductLabel = mode === 'free' && freeLabelKind === 'product';
  const [preparationName, setPreparationName] = useState('');
  const [recipeId, setRecipeId] = useState<string>('');
  const [shelfDays, setShelfDays] = useState<string>('3');
  // T-253: data di produzione (YYYY-MM-DD). Default = oggi; modificabile per
  // stampare etichette retrodatate. L'ora resta quella corrente di stampa.
  const [producedDate, setProducedDate] = useState<string>(todayDateStr());
  const [outputQty, setOutputQty] = useState<string>('');
  const [outputUom, setOutputUom] = useState<string>('porz');
  // yield originale della ricetta caricata (es. ricetta produce 1 kg → recipeYieldQty = 1).
  // Serve come denominatore per riproporzionare gli ingredienti quando cambia outputQty.
  const [recipeYieldQty, setRecipeYieldQty] = useState<number>(0);
  const [printerId, setPrinterId] = useState<string>('');
  const labelRasterOptions = useMemo(() => {
    const selected = printers.find(printer => printer.id === (materialOpen ? materialPrinterId : printerId));
    return selected?.driver === 'tspl' ? tsplProfile(selected.name) : undefined;
  }, [printers, materialOpen, materialPrinterId, printerId]);
  const [copies, setCopies] = useState<number>(1);
  const [copiesInput, setCopiesInput] = useState<string>('1');
  const [coreTemperature, setCoreTemperature] = useState('');
  const [coreMeasuredAt, setCoreMeasuredAt] = useState('');
  useEffect(() => { setCoreTemperature(''); setCoreMeasuredAt(''); }, [recipeId, profile?.id, activeTenantId, activeBusinessId]);
  const chillingRecipe = recipes.find(r => r.id === recipeId && r.chilling_enabled);
  function chillingCheck() {
    return chillingRecipe ? buildChillingCheck({ temperature: coreTemperature, measuredAt: coreMeasuredAt, target: chillingRecipe.temp_end_c, plannedMinutes: chillingRecipe.chilling_minutes }) : undefined;
  }
  const [lines, setLines] = useState<IngredientLine[]>([]);
  const [lotSelectionMode, setLotSelectionMode] = useState<'manual' | 'photo'>('manual');
  const [photoCaptureOpen, setPhotoCaptureOpen] = useState(false);
  const [photoBatch, setPhotoBatch] = useState<ProductionLotPhotoBatchDraft | null>(null);
  // Conserva la scelta esplicita precedente quando l'operatore prova la
  // modalità foto. Tornando a "Seleziona" non si perdono né il lotto scelto né
  // le foto già acquisite; al commit viene usata soltanto la modalità attiva.
  const manualLotKeys = useRef(new Map<string, string>());
  // L'id production nasce all'apertura del form, non al commit: in questo modo
  // anche offline ogni foto riceve subito il suo path remoto definitivo.
  const productionDraftId = useRef(crypto.randomUUID());
  // T-229 (collettiva): servizio del pasto (pranzo/cena) per la scheda di
  // produzione giornaliera. Vuoto = non indicato.
  const [mealService, setMealService] = useState<'' | 'pranzo' | 'cena'>('');
  // T-229 (collettiva): la ricetta selezionata definisce le porzioni base →
  // flusso "a porzioni" (lo scaling outputQty/recipeYieldQty diventa
  // porzioniProdotte/porzioniBase, 1 etichetta per porzione).
  const selectedRecipePortions = useMemo(() => {
    if (!collettivaEnabled || mode !== 'recipe' || !recipeId) return 0;
    const r = recipes.find((x) => x.id === recipeId);
    // T-242: porzioni base = yield_qty quando l'unità è "porzioni" (campo unico),
    // altrimenti yield_portions esplicito.
    return recipeBasePortions(r);
  }, [collettivaEnabled, mode, recipeId, recipes]);
  const portionFlow = selectedRecipePortions > 0;

  // Picker per aggiungere ingrediente
  const [pickFoodId, setPickFoodId] = useState<string>('');
  const [pickLotKey, setPickLotKey] = useState<string>('');
  const [pickQty, setPickQty] = useState<string>('');
  // Stato del picker "Aggiungi ingrediente": chiuso = bottone CTA tratteggiato,
  // aperto = card form con bordo blu (vedi rendering + UX redesign 2026-05-07)
  const [pickerOpen, setPickerOpen] = useState<boolean>(false);
  // Creazione "al volo" di un nuovo alimento dal picker ingrediente (come in
  // RecipesPage). Al salvataggio l'alimento viene auto-selezionato nel picker;
  // se non ha lotti compare il form "primo lotto" qui sotto.
  const [pickFoodEditorOpen, setPickFoodEditorOpen] = useState(false);
  // Quale selettore-ricerca a tutto schermo è aperto. Oggetto taggato così
  // ogni lista (anche lotti/stampanti) sa su quale riga/contesto agire.
  type SearchPickerState =
    | { kind: 'recipe' }
    | { kind: 'food' }
    | { kind: 'lineLot'; uid: string; foodId: string }
    | { kind: 'pickLot' }
    | { kind: 'printer' }
    | { kind: 'materialFood' }
    | { kind: 'materialLot' }
    | { kind: 'materialPrevLot' }
    | { kind: 'materialPrinter' }
    // T-095: picker della preparazione congelata (productions filtrate)
    | { kind: 'materialProduction' }
    // T-099: picker dei lotti specifici di un gruppo preparazione (data + LP code)
    | { kind: 'materialProductionLot' }
    | null;
  const [searchPicker, setSearchPicker] = useState<SearchPickerState>(null);
  // Busy condiviso del form "primo lotto" (un solo submit alla volta).
  const [newLotBusy, setNewLotBusy] = useState(false);
  // Ref per autofocus del nome preparazione (modalità "Libera").
  const prepNameRef = useRef<HTMLIonInputElement>(null);

  // Bump templateVersion per forzare il ricarico dopo "Salva" dall'editor.
  const [templateVersion, setTemplateVersion] = useState(0);
  // Preset GLOBALI (default per formato gestiti dall'owner): fetch+cache, usati
  // come fallback in pickTemplate quando il ristorante non ha una variante.
  const [globalPresets, setGlobalPresets] = useState<Record<string, LabelTemplate>>({});
  useEffect(() => {
    void loadGlobalPresets().then((m) => setGlobalPresets(m as unknown as Record<string, LabelTemplate>));
  }, [templateVersion]);
  // Tenant selezionato per la modifica del layout — separato dall'activeTenantId
  // così il consulente può editare il layout di qualsiasi ristorante senza
  // cambiare il contesto attivo dell'app.
  const [labelTenantId, setLabelTenantId] = useState<string | null>(activeTenantId);
  // Sincronizza il default quando cambia il tenant attivo (es. primo caricamento)
  const [labelTenantInitialized, setLabelTenantInitialized] = useState(false);
  useEffect(() => {
    if (!labelTenantInitialized && activeTenantId) {
      setLabelTenantId(activeTenantId);
      setLabelTenantInitialized(true);
    }
  }, [activeTenantId, labelTenantInitialized]);

  // Lista nomi tenant per il selettore (solo per owner/consulente con più tenant)
  const allTenantIds = profile?.tenants ?? [];
  const { data: allTenantsData = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['tenants-names', allTenantIds.join(',')],
    enabled: allTenantIds.length > 1,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const sb = getSupabase();
      const { data, error } = await sb
        .from('tenants')
        .select('id, name')
        .in('id', allTenantIds);
      if (error) throw error;
      return (data ?? []) as { id: string; name: string }[];
    },
  });

  // Template DB per il tenant selezionato nel selettore (può essere diverso
  // dall'activeTenantId, quindi non possiamo usare realTemplates che è
  // scoped al tenant attivo).
  const { data: labelTenantTemplates = [] } = useQuery<LabelTemplate[]>({
    queryKey: ['label-templates-for-tenant', labelTenantId],
    enabled: !!labelTenantId,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const sb = getSupabase();
      const { data, error } = await sb
        .from('label_templates')
        .select('*')
        .eq('tenant_id', labelTenantId!)
        .is('archived_at', null);
      if (error) throw error;
      return (data ?? []) as LabelTemplate[];
    },
  });

  const template = useMemo(
    () => {
      // `templateVersion` invalida esplicitamente la cache dopo un salvataggio
      // in localStorage: pickTemplate legge quella sorgente in modo sincrono.
      void templateVersion;
      return pickTemplate(
        labelTenantId === activeTenantId ? realTemplates : labelTenantTemplates,
        labelTenantId,
        globalPresets,
      );
    },
    [realTemplates, labelTenantTemplates, templateVersion, labelTenantId, activeTenantId, globalPresets],
  );

  const [editorOpen, setEditorOpen] = useState(false);

  // T-281: default = stampante PREDEFINITA del dispositivo (poi prima reale,
  // poi anteprima). Impostata UNA volta quando il device store è caricato, così
  // non sovrascrive le scelte manuali successive dell'utente.
  const prepPrinterInit = useRef(false);
  useEffect(() => {
    if (prepPrinterInit.current) return;
    if (!devicePrintersLoaded) return;
    prepPrinterInit.current = true;
    if (!printerId) {
      setPrinterId(resolveDefaultPrinterId(printers, deviceDefaultId, PREVIEW_PRINTER_ID));
    }
  }, [devicePrintersLoaded, printers, deviceDefaultId, printerId]);

  // T-221: filtro per sede attiva — ogni sede ("locale") ha il suo magazzino.
  // Gli eventi storici senza sede sono utilizzabili soltanto se il catalogo
  // dimostra che il tenant ha una singola sede attiva.
  const inActiveBusiness = useCallback(
    (event: StockInEvent): boolean =>
      stockEventMatchesBusiness(event, activeBusinessId, allowLegacyUnscopedBusiness),
    [activeBusinessId, allowLegacyUnscopedBusiness],
  );

  // ───────── T-303: pre-stampa etichette collettiva (solo Bluecoop) ─────────
  // Terzo segmento della pagina: una lista di alimenti (seed dal catalogo
  // ordini del preposto, poi personalizzabile) con copie suggerite per riga,
  // dalla quale si stampano in serie tutte le etichette del giorno. Il lotto è
  // la DATA di produzione (LP-DDMMYYYY, condiviso da tutta la giornata).
  const isCollPrelabel = activeTenantId === BLUECOOP_TENANT_ID && collettivaEnabled;
  const collPrelabelRows = useCollettivaPrelabelList(isCollPrelabel);
  const setCollPrelabel = useSetCollettivaPrelabelList();
  const collTemplate = useMemo(
    () => pickTemplate(realTemplates, activeTenantId, globalPresets),
    [realTemplates, activeTenantId, globalPresets],
  );
  const [collDate, setCollDate] = useState<string>(todayDateStr());
  // Servizio: la rotazione settimanale ha piatti diversi per pranzo e cena.
  const [collMeal, setCollMeal] = useState<'pranzo' | 'cena'>('pranzo');
  const [collPrinterId, setCollPrinterId] = useState<string>('');
  const [collCounts, setCollCounts] = useState<Record<string, string>>({});
  const [collBusy, setCollBusy] = useState(false);
  const [collManageOpen, setCollManageOpen] = useState(false);
  // Bozza editabile della lista in modalità "Modifica lista".
  const [collDraft, setCollDraft] = useState<CollettivaPrelabelRow[]>([]);
  // Override per-stampa del piatto dei 3 ordinari (default = rotazione del giorno).
  const [collOrdOverride, setCollOrdOverride] = useState<Partial<Record<OrdCat, string>>>({});
  // Quale ordinario sto modificando nell'alert "cambia piatto del giorno".
  const [collEditOrd, setCollEditOrd] = useState<OrdCat | null>(null);
  // T-406 FIX: id riga della bozza per cui sto scegliendo la RICETTA (picker).
  const [collRecipePickFor, setCollRecipePickFor] = useState<string | null>(null);

  // Catalogo ordini del preposto (order_catalog_rows) = "la lista che hanno i
  // preposti". Usato come default quando la lista personalizzata è vuota, così
  // la scheda mostra subito tutta la lista senza import manuale.
  const { data: collCatalog = [] } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['coll_order_catalog', activeTenantId],
    enabled: isCollPrelabel && !!activeTenantId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('order_catalog_rows')
        .select('id,label,sort_order')
        .eq('tenant_id', activeTenantId!)
        .eq('active', true)
        .is('archived_at', null)
        .order('sort_order');
      if (error) throw error;
      return ((data ?? []) as Array<{ id: string; label: string }>).map((r) => ({ id: r.id, name: r.label }));
    },
  });

  // Righe effettive: la lista personalizzata se esiste, altrimenti l'intero
  // catalogo ordini del preposto (personalizzando si salva una copia).
  const collRows = useMemo<Array<{ id: string; name: string; recipe_id?: string | null; ordinario_cat?: OrdCat | null; category?: string | null; variant_suffix?: string | null }>>(
    () => (collPrelabelRows.length > 0
      ? collPrelabelRows.map((r) => ({ id: r.id, name: r.name, recipe_id: r.recipe_id ?? null, ordinario_cat: r.ordinario_cat ?? null, category: r.category ?? r.ordinario_cat ?? null, variant_suffix: r.variant_suffix ?? (r.morbido ? 'morbido' : null) }))
      : collCatalog),
    [collPrelabelRows, collCatalog],
  );

  // T-380: mappa ricetta per nome normalizzato → aggancio recipe_id alla riga.
  const recipeByNorm = useMemo(() => {
    const m = new Map<string, RecipeRow>();
    for (const r of recipes) m.set(normDishName(r.name), r);
    return m;
  }, [recipes]);

  // Piatti del giorno (rotazione settimanale Bluecoop) per data + servizio scelti.
  const collDayDishes = useMemo<Partial<Record<OrdCat, string>>>(() => {
    if (!isCollPrelabel) return {};
    const slot = menuSlotForDate(collDate);
    const map: Partial<Record<OrdCat, string>> = {};
    for (const d of bluecoopMenuDishes(slot, slot.weekday, collMeal)) {
      map[d.category as OrdCat] = d.name;
    }
    return map;
  }, [isCollPrelabel, collDate, collMeal]);

  // Nome stampato + RICETTA agganciata di una riga.
  // - Ordinari (3 categorie): piatto del giorno (override → rotazione → nome
  //   generico), ricetta risolta per nome dalla rotazione.
  // - Righe esplicite: recipe_id salvato (nuovo modello) o match per nome
  //   (legacy) → output_name = nome ricetta se trovata.
  const resolveRowDish = useCallback(
    (row: { name: string; recipe_id?: string | null; ordinario_cat?: OrdCat | null; morbido?: boolean | null; variant_suffix?: string | null }): { product: string; ordinario: OrdCat | null; recipeId: string | null } => {
      const cat = row.ordinario_cat ?? ordinarioCategory(row.name);
      if (cat) {
        const dish = (collOrdOverride[cat]?.trim() || collDayDishes[cat] || '').trim();
        const base = dish || row.name;
        // T-406 FIX (diete/morbido): riga variante = STESSO piatto/ricetta
        // dell'ordinario del giorno della categoria, ma il nome STAMPATO aggiunge
        // il suffisso (es. "diabetico", "iposodico", "no latticini", "morbido").
        const suffix = (row.variant_suffix?.trim() || (row.morbido ? 'morbido' : '')).trim();
        const product = suffix ? `${base} ${suffix}` : base;
        const rec = dish ? recipeByNorm.get(normDishName(dish)) : undefined;
        return { product, ordinario: cat, recipeId: rec?.id ?? null };
      }
      const rec = row.recipe_id
        ? recipes.find((x) => x.id === row.recipe_id)
        : recipeByNorm.get(normDishName(row.name));
      return { product: rec?.name ?? row.name, ordinario: null, recipeId: rec?.id ?? row.recipe_id ?? null };
    },
    [collOrdOverride, collDayDishes, recipeByNorm, recipes],
  );

  // Seed delle copie del run dalle copie suggerite salvate (solo righe non ancora toccate).
  useEffect(() => {
    if (!isCollPrelabel) return;
    setCollCounts((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const r of collRows) {
        if (next[r.id] == null) {
          const sc = collPrelabelRows.find((x) => x.id === r.id)?.suggested_copies ?? 0;
          next[r.id] = sc > 0 ? String(sc) : '';
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [collRows, collPrelabelRows, isCollPrelabel]);

  // Stampante di default per il tab collettiva.
  useEffect(() => {
    if (!isCollPrelabel) return;
    if (!collPrinterId) {
      setCollPrinterId(resolveDefaultPrinterId(printers, deviceDefaultId, PREVIEW_PRINTER_ID));
    }
  }, [isCollPrelabel, printers, deviceDefaultId, collPrinterId]);

  const collDateLabel = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(collDate);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : collDate;
  }, [collDate]);

  const collTotalLabels = useMemo(
    () =>
      collRows.reduce(
        (acc, r) => acc + Math.max(0, Math.round(Number(collCounts[r.id] ?? '') || 0)),
        0,
      ),
    [collRows, collCounts],
  );

  // T-406 FIX (UX): il numero che il titolare mette sulla pagina principale è
  // il DEFAULT salvato (suggested_copies), non un valore usa-e-getta. Ogni
  // modifica lo ripersiste (debounced) nella config → la volta dopo riparte da lì.
  const copiesPersistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copiesPending = useRef<Record<string, number>>({});
  function persistCollCopies(id: string, n: number): void {
    copiesPending.current[id] = Math.max(0, Math.min(999, Math.round(n)));
    if (copiesPersistTimer.current) clearTimeout(copiesPersistTimer.current);
    copiesPersistTimer.current = setTimeout(() => {
      const pending = copiesPending.current;
      copiesPending.current = {};
      if (collPrelabelRows.length === 0) return; // nessuna config: non dumpare il catalogo
      const updated = collPrelabelRows.map((r) =>
        r.id in pending ? { ...r, suggested_copies: pending[r.id] } : r,
      );
      void setCollPrelabel.mutateAsync(updated).catch(() => { /* silent: non bloccare */ });
    }, 900);
  }

  function setCollCount(id: string, n: number): void {
    setCollCounts((q) => ({ ...q, [id]: n > 0 ? String(Math.min(999, n)) : '' }));
    persistCollCopies(id, n);
  }
  const collCount = (id: string): number => Math.max(0, Math.round(Number(collCounts[id] ?? '') || 0));

  // Apre la modalità "Modifica lista" con una bozza: parte dalle righe effettive
  // (lista personalizzata o catalogo del preposto), così il primo salvataggio
  // cattura il catalogo e lo rende editabile.
  function openCollManage(): void {
    setCollDraft(
      collRows.map((r, i) => {
        const src = collPrelabelRows.find((x) => x.id === r.id);
        // T-406 FIX: preserva i legami ricetta/categoria/ordinario/morbido (prima
        // venivano scartati → salvando "Modifica lista" si perdevano le ricette).
        return {
          id: r.id,
          name: r.name,
          suggested_copies: src?.suggested_copies ?? 0,
          sort_order: i,
          recipe_id: src?.recipe_id ?? r.recipe_id ?? null,
          ordinario_cat: src?.ordinario_cat ?? r.ordinario_cat ?? null,
          category: src?.category ?? (r.category as CollettivaPrelabelRow['category']) ?? null,
          variant_suffix: src?.variant_suffix ?? r.variant_suffix ?? (src?.morbido ? 'morbido' : null),
        };
      }),
    );
    setCollManageOpen(true);
  }


  async function saveCollList(): Promise<void> {
    try {
      const clean = collDraft
        .map((r) => ({ ...r, name: r.name.trim() }))
        .filter((r) => r.name.length > 0);
      await setCollPrelabel.mutateAsync(clean);
      setCollManageOpen(false);
      setToast('Lista salvata');
    } catch (e) {
      setToast(toUserMessage(e));
    }
  }

  // Stampa in serie: per ogni riga con copie>0 → 1 production (solo etichetta:
  // niente inputs/scarico stock) + N copie + 1 evento label_printed.
  async function printAllCollettiva(): Promise<void> {
    if (!profile?.active_tenant_id || !profile.active_business_id) {
      setToast('Nessun locale attivo'); return;
    }
    const printer = printers.find((p) => p.id === collPrinterId);
    if (!printer) { setToast('Seleziona la stampante'); return; }
    const jobs = collRows
      // T-406 (A5): normalizzazione sicura del nome stampato (trim + spazi singoli).
      .map((r) => { const d = resolveRowDish(r); return { name: d.product.replace(/\s+/g, ' ').trim(), recipeId: d.recipeId, copies: collCount(r.id) }; })
      .filter((j) => j.copies > 0);
    if (jobs.length === 0) { setToast('Imposta almeno una quantità'); return; }
    // T-380: la data pre-stampata è quella del giorno di PRODUZIONE scelto
    // (di norma domani); il servizio è collMeal. Le produzioni prestampa cadono
    // nel giorno giusto della scheda (produced_at = mezzogiorno locale).
    setCollBusy(true);
    let activeOperationKey: string | null = null;
    try {
      const producedAt = composeProducedAt(collDate);
      const lp = collettivaLotCode(producedAt);
      const batchId = collPrintBatchId.current ?? crypto.randomUUID();
      collPrintBatchId.current = batchId;
      let printed = 0;
      // T-406 (A1): ingredienti + allergeni dalla ricetta agganciata, per l'etichetta.
      const foodByIdColl = new Map(foods.map((f) => [f.id, f]));
      const recipeByIdColl = new Map(recipes.map((r) => [r.id, r]));
      for (const [jobIndex, job] of jobs.entries()) {
        const operationKey = `coll:${batchId}:${jobIndex}:${job.name}`;
        activeOperationKey = operationKey;
        if (completedCollPrintKeys.current.has(operationKey)) {
          printed += job.copies;
          continue;
        }
        const pendingOperation = durablePrintOperations.current.get(operationKey);
        if (pendingOperation) {
          await executeDurableLabelPrint(pendingOperation);
          durablePrintOperations.current.delete(operationKey);
          completedCollPrintKeys.current.add(operationKey);
          printed += pendingOperation.copies;
          continue;
        }

        const productionId = crypto.randomUUID();
        const traceToken = Array.from(crypto.getRandomValues(new Uint8Array(16)))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('');
        const jobRecipe = job.recipeId ? recipeByIdColl.get(job.recipeId) : undefined;
        const jobIngNames = (jobRecipe?.ingredients ?? []).map((x) => ingName(x.food_id, foodByIdColl.get(x.food_id)?.name ?? '')).filter(Boolean);
        const jobAllergens = [...new Set((jobRecipe?.ingredients ?? []).flatMap((x) => foodByIdColl.get(x.food_id)?.allergens ?? []))];
        // T-406 (A1): scadenza dalla shelf-life della ricetta agganciata.
        const jobShelfDays = jobRecipe ? shelfLifeDaysFromRecipe(jobRecipe) : 0;
        const jobExpiresAt = jobShelfDays > 0 ? new Date(producedAt.getTime() + jobShelfDays * 86400000).toISOString() : null;
        // 1) productions row: T-380 PRE-STAMPA recipe-based. Aggancia la ricetta
        //    (recipe_id) + prestampa=true + meal_service, SENZA porzioni (le
        //    copie sono margine, non porzioni). Al mattino il piano RIUSA questa
        //    riga (le attacca source_production_plan_item_id + porzioni) → no
        //    doppioni. Nessun input/scarico stock in pre-stampa.
        const command: StockCommandV1 = StockCommandV1Schema.parse({
          version: 1,
          command_id: crypto.randomUUID(),
          tenant_id: profile.active_tenant_id,
          movement_class: 'production',
          economic_effect: 'internal',
          business_id: profile.active_business_id,
          production_id: productionId,
          reason: `Prestampa collettiva ${collDate}:${collMeal}`,
          inputs: [],
          outputs: [],
        });
        const masterEffects: StockCommandMasterEffectV1[] = [{
          table: 'productions',
          id: productionId,
          op: 'insert',
          base_version: 0,
          patch: {
            id: productionId,
            tenant_id: profile.active_tenant_id,
            business_id: profile.active_business_id,
            created_by: profile.id,
            produced_at: producedAt.toISOString(),
            output_name: job.name,
            recipe_id: job.recipeId,
            meal_service: collMeal,
            prestampa: true,
            portions: null,
            inputs: [],
            allergens_aggregated: [],
            trace_token: traceToken,
            lp_code: lp,
          },
        }];
        const labelData: Record<string, unknown> = {
          product: job.name,
          prepared_at: producedAt.toISOString(),
          now: producedAt.toISOString(),
          expires_at: jobExpiresAt,
          allergens: jobAllergens,
          ingredients: jobIngNames,
          lp_code: lp,
          trace_url: buildTraceUrl(traceToken),
          lot: lp,
        };
        const labelPayload: LabelPrintedPayload = {
          template_id: collTemplate.id,
          ...labelPrinterAudit(printer),
          preset: collTemplate.preset,
          data: {
            product: job.name,
            prestampa: true,
            production_id: productionId,
            trace_token: traceToken,
            ingredients: jobIngNames,
            allergens: jobAllergens,
            stock_command_version: 1,
            stock_command_not_applicable: true,
          },
          copies: job.copies,
          lp_code: lp,
          produced_at: producedAt.toISOString(),
          expires_at: jobExpiresAt,
        };
        const intentId = crypto.randomUUID();
        masterEffects.push(labelPrintIntentEffect({
          intentId,
          tenantId: profile.active_tenant_id,
          businessId: profile.active_business_id,
          productionId,
          commandId: command.command_id,
          auditClientUuid: crypto.randomUUID(),
          labelPayload,
          printJob: {
            printer,
            template: collTemplate,
            label_data: labelData,
            copies: job.copies,
          },
          requestedAt: new Date().toISOString(),
        }));
        const operation: DurableLabelPrintOperation = {
          key: operationKey,
          command,
          masterEffects,
          intentId,
          productionId,
          tenantId: profile.active_tenant_id,
          businessId: profile.active_business_id,
          printer,
          template: collTemplate,
          labelData,
          labelPayload,
          copies: job.copies,
        };
        durablePrintOperations.current.set(operationKey, operation);
        await executeDurableLabelPrint(operation);
        durablePrintOperations.current.delete(operationKey);
        completedCollPrintKeys.current.add(operationKey);
        printed += job.copies;
      }
      collPrintBatchId.current = null;
      completedCollPrintKeys.current.clear();
      activeOperationKey = null;
      setToast(`Pre-stampa collettiva · lotto ${collDateLabel} · ${printed} etichette (${jobs.length} piatti)`);
    } catch (e) {
      if (
        activeOperationKey
        && shouldDiscardDurablePrintOperation(e)
      ) {
        durablePrintOperations.current.delete(activeOperationKey);
        if (e instanceof DurableLabelPrintError && e.physicalPrintCompleted) {
          completedCollPrintKeys.current.add(activeOperationKey);
        }
      }
      setToast(toUserMessage(e));
    } finally {
      setCollBusy(false);
    }
  }

  // Metadati del lotto dagli eventi originali + quantità/disponibilità/costo
  // medio dalla proiezione canonica condivisa. Non usiamo il prezzo di un
  // acquisto arbitrario: lotti prodotti o trasferiti portano il proprio valore.
  const lots: LotRow[] = useMemo(() => {
    type LotMetadata = Omit<LotRow, 'uom' | 'available' | 'unit_cost' | 'currency'>;
    const metadata = new Map<string, LotMetadata>();
    const foodById = new Map(foods.map((f) => [f.id, f]));
    const supplierById = new Map(suppliers.map((s) => [s.id, s]));
    for (const ev of stockIn as StockInEvent[]) {
      if (!inActiveBusiness(ev)) continue;
      const food = foodById.get(ev.payload.food_id);
      const key = `${ev.payload.food_id}|${ev.payload.batch_lot ?? ''}`;
      const existing = metadata.get(key);
      if (existing) {
        if (!existing.supplier_id && ev.payload.supplier_id) {
          existing.supplier_id = ev.payload.supplier_id;
          existing.supplier_name = supplierById.get(ev.payload.supplier_id)?.name ?? null;
        }
        if (!existing.document_id && ev.payload.document_id) existing.document_id = ev.payload.document_id;
        if (!existing.storage_unit_id && ev.payload.storage_unit_id) {
          existing.storage_unit_id = ev.payload.storage_unit_id;
        }
        // T-259: conserva la production di origine (lotto prodotto-finito).
        if (!existing.source_production_id && ev.payload.source_production_id) {
          existing.source_production_id = ev.payload.source_production_id;
        }
        if (!existing.command_id && ev.payload.command_id) {
          existing.command_id = ev.payload.command_id;
        }
        if (!existing.received_at) existing.received_at = ev.client_ts;
      } else {
        const supplier = ev.payload.supplier_id ? supplierById.get(ev.payload.supplier_id) : undefined;
        metadata.set(key, {
          key,
          food_id: ev.payload.food_id,
          food_name: food?.name ?? '—',
          batch_lot: ev.payload.batch_lot,
          supplier_id: ev.payload.supplier_id ?? null,
          supplier_name: supplier?.name ?? null,
          document_id: ev.payload.document_id ?? null,
          storage_unit_id: ev.payload.storage_unit_id ?? null,
          expires: ev.payload.expiration_date,
          allergens: food?.allergens ?? [],
          source_production_id: ev.payload.source_production_id ?? null,
          command_id: ev.payload.command_id ?? null,
          received_at: ev.client_ts,
        });
      }
    }

    const projected = stockProjection.balances.flatMap((balance): LotRow[] => {
      if (!balance.batchLot?.trim()) return [];
      const key = `${balance.foodId}|${balance.batchLot}`;
      const food = foodById.get(balance.foodId);
      const carried = metadata.get(key);
      // Il projector canonico comprende già le prenotazioni delle bozze e
      // rende fail-closed un lotto con UdM di prenotazione incompatibile.
      const available = Math.max(0, balance.availableQty);
      const carryingCost = projectedCarryingCost(balance);
      return [{
        key,
        food_id: balance.foodId,
        food_name: food?.name ?? carried?.food_name ?? '—',
        batch_lot: balance.batchLot,
        supplier_id: carried?.supplier_id ?? null,
        supplier_name: carried?.supplier_name ?? null,
        document_id: carried?.document_id ?? null,
        storage_unit_id: carried?.storage_unit_id ?? null,
        uom: balance.uom,
        available,
        expires: balance.expirationDate ?? carried?.expires ?? null,
        allergens: food?.allergens ?? carried?.allergens ?? [],
        unit_cost: carryingCost.unitCost,
        currency: carryingCost.currency,
        source_production_id: carried?.source_production_id ?? null,
        command_id: carried?.command_id ?? null,
        received_at: carried?.received_at ?? null,
      }];
    });

    const productionById = new Map(scopedProductions.map((production) => [production.id, production]));
    const sourceFoodsByCommand = new Map<string, Set<string>>();
    for (const event of stockOut as LocalStockCommandEvent[]) {
      if (!stockEventMatchesBusiness(event, activeBusinessId, allowLegacyUnscopedBusiness)) continue;
      if (event.payload.movement_class !== 'production') continue;
      const commandId = event.payload.command_id;
      const foodId = event.payload.food_id;
      if (typeof commandId !== 'string' || typeof foodId !== 'string') continue;
      const sourceIds = sourceFoodsByCommand.get(commandId) ?? new Set<string>();
      sourceIds.add(foodId);
      sourceFoodsByCommand.set(commandId, sourceIds);
    }
    const withMaterialContext = projected.map((row): LotRow => {
      const food = foodById.get(row.food_id);
      const production = row.source_production_id
        ? productionById.get(row.source_production_id)
        : null;
      const withProductionDate = production
        ? { ...row, produced_at: production.produced_at }
        : row;
      const transformation = food
        ? materialTransformationFromDerivedFood(food)
        : null;
      if (!transformation) return withProductionDate;
      const sourceIds = row.command_id ? sourceFoodsByCommand.get(row.command_id) : undefined;
      const sourceFoodId = sourceIds?.size === 1 ? [...sourceIds][0] : null;
      return {
        ...withProductionDate,
        material_source_food_id: sourceFoodId && sourceFoodId !== row.food_id ? sourceFoodId : null,
        material_transformation: transformation,
        transformed_at: production?.produced_at ?? row.received_at ?? null,
      };
    });

    // Piano base (warehouse OFF): i lotti sono tracciati per *presenza*, non per
    // giacenza. Il projector applica anche register/close a quantità zero.
    if (!warehouseEnabled) {
      const closedKeys = new Set(
        stockProjection.balances
          .filter((balance) => balance.closed)
          .map((balance) => `${balance.foodId}|${balance.batchLot ?? ''}`),
      );
      return withMaterialContext
        .filter((row) => !closedKeys.has(row.key))
        .map((row) => ({ ...row, available: Number.POSITIVE_INFINITY }));
    }
    return withMaterialContext.filter((row) => row.available > 0.0001);
  }, [
    foods,
    activeBusinessId,
    allowLegacyUnscopedBusiness,
    inActiveBusiness,
    stockIn,
    stockOut,
    stockProjection.balances,
    scopedProductions,
    suppliers,
    warehouseEnabled,
  ]);

  const lotsByFood = useMemo(() => {
    const m = new Map<string, LotRow[]>();
    for (const l of lots) {
      const arr = m.get(l.food_id) ?? [];
      arr.push(l);
      m.set(l.food_id, arr);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => {
        const ea = a.expires ? Date.parse(a.expires) : Infinity;
        const eb = b.expires ? Date.parse(b.expires) : Infinity;
        return ea - eb;
      });
    }
    return m;
  }, [lots]);

  // La giacenza resta separata per articolo; soltanto il picker ricetta espone
  // sotto la materia prima anche i suoi lotti operativi aperti/scongelati.
  const recipeLotsByFood = useMemo(() => {
    const grouped = new Map<string, LotRow[]>();
    for (const lot of lots) {
      const own = grouped.get(lot.food_id) ?? [];
      own.push(lot);
      grouped.set(lot.food_id, own);
      if (lot.material_source_food_id) {
        const source = grouped.get(lot.material_source_food_id) ?? [];
        source.push(lot);
        grouped.set(lot.material_source_food_id, source);
      }
    }
    for (const candidates of grouped.values()) {
      candidates.sort((left, right) => {
        const leftDerived = left.material_transformation != null;
        const rightDerived = right.material_transformation != null;
        if (leftDerived !== rightDerived) return leftDerived ? -1 : 1;
        if (leftDerived && rightDerived) {
          const leftAt = Date.parse(left.transformed_at ?? '');
          const rightAt = Date.parse(right.transformed_at ?? '');
          return (Number.isFinite(rightAt) ? rightAt : 0)
            - (Number.isFinite(leftAt) ? leftAt : 0);
        }
        const leftExpiry = left.expires ? Date.parse(left.expires) : Infinity;
        const rightExpiry = right.expires ? Date.parse(right.expires) : Infinity;
        return leftExpiry - rightExpiry;
      });
    }
    return grouped;
  }, [lots]);

  function oldestMaterialLotToReplace(derivedFoodId: string): LotRow | null {
    const active = (lotsByFood.get(derivedFoodId) ?? [])
      .filter((lot) => lot.material_transformation != null)
      .sort((left, right) => {
        const leftAt = Date.parse(left.transformed_at ?? left.received_at ?? '');
        const rightAt = Date.parse(right.transformed_at ?? right.received_at ?? '');
        const safeLeft = Number.isFinite(leftAt) ? leftAt : Infinity;
        const safeRight = Number.isFinite(rightAt) ? rightAt : Infinity;
        return safeLeft - safeRight || left.key.localeCompare(right.key);
      });
    return active.length >= 2 ? active[0] ?? null : null;
  }

  function materialReplacementCommand(lot: LotRow): StockCommandV1 {
    if (!profile?.active_tenant_id || !profile.active_business_id || !lot.batch_lot) {
      throw new Error('Contesto del lotto precedente non disponibile');
    }
    const balance = stockProjection.balances.find((candidate) => (
      candidate.foodId === lot.food_id
      && candidate.batchLot === lot.batch_lot
    ));
    if (!balance || balance.onHandQty <= 0.0001) {
      throw new Error('Il lotto precedente non ha più una quantità da chiudere');
    }
    if (
      balance.reservedQty > 0.0001
      || Math.abs(balance.availableQty - balance.onHandQty) > 0.0001
    ) {
      throw new Error(
        `Il lotto ${lot.batch_lot} è impegnato da una bozza: liberalo prima di creare il terzo lotto`,
      );
    }
    return buildConsumeStockCommandV1({
      commandId: crypto.randomUUID(),
      tenantId: profile.active_tenant_id,
      businessId: profile.active_business_id,
      foodId: lot.food_id,
      lot: lot.batch_lot,
      expirationDate: lot.expires,
      qty: balance.onHandQty,
      uom: balance.uom,
      reason: `Sostituzione del lotto trasformato precedente ${lot.batch_lot}`,
      closeLot: true,
      operatorName: profile.full_name?.trim() || null,
      provenance: {
        supplierId: lot.supplier_id,
        documentId: lot.document_id,
        storageUnitId: lot.storage_unit_id,
        lineRef: `material-replacement:${lot.source_production_id ?? lot.key}`,
      },
    });
  }

  useEffect(() => {
    if (
      lotSelectionMode !== 'photo'
      || productionLotPhotoAvailable
    ) return;
    setLines((current) => current.map((line) => {
      const candidates = recipeLotsByFood.get(line.food_id) ?? [];
      const rememberedKey = manualLotKeys.current.get(line.uid);
      const lot = candidates.find((candidate) => candidate.key === rememberedKey)
        ?? candidates[0];
      if (!lot) {
        return {
          ...line,
          lot_key: '',
          batch_lot: null,
          supplier_id: null,
          supplier_name: null,
          document_id: null,
          available: 0,
          source_production_id: null,
        };
      }
      return {
        ...line,
        lot_key: lot.key,
        batch_lot: lot.batch_lot,
        supplier_id: lot.supplier_id,
        supplier_name: lot.supplier_name,
        document_id: lot.document_id,
        uom: lot.uom || line.uom,
        available: lot.available,
        allergens: lot.allergens,
        source_production_id: lot.source_production_id ?? null,
      };
    }));
    setLotSelectionMode('manual');
  }, [lotSelectionMode, recipeLotsByFood, mode, productionLotPhotoAvailable]);

  // Totale disponibile per l'alimento della ricetta: comprende anche i suoi
  // articoli operativi aperti/scongelati, che restano separati in magazzino.
  const foodTotalAvailable = useCallback((foodId: string): number => {
    const cands = recipeLotsByFood.get(foodId) ?? [];
    return cands.reduce((acc, c) => acc + c.available, 0);
  }, [recipeLotsByFood]);

  const availableLotsForPick = useMemo(() => {
    if (!pickFoodId) return [];
    return recipeLotsByFood.get(pickFoodId) ?? [];
  }, [pickFoodId, recipeLotsByFood]);

  // Materia prima: lista food filtrata per azione.
  // - Piano Pro (warehouse ON): food con almeno un lotto in giacenza
  //   (thaw → solo congelati/surgelati).
  // - Piano base (warehouse OFF): food con `current_batch_lot` valorizzato
  //   (= almeno un carico DDT/fattura ricevuto).
  const materialFoods = useMemo(() => {
    if (!materialAction) return [] as FoodRow[];
    const foodIdsWithLots = new Set(Array.from(lotsByFood.keys()));
    return foods
      .filter((f) => {
        if (f.archived_at) return false;
        if (isAutoMaterialDerivedFood(f)) return false;
        return warehouseEnabled
          ? foodIdsWithLots.has(f.id)
          : !!(f.current_batch_lot && f.current_batch_lot.trim());
      })
      .filter((f) => {
        if (materialAction === 'thaw') {
          const st = (f.storage_type ?? '').toLowerCase();
          return st === 'surgelato';
        }
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'it'));
  }, [foods, lotsByFood, materialAction, warehouseEnabled]);

  const visibleMaterialFoods = useMemo(
    () => filterMaterialFoodsByName(materialFoods, materialFoodSearch),
    [materialFoodSearch, materialFoods],
  );

  const materialFood = useMemo(
    () => foods.find((f) => f.id === materialFoodId) ?? null,
    [foods, materialFoodId],
  );

  // Preparazioni con Congelamento esplicitamente attivo nella ricetta.
  // Abbattimento e durata non costituiscono una dichiarazione di conservazione.
  // T-099/T-533: esclude le production già marcate esplicitamente come
  // scongelate. `inputs[].source_production_id` non basta: è anche il normale
  // riferimento di tracciabilità per un semilavorato usato in una ricetta.
  // T-100: set di production-id con almeno un label_printed event vivo.
  // Difesa per dati legacy: prima di T-100 `cancelLabel` cancellava solo
  // l'event ma lasciava viva la production master → ricompariva in lista
  // gonfiando il counter dei lotti. Oggi `cancelLabel` archivia anche la
  // production, ma questo filtro copre comunque i dati pre-fix (production
  // orfana senza alcuna label vivente).
  const labeledProductionIds = useMemo(() => {
    const s = new Set<string>();
    for (const e of scopedHistory) {
      const pid = e.payload?.data?.production_id as string | undefined;
      if (pid) s.add(pid);
    }
    return s;
  }, [scopedHistory]);

  // T-533: uno scongelamento partito da una materia prima non ha un
  // `source_production_id` nel proprio input (il lotto padre è uno stock lot,
  // non una production). Il suo evento `label_printed` è però marcato
  // `preset=decongelato`: usiamo quel fatto append-only per non riclassificare
  // il risultato come nuova preparazione congelata.
  const thawedProductionIds = useMemo(
    () => collectThawedProductionIds(scopedHistory),
    [scopedHistory],
  );
  const frozenProductions = useMemo(() => selectFrozenProductions(scopedProductions, {
    recipes,
    now: Date.now(),
    thawedProductionIds,
    labeledProductionIds,
    warehouseEnabled,
    lots,
  }), [scopedProductions, recipes, thawedProductionIds, labeledProductionIds, warehouseEnabled, lots]);

  // T-099: aggregazione per "articolo" (output_name). Il cuoco oggi vede una
  // riga per ogni production stampata; questo gli toglie la scelta del lotto
  // quando ha più stampe dello stesso prodotto in giorni diversi. Aggregando
  // per nome, vede UNA voce "Ragù della domenica" che al tap apre il picker
  // dei lotti specifici (data + LP code + scadenza). Coerente col flusso
  // "Materia prima → Apertura confezione" già esistente per i food.
  const frozenGroups = useMemo(() => {
    const groups = new Map<string, ProductionRow[]>();
    for (const p of frozenProductions) {
      const key = p.output_name?.trim() || '(senza nome)';
      const arr = groups.get(key) ?? [];
      arr.push(p);
      groups.set(key, arr);
    }
    // Ogni gruppo ha già le sue production in ordine (frozenProductions è
    // sortato desc); gli articoli sono invece ordinati per nome A–Z.
    return [...groups.entries()]
      .map(([name, ps]) => ({ name, productions: ps }))
      .sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base', numeric: true }));
  }, [frozenProductions]);

  const visibleFrozenGroups = useMemo(
    () => filterMaterialFoodsByName(frozenGroups, materialFoodSearch),
    [frozenGroups, materialFoodSearch],
  );

  const materialProduction = useMemo(
    () => (materialAction === 'open' ? scopedProductions : frozenProductions).find((p) => p.id === materialProductionId) ?? null,
    [frozenProductions, scopedProductions, materialAction, materialProductionId],
  );

  // T-099: lista dei lotti del gruppo correntemente "aperto" (= articolo
  // tappato dall'utente). Vuoto se non c'è gruppo selezionato.
  const materialProductionGroupLots = useMemo(() => {
    if (!materialProductionGroupName) return [] as ProductionRow[];
    const g = frozenGroups.find((x) => x.name === materialProductionGroupName);
    return g?.productions ?? [];
  }, [materialProductionGroupName, frozenGroups]);

  const materialLots = useMemo(() => {
    if (!materialFoodId) return [];
    return lotsByFood.get(materialFoodId) ?? [];
  }, [materialFoodId, lotsByFood]);

  const materialProductionStockLot = useMemo(() => {
    if (!warehouseEnabled || !materialProductionId) return null;
    const candidates = lots.filter((lot) => (
      lot.source_production_id === materialProductionId
      && lot.available > 0.0001
    ));
    return candidates.length === 1 ? candidates[0] ?? null : null;
  }, [lots, materialProductionId, warehouseEnabled]);

  // Auto-seleziona il lotto. Piano base: preferisci il `current_batch_lot`
  // del food (lotto attivo); Piano Pro: lotto unico se presente.
  useEffect(() => {
    if (!materialFoodId) { setMaterialLotKey(''); return; }
    if (!warehouseEnabled) {
      const activeLot = materialFood?.current_batch_lot ?? null;
      if (activeLot) {
        const found = materialLots.find((l) => l.batch_lot === activeLot);
        if (found) {
          setMaterialLotKey(found.key);
          return;
        }
      }
    }
    if (materialLots.length === 1) {
      setMaterialLotKey(materialLots[0]?.key ?? '');
    } else if (!materialLots.some((l) => l.key === materialLotKey)) {
      setMaterialLotKey('');
    }
  }, [materialFoodId, materialLots, materialLotKey, warehouseEnabled, materialFood]);

  // Quando l'utente sceglie food + azione, pre-popola lo SL in ore dal master.
  // Per i prodotti finiti generati da una ricetta, la configurazione specifica
  // della ricetta ha precedenza; in assenza usa l'anagrafica food.
  useEffect(() => {
    if (!materialFood || !materialAction) return;
    const linkedRecipe = materialFood.recipe_id
      ? recipes.find((recipe) => recipe.id === materialFood.recipe_id) ?? null
      : null;
    const derivedName = materialDerivedFoodName(materialFood.name, materialAction);
    const derivedFood = foods.find((food) =>
      !food.archived_at
      && food.creation_source === 'quick_label'
      && food.recipe_id == null
      && food.name === derivedName
    ) ?? null;
    const hours = recipeOperationShelfLifeHours(linkedRecipe, materialAction)
      ?? foodOperationShelfLifeHours(derivedFood, materialAction)
      ?? foodOperationShelfLifeHours(materialFood, materialAction);
    setMaterialShelfHours(hours == null ? '' : String(hours));
  }, [foods, materialFood, materialAction, recipes]);

  // Le preparazioni congelate non hanno un food selezionato: risali alla
  // ricetta della production e usa la sua shelf life di scongelamento.
  useEffect(() => {
    if (!materialProduction || !materialAction) return;
    const recipe = materialProduction.recipe_id
      ? recipes.find((candidate) => candidate.id === materialProduction.recipe_id) ?? null
      : null;
    const hours = recipeOperationShelfLifeHours(recipe, materialAction);
    setMaterialShelfHours(hours == null ? '' : String(hours));
  }, [materialProduction, materialAction, recipes]);

  // T-281: flusso materia prima — default = stampante predefinita del
  // dispositivo (poi prima reale, poi anteprima), impostata all'apertura.
  useEffect(() => {
    if (!materialOpen || materialPrinterId) return;
    setMaterialPrinterId(resolveDefaultPrinterId(printers, deviceDefaultId, PREVIEW_PRINTER_ID));
  }, [materialOpen, materialPrinterId, printers, deviceDefaultId]);

  // T-095: default segment quando l'utente sceglie "Scongelamento".
  // Se ci sono preparazioni congelate disponibili, preferisci quella sezione
  // (uso più frequente: il cuoco scongela un proprio prep, non una bustina
  // di pesce surgelato). Se non ce ne sono, fallback su 'food'. 'open'
  // resta sempre su 'food' (non si "apre" una preparazione).
  useEffect(() => {
    if (materialAction === 'thaw') {
      // T-099: usa frozenGroups.length (aggregato) invece di frozenProductions.
      // Equivalente in pratica (ogni group ha ≥1 prod), ma semanticamente corretto.
      setMaterialSource(frozenGroups.length > 0 ? 'production' : 'food');
    } else if (materialAction === 'open') {
      setMaterialSource('food');
    }
    // dipendiamo solo dall'azione, non da frozenGroups.length, altrimenti
    // se nuove productions arrivano via sync mentre l'utente sta scegliendo,
    // gli sposteremmo il segment sotto le dita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialAction]);

  // La ricerca appartiene solo ad "Apertura confezione": tornando alla scelta
  // azione o passando a scongelamento non deve restare un filtro invisibile.
  useEffect(() => {
    if (materialAction !== 'open') setMaterialFoodSearch('');
  }, [materialAction]);

  // T-095: se l'utente cambia segment, azzera la selezione dell'altra source
  // così non resta uno stato "appeso" (es. avevo selezionato il prep, poi vado
  // su food, il prep resta selezionato in stato → strano per validazione).
  // T-099: reset anche del gruppo aperto.
  useEffect(() => {
    if (materialSource === 'production') {
      setMaterialFoodId('');
      setMaterialLotKey('');
    } else {
      setMaterialProductionId('');
      setMaterialProductionGroupName('');
    }
  }, [materialSource]);

  // Pre-popola la quantità con il disponibile del lotto scelto (modificabile).
  useEffect(() => {
    if (!warehouseEnabled) return; // piano base: nessuna giacenza da pre-popolare
    if (!materialLotKey) return;
    const lot = materialLots.find((l) => l.key === materialLotKey);
    if (lot && Number.isFinite(lot.available)) setMaterialQty(String(lot.available));
  }, [materialLotKey, materialLots, warehouseEnabled]);

  // T-630: anche una preparazione congelata usa la quantità reale del lotto.
  // Il default resta il totale disponibile, ma l'operatore può indicare una
  // porzione inferiore (es. 0,2 kg su 1 kg) senza consumare il residuo.
  useEffect(() => {
    if (!warehouseEnabled || materialSource !== 'production') return;
    setMaterialQty(
      materialProductionStockLot
        ? String(materialProductionStockLot.available)
        : '',
    );
  }, [materialProductionId, materialProductionStockLot, materialSource, warehouseEnabled]);

  function resetMaterial(): void {
    setMaterialAction(null);
    // T-095: reset anche del nuovo source per non trascinare stato fra aperture.
    setMaterialSource('food');
    setMaterialProductionId('');
    // T-099: reset anche del gruppo aperto.
    setMaterialProductionGroupName('');
    setMaterialFoodId('');
    setMaterialLotKey('');
    setMaterialFoodSearch('');
    setMaterialQty('');
    setMaterialShelfHours('');
    setMaterialCopies(1);
    setMaterialReplacementPrompt(null);
    confirmedMaterialReplacementKey.current = null;
    setMaterialPreviewExpanded(false);
  }

  // T-606: la scansione seleziona la produzione mantenendo l'azione scelta.
  // Cade in errore esplicito (toast) se il QR non corrisponde a nessuna
  // production del tenant attivo o se è già scaduta.
  async function scanMaterialLabelQr(): Promise<void> {
    if (!materialAction) return;
    if (!isBarcodeScanSupported()) {
      setToast('Scansione QR non supportata su questo dispositivo');
      return;
    }
    setMaterialScanBusy(true);
    try {
      const raw = await scanBarcode();
      if (!raw) { setToast('Nessun codice riconosciuto'); return; }
      // Estrazione token: accetta sia full URL (https://trackeat.eu/t/<token>)
      // sia solo token nudo. Regex per il formato definito in trace-page:
      // /^[a-f0-9]{16,64}$/.
      let token = raw.trim();
      const m = token.match(/\/t\/([a-f0-9]{16,64})(?:[?#].*)?$/i);
      if (m) token = m[1];
      if (!/^[a-f0-9]{16,64}$/i.test(token)) {
        setToast('Il QR scansionato non è un\'etichetta TrackEAT valida');
        return;
      }
      const lower = token.toLowerCase();
      if (materialAction === 'open') {
        const source = scopedProductions.find((p) => (p.trace_token ?? '').toLowerCase() === lower);
        if (!source || source.archived_at || !labeledProductionIds.has(source.id)) {
          setToast('Etichetta non disponibile in questo locale');
          return;
        }
        if (!source.expires_at || !Number.isFinite(Date.parse(source.expires_at)) || Date.parse(source.expires_at) <= Date.now()) {
          setToast('La preparazione è scaduta o non ha una scadenza valida');
          return;
        }
        setMaterialSource('production');
        setMaterialProductionId(source.id);
        setToast(`Da aprire: ${source.output_name}`);
        return;
      }
      // Cerca prima nelle frozen ancora vive (caso d'uso principale).
      const frozen = frozenProductions.find((p) => (p.trace_token ?? '').toLowerCase() === lower);
      if (frozen) {
        setMaterialSource('production');
        setMaterialProductionId(frozen.id);
        setToast(`Selezionata: ${frozen.output_name}`);
        return;
      }
      // Fallback: cerca soltanto nelle produzioni della sede attiva per dare
      // feedback specifico senza poter selezionare lotti di un altro locale.
      const any = scopedProductions.find((p) => (p.trace_token ?? '').toLowerCase() === lower);
      if (any) {
        if (
          warehouseEnabled
          && !lots.some((lot) => lot.source_production_id === any.id && lot.available > 0.0001)
        ) {
          setToast(`"${any.output_name}" non ha più quantità congelata disponibile.`);
          return;
        }
        const exp = any.expires_at ? new Date(any.expires_at) : null;
        if (exp && exp.getTime() < Date.now()) {
          setToast(`"${any.output_name}" è scaduta il ${exp.toLocaleDateString('it-IT')} — non scongelabile.`);
          return;
        }
        // T-100: production esiste ma non è in giacenza (es. etichetta
        // cancellata dallo storico via cancelLabel).
        if (!labeledProductionIds.has(any.id)) {
          setToast(`"${any.output_name}" non è più in giacenza (etichetta cancellata dallo storico).`);
          return;
        }
        setToast(`"${any.output_name}" non risulta congelata. Usa "M. prima" per altri lotti.`);
        return;
      }
      setToast('Etichetta non trovata: non corrisponde a nessuna preparazione di questo locale');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // L'utente potrebbe aver annullato la fotocamera: messaggio gentile.
      if (msg.toLowerCase().includes('user cancel') || msg.toLowerCase().includes('cancelled')) {
        return;
      }
      setToast(`Scansione fallita: ${msg}`);
    } finally {
      setMaterialScanBusy(false);
    }
  }

  // Stampa rapida contiene esclusivamente le stelle scelte dall'account o
  // dall'operatore tablet attivo. La frequenza di stampa non crea preferiti.
  const quickPrintRecipes = useMemo(
    () => selectPrintableFavoriteRecipes(recipes, favoriteIds),
    [favoriteIds, recipes],
  );
  const hasPrintableRecipes = recipes.some(
    (recipe) => !recipe.archived_at && !recipe.is_food_cost_only && recipe.variant_of_recipe_id == null,
  );

  // Aggregati per la conferma e per l'etichetta
  const aggregatedAllergens = useMemo(() => {
    const set = new Set<string>();
    for (const l of lines) for (const a of l.allergens ?? []) set.add(a);
    if (recipeId) {
      const r = recipes.find((x) => x.id === recipeId);
      for (const a of r?.allergens ?? []) set.add(a);
    }
    return [...set];
  }, [lines, recipeId, recipes]);

  const expiresAt = useMemo(() => {
    const days = Number(shelfDays.replace(',', '.'));
    const ms = (Number.isFinite(days) && days > 0 ? days : 3) * 86_400_000;
    // T-253: scadenza calcolata dalla data di produzione scelta (di default oggi).
    return new Date(composeProducedAt(producedDate).getTime() + ms);
  }, [shelfDays, producedDate]);

  // T-298 (collettiva): deep-link `?recipe=<id>` da "Comanda di produzione"
  // (ProductionTodoPage) → apre direttamente la preparazione di quella ricetta
  // allo step 1. Una sola volta, appena le ricette sono caricate.
  const recipeDeepLinkDone = useRef(false);
  useEffect(() => {
    if (recipeDeepLinkDone.current || typeof window === 'undefined' || recipes.length === 0) return;
    const rid = new URLSearchParams(window.location.search).get('recipe');
    if (!rid) {
      recipeDeepLinkDone.current = true;
      return;
    }
    if (!recipes.some((r) => r.id === rid)) return; // ricette non ancora tutte caricate
    recipeDeepLinkDone.current = true;
    setMode('recipe');
    loadFromRecipe(rid);
    setPrepStep(1);
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipes]);

  // Pre-allocazione ingredienti da ricetta (suggestiva, modificabile dall'utente).
  // Il yield della ricetta (`r.yield_qty`) è la base: se l'utente poi cambia outputQty
  // a un valore diverso, l'useEffect più sotto riproporziona automaticamente le righe.
  function abandonProductionLotPhotoDraft(): void {
    setCoreTemperature(''); setCoreMeasuredAt('');
    void discardStagedProductionLotPhotos(productionDraftId.current);
    productionDraftId.current = crypto.randomUUID();
    manualLotKeys.current.clear();
    setLotSelectionMode('manual');
    setPhotoCaptureOpen(false);
    setPhotoBatch(null);
  }

  function loadFromRecipe(rid: string): void {
    // Una nuova ricetta è una nuova produzione anche se le foto erano state
    // nascoste tornando temporaneamente alla modalità manuale.
    abandonProductionLotPhotoDraft();
    setRecipeId(rid);
    if (!rid) {
      setRecipeYieldQty(0);
      return;
    }
    const r = recipes.find((x) => x.id === rid);
    if (!r) return;
    // T-229 (collettiva): se la ricetta dichiara le porzioni base, lo scaling
    // si fa A PORZIONI: recipeYieldQty = porzioni base, outputQty = porzioni
    // prodotte. Gli ingredienti base_qty restano quelli della ricetta, quindi
    // il factor outputQty/recipeYieldQty = porzioniProdotte/porzioniBase è
    // già corretto per costruzione.
    // T-242: porzioni base = yield_qty quando l'unità è "porzioni" (campo unico
    // in RecipesPage), altrimenti yield_portions esplicito.
    const basePortions = recipeBasePortions(r);
    const usePortions = collettivaEnabled && basePortions > 0;
    const yieldQty = usePortions ? basePortions : (Number(r.yield_qty) || 1);
    setRecipeYieldQty(yieldQty);
    // Conserva il nome completo: il renderer adatta il testo alla casella.
    setPreparationName(r.name);
    setOutputQty(String(yieldQty));
    setOutputUom(usePortions ? 'porz' : (r.yield_uom ?? 'porz'));
    setShelfDays(String(shelfLifeDaysFromRecipe(r)));
    // Suggerisci righe ingredienti: per ogni ingrediente della ricetta, NON allochiamo
    // ancora un lotto (l'utente sceglie). Mostriamo solo righe placeholder per food.
    const suggested: IngredientLine[] = [];
    for (const ing of r.ingredients ?? []) {
      const food = foods.find((f) => f.id === ing.food_id);
      if (!food) continue;
      const baseQty = Number(ing.qty) || 0;
      const cands = recipeLotsByFood.get(ing.food_id) ?? [];
      // T-240: pre-seleziona il primo lotto disponibile (FEFO, in scadenza
      // prima) come DEFAULT — sia a magazzino ON sia OFF. In produzione lo chef
      // deve poter VEDERE e CAMBIARE il lotto (double-check tracciabilità) e
      // correggere la quantità prima di stampare/scaricare; lo scarico avviene
      // sul lotto effettivamente scelto (non più FEFO silenzioso).
      const lot = cands[0];
      if (!lot) {
        // Nessun lotto: aggiungo una riga "non allocata" che blocca la conferma
        suggested.push({
          uid: crypto.randomUUID(),
          food_id: food.id,
          food_name: food.name,
          lot_key: '',
          batch_lot: null,
          supplier_id: null,
          supplier_name: null,
          document_id: null,
          uom: food.default_uom ?? ing.uom,
          qty_used: String(baseQty),
          available: 0,
          allergens: food.allergens ?? [],
          base_qty: baseQty,
          source_production_id: null,
        });
      } else {
        suggested.push({
          uid: crypto.randomUUID(),
          food_id: food.id,
          food_name: food.name,
          lot_key: lot.key,
          batch_lot: lot.batch_lot,
          supplier_id: lot.supplier_id,
          supplier_name: lot.supplier_name,
          document_id: lot.document_id,
          uom: food.default_uom ?? lot.uom,
          qty_used: String(baseQty),
          available: lot.available,
          allergens: lot.allergens,
          base_qty: baseQty,
          source_production_id: lot.source_production_id ?? null,
        });
      }
    }
    // T-259: espandi i semilavorati (ricette base) come righe-ingrediente. Un
    // semilavorato prodotto è un food "prodotto finito" (foods.recipe_id) con un
    // lotto stock_in (lp_code) che porta `source_production_id` → consumandolo,
    // l'input eredita quell'id e la trace-page risale alla produzione del
    // semilavorato. La qty viene da parent_recipes (scala con outputQty come gli
    // ingredienti); le ricette legacy hanno solo parent_recipe_ids (qty ignota).
    const subList: Array<{ recipe_id: string; qty: number }> =
      (r.parent_recipes && r.parent_recipes.length > 0)
        ? r.parent_recipes.map((p) => ({ recipe_id: p.recipe_id, qty: Number(p.qty) || 0 }))
        : (r.parent_recipe_ids ?? []).map((rid) => ({ recipe_id: rid, qty: 0 }));
    for (const sub of subList) {
      const subRecipe = recipes.find((x) => x.id === sub.recipe_id);
      if (!subRecipe) continue;
      const finishedFood = foods.find((f) => !f.archived_at && f.recipe_id === sub.recipe_id);
      const cands = finishedFood ? (recipeLotsByFood.get(finishedFood.id) ?? []) : [];
      const lot = cands[0]; // FEFO: il lotto del semilavorato in scadenza prima
      const baseQty = sub.qty;
      if (finishedFood && lot) {
        suggested.push({
          uid: crypto.randomUUID(),
          food_id: finishedFood.id,
          food_name: subRecipe.name,
          lot_key: lot.key,
          batch_lot: lot.batch_lot,
          supplier_id: lot.supplier_id,
          supplier_name: lot.supplier_name,
          document_id: lot.document_id,
          uom: lot.uom || finishedFood.default_uom || subRecipe.yield_uom || 'porz',
          qty_used: String(baseQty),
          available: lot.available,
          allergens: (lot.allergens && lot.allergens.length > 0) ? lot.allergens : (subRecipe.allergens ?? []),
          // qty nota → scala con la resa; qty ignota (legacy) → niente scaling.
          ...(baseQty > 0 ? { base_qty: baseQty } : {}),
          source_production_id: lot.source_production_id ?? null,
        });
      } else {
        // Semilavorato non ancora prodotto (nessun lotto): riga non allocata che
        // blocca la conferma e segnala allo chef di produrlo prima.
        suggested.push({
          uid: crypto.randomUUID(),
          food_id: finishedFood?.id ?? '',
          food_name: subRecipe.name,
          lot_key: '',
          batch_lot: null,
          supplier_id: null,
          supplier_name: null,
          document_id: null,
          uom: finishedFood?.default_uom || subRecipe.yield_uom || 'porz',
          qty_used: String(baseQty),
          available: 0,
          allergens: subRecipe.allergens ?? [],
          ...(baseQty > 0 ? { base_qty: baseQty } : {}),
          source_production_id: null,
        });
      }
    }
    setLines(suggested);
  }

  // Riproporziona tutte le righe da ricetta quando cambia outputQty.
  // Esempio: ricetta produce 1 kg con 100 g basilico → ricetta caricata mostra 100.
  // L'utente cambia outputQty a 5 → tutte le righe con base_qty si moltiplicano *5.
  // Le righe aggiunte a mano (senza base_qty) restano invariate.
  useEffect(() => {
    if (!recipeId || recipeYieldQty <= 0) return;
    const targetQty = Number(outputQty.replace(',', '.'));
    if (!Number.isFinite(targetQty) || targetQty <= 0) return;
    const factor = targetQty / recipeYieldQty;
    setLines((prev) => prev.map((ln) => {
      if (ln.base_qty == null) return ln;
      const scaled = ln.base_qty * factor;
      // Arrotonda a 3 decimali per ridurre rumore visivo.
      const rounded = Math.round(scaled * 1000) / 1000;
      const next = String(rounded);
      return ln.qty_used === next ? ln : { ...ln, qty_used: next };
    }));
  }, [outputQty, recipeYieldQty, recipeId]);

  // T-229 (collettiva, flusso a porzioni): 1 etichetta per porzione — le copie
  // seguono le porzioni prodotte ogni volta che outputQty cambia (l'utente può
  // comunque ritoccarle a step 3, ma un nuovo cambio di porzioni le riallinea).
  useEffect(() => {
    if (!collettivaEnabled || !portionFlow) return;
    const n = Math.round(Number(outputQty.replace(',', '.')));
    if (!Number.isFinite(n) || n <= 0) return;
    const nextCopies = Math.max(1, Math.min(500, n));
    setCopies(nextCopies);
    setCopiesInput(String(nextCopies));
  }, [collettivaEnabled, portionFlow, outputQty]);

  function addLineFromPicker(): void {
    if (singleProductLabel && lines.length > 0) { setToast('Per un prodotto singolo seleziona un solo alimento'); return; }
    if (!pickFoodId) { setToast('Seleziona alimento'); return; }
    const qty = Number(pickQty.replace(',', '.'));
    if (!Number.isFinite(qty) || qty <= 0) { setToast('Quantità non valida'); return; }
    if (lotSelectionMode === 'photo') {
      const food = foods.find((candidate) => candidate.id === pickFoodId);
      if (!food) { setToast('Alimento non trovato'); return; }
      const uid = crypto.randomUUID();
      const suggestedLot = (recipeLotsByFood.get(food.id) ?? [])[0];
      setLines((prev) => [
        ...prev,
        {
          uid,
          food_id: food.id,
          food_name: food.name,
          lot_key: suggestedLot?.key ?? '',
          batch_lot: suggestedLot?.batch_lot ?? null,
          supplier_id: suggestedLot?.supplier_id ?? null,
          supplier_name: suggestedLot?.supplier_name ?? null,
          document_id: suggestedLot?.document_id ?? null,
          uom: food.default_uom?.trim() || suggestedLot?.uom || '',
          qty_used: String(qty),
          available: suggestedLot?.available ?? 0,
          allergens: suggestedLot?.allergens ?? food.allergens ?? [],
          source_production_id: suggestedLot?.source_production_id ?? null,
        },
      ]);
      setPickFoodId('');
      setPickLotKey('');
      setPickQty('');
      setPickerOpen(false);
      return;
    }
    // T-240: anche a magazzino ON si sceglie il LOTTO esplicito (double-check
    // tracciabilità in produzione). Il blocco giacenza resta per-alimento (×1.1)
    // in stockGateError; qui il vincolo è sul lotto scelto.
    if (!pickLotKey) { setToast('Seleziona lotto'); return; }
    const lot = lots.find((l) => l.key === pickLotKey);
    if (!lot) return;
    const selectedFood = foods.find((food) => food.id === pickFoodId);
    if (!selectedFood) { setToast('Alimento non trovato'); return; }
    if (qty > lot.available) { setToast(`Disponibili solo ${lot.available.toFixed(2)} ${lot.uom}`); return; }
    setLines((prev) => [
      ...prev,
      {
        uid: crypto.randomUUID(),
        food_id: selectedFood.id,
        food_name: selectedFood.name,
        lot_key: lot.key,
        batch_lot: lot.batch_lot,
        supplier_id: lot.supplier_id,
        supplier_name: lot.supplier_name,
        document_id: lot.document_id,
        uom: lot.uom,
        qty_used: String(qty),
        available: lot.available,
        allergens: lot.allergens,
        // T-259: se è un lotto prodotto-finito (semilavorato), propaga la trace.
        source_production_id: lot.source_production_id ?? null,
      },
    ]);
    setPickFoodId('');
    setPickLotKey('');
    setPickQty('');
    // Chiudi automaticamente il picker dopo aver aggiunto: l'utente vede
    // l'ingrediente apparire sopra e il pulsante CTA tornare disponibile
    // per aggiungerne un altro.
    setPickerOpen(false);
  }

  // Registra il PRIMO lotto di un alimento direttamente dal picker, senza
  // passare dal Magazzino. Piano base = presenza (qty=0, manual_entry), mirror
  // di WarehousePage.commitManualLot. Dopo l'enqueue la query `stock_in` viene
  // invalidata → `lots` ricalcola → il lotto compare nel select qui sopra.
  // Crea il PRIMO lotto di un alimento (piano base = presenza, qty=0),
  // mirror di WarehousePage.commitManualLot. Ritorna la chiave del lotto
  // (`food_id|batch`) o null in caso di errore. Usato sia dal picker sia
  // dalle singole righe ingrediente che non hanno lotti.
  async function createFirstLot(foodId: string, batch: string, expiry: string, qty: number | null = null): Promise<string | null> {
    const food = foods.find((f) => f.id === foodId);
    if (!food) { setToast('Alimento non trovato'); return null; }
    if (!batch) { setToast('Inserisci il codice lotto'); return null; }
    if (qty != null && (!Number.isFinite(qty) || qty < 0)) {
      setToast('Quantità lotto non valida'); return null;
    }
    if (!profile?.active_tenant_id || !profile.active_business_id) {
      setToast('Nessun locale attivo'); return null;
    }
    setNewLotBusy(true);
    try {
      const uom = food.default_uom?.trim() || 'kg';
      const positiveQty = qty != null && Number.isFinite(qty) && qty > 0 ? qty : null;
      const command: StockCommandV1 = StockCommandV1Schema.parse(
        positiveQty == null
          ? {
              version: 1,
              command_id: crypto.randomUUID(),
              tenant_id: profile.active_tenant_id,
              movement_class: 'lot_state',
              economic_effect: 'stock_only',
              business_id: profile.active_business_id,
              reason: 'Registrazione primo lotto da etichette',
              lines: [{
                food_id: food.id,
                batch_lot: batch,
                expiration_date: expiry || null,
                supplier_id: null,
                document_id: null,
                storage_unit_id: null,
                line_ref: `labels:first-lot:${food.id}:${batch}`,
                action: 'register',
                uom,
                manual_entry: true,
                notify_expiration: true,
              }],
            }
          : {
              version: 1,
              command_id: crypto.randomUUID(),
              tenant_id: profile.active_tenant_id,
              movement_class: 'receive',
              economic_effect: 'stock_only',
              business_id: profile.active_business_id,
              reason: 'Carico primo lotto da etichette',
              lines: [{
                food_id: food.id,
                batch_lot: batch,
                expiration_date: expiry || null,
                supplier_id: null,
                document_id: null,
                storage_unit_id: null,
                line_ref: `labels:first-lot:${food.id}:${batch}`,
                lot_action: 'updated_active',
                operator_name: profile.full_name?.trim() || null,
                manual_entry: true,
                notify_expiration: true,
                source_qty: positiveQty,
                source_uom: uom,
                source_unit_price: null,
                source_line_total: null,
                currency: stockCurrency(food.last_unit_cost_currency),
                stock_uom: uom,
                conversion: { factor: 1, source: 'identity' },
              }],
            },
      );
      await enqueueStockCommand.mutateAsync(command);
      setToast('Lotto registrato');
      return `${food.id}|${batch}`;
    } catch (err) {
      setToast(toUserMessage(err));
      return null;
    } finally {
      setNewLotBusy(false);
    }
  }

  async function handlePickerFirstLot(batch: string, expiry: string, qty: number | null): Promise<void> {
    const key = await createFirstLot(pickFoodId, batch, expiry, qty);
    // Pre-seleziona il lotto appena creato così l'utente prosegue con la qty.
    if (key) setPickLotKey(key);
  }

  // Primo lotto creato da una riga ingrediente esistente (es. ricetta vecchia
  // con un alimento mai caricato): assegna subito il lotto a quella riga.
  async function handleLineFirstLot(uid: string, foodId: string, batch: string, expiry: string, qty: number | null): Promise<void> {
    const key = await createFirstLot(foodId, batch, expiry, qty);
    if (!key) return;
    const food = foods.find((f) => f.id === foodId);
    setLines((prev) => prev.map((l) => (
      l.uid === uid
        ? {
            ...l,
            // T-240/T-481: lo scarico è sul lotto scelto esplicitamente, anche
            // con magazzino attivo. Azzerare qui il locator appena creato
            // bloccava la validazione e, se aggirata, preparava uno stock_out
            // sul lotto NULL anziché sul nuovo lotto.
            lot_key: key,
            batch_lot: batch,
            available: warehouseEnabled ? (qty ?? 0) : Number.POSITIVE_INFINITY,
            uom: food?.default_uom ?? l.uom,
            allergens: food?.allergens ?? l.allergens,
          }
        : l
    )));
  }

  function handlePickFoodCreated(food: FoodEditorValue): void {
    // Auto-seleziona il nuovo alimento nel picker. Non avendo lotti, comparirà
    // il form "primo lotto" qui sotto.
    setPickFoodId(food.id);
    setPickLotKey('');
    setPickQty('');
    celebrate(`"${food.name}" creato`);
  }

  // Salva il layout corrente come PREDEFINITO dell'azienda nel DB
  // (label_templates). Così tutti gli account del tenant — incluso il
  // ristoratore che non apre mai l'editor — usano questo layout
  // (pickTemplate: localStorage → DB → default hardcoded). Solo consulente.
  async function saveLayoutAsDefault(): Promise<void> {
    const targetTenantId = labelTenantId ?? profile?.active_tenant_id;
    if (!targetTenantId) { setToast('Nessun tenant selezionato'); return; }

    // Se salviamo per il tenant attivo usiamo la coda offline (enqueueMaster),
    // altrimenti scriviamo direttamente su Supabase (il consulente ha accesso RLS).
    if (targetTenantId === profile?.active_tenant_id) {
      const existing = realTemplates.find((tpl) => {
        const r = tpl as { archived_at?: string | null; preset?: string };
        return !r.archived_at && (r.preset === 'tracciabilita_lotto' || r.preset === 'lavorato');
      });
      const id = existing?.id ?? crypto.randomUUID();
      try {
        await enqueueMaster.mutateAsync({
          table: 'label_templates',
          id,
          op: existing ? 'update' : 'insert',
          base_version: (existing as { version?: number } | undefined)?.version ?? 0,
          patch: {
            id,
            tenant_id: targetTenantId,
            business_id: profile?.active_business_id ?? null,
            name: template.name || 'Etichetta predefinita',
            preset: 'tracciabilita_lotto',
            width_mm: template.width_mm,
            height_mm: template.height_mm,
            elements: template.elements,
            required_allergens: template.required_allergens ?? false,
          },
        });
        setToast('Layout salvato come predefinito per l\'azienda');
      } catch (err) {
        setToast(toUserMessage(err));
      }
    } else {
      // Tenant diverso dall'attivo: scrittura diretta (consulente su tenant esterno)
      const existing = labelTenantTemplates.find((tpl) => {
        const r = tpl as { archived_at?: string | null; preset?: string };
        return !r.archived_at && (r.preset === 'tracciabilita_lotto' || r.preset === 'lavorato');
      });
      const id = existing?.id ?? crypto.randomUUID();
      try {
        const sb = getSupabase();
        const payload = {
          id,
          tenant_id: targetTenantId,
          business_id: null,
          name: template.name || 'Etichetta predefinita',
          preset: 'tracciabilita_lotto',
          width_mm: template.width_mm,
          height_mm: template.height_mm,
          elements: template.elements,
          required_allergens: template.required_allergens ?? false,
        };
        const { error } = existing
          ? await sb.from('label_templates').update(payload).eq('id', id)
          : await sb.from('label_templates').insert(payload);
        if (error) throw error;
        setToast('Layout salvato come predefinito per il ristorante');
      } catch (err) {
        setToast(toUserMessage(err));
      }
    }
  }

  function removeLine(uid: string): void {
    manualLotKeys.current.delete(uid);
    setLines((prev) => prev.filter((l) => l.uid !== uid));
  }

  function changeLineLot(uid: string, lotKey: string): void {
    const lot = lots.find((candidate) => candidate.key === lotKey);
    if (!lot) return;
    setLines((prev) =>
      prev.map((l) =>
        l.uid === uid
          ? {
              ...l,
              lot_key: lot.key,
              batch_lot: lot.batch_lot,
              supplier_id: lot.supplier_id,
              supplier_name: lot.supplier_name,
              document_id: lot.document_id,
              source_production_id: lot.source_production_id ?? null,
              uom: lot.uom,
              available: lot.available,
              allergens: lot.allergens,
            }
          : l,
      ),
    );
  }

  function withoutLotLink(line: IngredientLine): IngredientLine {
    return {
      ...line,
      lot_key: '',
      batch_lot: null,
      supplier_id: null,
      supplier_name: null,
      document_id: null,
      available: 0,
      source_production_id: null,
    };
  }

  function switchLotSelectionMode(next: 'manual' | 'photo'): void {
    if (next === lotSelectionMode) return;
    if (next === 'photo') {
      const configurationError = photoIngredientConfigurationError();
      if (configurationError) {
        setToast(configurationError);
        return;
      }
      // Il lotto FEFO gia proposto resta soltanto una gamba operativa
      // provvisoria: non viene mostrato ne confermato dal cuoco. Il job AI lo
      // confrontera con le foto e produrra un alert se osserva un lotto
      // diverso. In questo modo il wizard non perde tempo e non riscriviamo
      // retroattivamente uno stock_out gia registrato.
      setLines((current) => current.map((line) => {
        if (line.lot_key) manualLotKeys.current.set(line.uid, line.lot_key);
        return line;
      }));
      setLotSelectionMode('photo');
      // Un solo gesto porta l'operatore nella sessione multi-scatto. Il CTA
      // resta visibile come riapertura se la fotocamera viene chiusa senza foto.
      setPhotoCaptureOpen(true);
      return;
    }
    setLines((current) => current.map((line) => {
      const rememberedKey = manualLotKeys.current.get(line.uid);
      const candidates = recipeLotsByFood.get(line.food_id) ?? [];
      const lot = candidates.find((candidate) => candidate.key === rememberedKey)
        ?? candidates.find((candidate) => candidate.key === line.lot_key)
        ?? candidates[0];
      return lot ? {
        ...line,
        lot_key: lot.key,
        batch_lot: lot.batch_lot,
        supplier_id: lot.supplier_id,
        supplier_name: lot.supplier_name,
        document_id: lot.document_id,
        uom: lot.uom || line.uom,
        available: lot.available,
        allergens: lot.allergens,
        source_production_id: lot.source_production_id ?? null,
      } : withoutLotLink(line);
    }));
    setPhotoCaptureOpen(false);
    setLotSelectionMode('manual');
  }

  async function saveProductionLotPhotoBatch(photos: CapturedBatchPhoto[]): Promise<void> {
    const tenantId = profile?.active_tenant_id;
    const businessId = profile?.active_business_id;
    if (!tenantId || !businessId) throw new Error('Contesto produzione non disponibile');

    const previous = photoBatch;
    const manifest = await stageProductionLotPhotoBatch({
      tenantId,
      businessId,
      productionId: productionDraftId.current,
      photos: photos.map(({ photo, capturedAt }) => ({ photo, capturedAt })),
    });
    setPhotoBatch({
      manifest,
      preview_data_urls: photos.map(({ previewDataUrl }) => previewDataUrl),
    });
    setLotSelectionMode('photo');
    // L'etichetta veloce può iniziare dalle foto, prima della scelta dell'alimento.
    setPrepStep(lines.length > 0 ? 3 : 2);

    // Il batch nuovo è già integro e referenziato nello stato. Le vecchie
    // catture, ancora soltanto staged, possono essere eliminate best-effort.
    if (previous) {
      void Promise.allSettled(previous.manifest.captures.map((capture) => (
        discardStagedProductionLotCapture(capture.capture_id)
      )));
    }
  }

  function renderProductionLotPhotoControls(
    line: IngredientLine,
    stockUom: string,
  ): JSX.Element {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        padding: '9px 11px', borderRadius: 11, background: '#eff6ff',
        border: '1px solid #bfdbfe', color: '#1e40af', fontSize: 12,
      }}>
        <span>Lotto associato dopo la stampa</span>
        <b style={{ whiteSpace: 'nowrap' }}>{line.qty_used} {stockUom}</b>
      </div>
    );
  }
  function changeLineQty(uid: string, qty: string): void {
    setLines((prev) => prev.map((l) => (l.uid === uid ? { ...l, qty_used: qty } : l)));
  }

  function resetForm(): void {
    abandonProductionLotPhotoDraft();
    setPrepStep(1);
    setMode('recipe');
    setPreparationName('');
    setRecipeId('');
    setRecipeYieldQty(0);
    setShelfDays('3');
    setProducedDate(todayDateStr()); // T-253: ripristina sempre a oggi
    setOutputQty('');
    setOutputUom('porz');
    setLines([]);
    setPickFoodId('');
    setPickLotKey('');
    setPickQty('');
    setCopies(1);
    setCopiesInput('1');
    setMealService('');
  }

  function closeAll(): void {
    setOpen(false);
    resetForm();
  }

  useBackGuard(open, closeAll);

  // Validazione pre-conferma
  // T-070: con magazzino OFF la gestione delle quantità è completamente
  // disabilitata nella stampa etichetta — le qty restano gestite SOLO in
  // ricetta. Saltiamo i check `q > 0` e `q > available` per non bloccare
  // la stampa. Resta obbligatorio il `lot_key` (tracciabilità HACCP base).
  // T-221/T-481: con magazzino ON le righe sono a livello alimento — il
  // vincolo è sul TOTALE disponibile nel lotto. Il preflight usa la stessa
  // tolleranza esclusivamente numerica del server: non può stampare un QR per
  // un comando che verrà poi rifiutato per giacenza insufficiente.
  function photoIngredientConfigurationError(): string | null {
    for (const line of lines) {
      if (!line.food_id || !foods.some((food) => food.id === line.food_id && !food.archived_at)) {
        return `${line.food_name} non è collegato a un alimento attivo: correggi la ricetta prima di fotografare`;
      }
      const qty = Number(line.qty_used.replace(',', '.'));
      if (!Number.isFinite(qty) || qty <= 0) {
        return `Quantità di ricetta non valida per ${line.food_name}`;
      }
    }
    return null;
  }

  function photoEvidenceError(): string | null {
    if (lotSelectionMode !== 'photo') return null;
    const configurationError = photoIngredientConfigurationError();
    if (configurationError) return configurationError;
    if (!photoBatch || photoBatch.manifest.captures.length < 1) {
      return 'Scatta almeno una foto delle confezioni utilizzate';
    }
    return null;
  }

  function stockGateError(): string | null {
    if (!warehouseEnabled) return null;
    if (lotSelectionMode === 'photo') {
      // L'operatore deve soltanto fotografare: il client controlla la quantita
      // di ricetta ma non blocca mai per un lotto nascosto/insufficiente. Al
      // commit useremo una gamba FEFO provvisoria solo se copre davvero il
      // fabbisogno; altrimenti la produzione resta valida senza stock link e
      // il job asincrono emetterà il warning di riconciliazione.
      for (const line of lines) {
        const qty = Number(line.qty_used.replace(',', '.'));
        if (!Number.isFinite(qty) || qty <= 0) {
          return `Quantità non valida per ${line.food_name}`;
        }
      }
      return null;
    }
    // T-240: ogni riga deve avere il LOTTO scelto + qty valida; il blocco
    // è sul LOTTO effettivamente selezionato. Righe sullo stesso lotto sommano.
    const byLot = new Map<string, {
      qty: number;
      name: string;
      uom: string;
      optimisticAvailable: number;
      projectedAvailable: number | null;
    }>();
    for (const l of lines) {
      const q = Number(l.qty_used.replace(',', '.'));
      if (!Number.isFinite(q) || q <= 0) return `Quantità non valida per ${l.food_name}`;
      if (!l.lot_key) return `Scegli il lotto per ${l.food_name}`;
      const selectedKey = l.lot_key;
      if (!selectedKey) return `Il lotto associato a ${l.food_name} non è più disponibile`;
      // Più ricezioni (DDT/scadenze) possono condividere lo stesso saldo
      // quantitativo food/lotto/unità. Il gate somma su quel saldo fisico,
      // non sull'identità documentale della foto, così due righe non possono
      // spendere entrambe l'intera disponibilità aggregata.
      const selectedLot = lots.find((candidate) => candidate.key === selectedKey);
      const stockBalanceKey = selectedLot
        ? JSON.stringify([selectedLot.key, selectedLot.storage_unit_id])
        : selectedKey;
      const cur = byLot.get(stockBalanceKey) ?? {
        qty: 0,
        name: l.food_name,
        uom: l.uom,
        // Il primo carico è già durable nell'outbox, ma React Query può non
        // aver ancora ricalcolato `lots` nello stesso frame. Conserviamo il
        // saldo ottimistico della riga soltanto come ponte fino al projector.
        optimisticAvailable: Number.isFinite(l.available) ? l.available : 0,
        projectedAvailable: selectedLot?.available ?? null,
      };
      cur.qty += q;
      cur.optimisticAvailable = Math.max(
        cur.optimisticAvailable,
        Number.isFinite(l.available) ? l.available : 0,
      );
      byLot.set(stockBalanceKey, cur);
    }
    for (const [lotKey, req] of byLot) {
      const lot = lots.find((candidate) => candidate.key === lotKey);
      const available = req.projectedAvailable ?? lot?.available ?? req.optimisticAvailable;
      if (!hasSufficientLabelStock(req.qty, available)) {
        return `Lotto insufficiente per ${req.name}: richiesti ${req.qty.toFixed(2)} ${req.uom}, nel lotto ${available.toFixed(2)} ${req.uom} — scegli un altro lotto o aggiungi una riga`;
      }
    }
    return null;
  }

  function validate(): string | null {
    if (!preparationName.trim()) return 'Nome preparazione richiesto';
    if (lines.length === 0) return singleProductLabel ? 'Scegli il prodotto da etichettare' : 'Aggiungi almeno un ingrediente';
    if (singleProductLabel && lines.length !== 1) return 'Per un prodotto singolo seleziona un solo alimento';
    const evidenceError = photoEvidenceError();
    if (evidenceError) return evidenceError;
    if (warehouseEnabled) {
      const gate = stockGateError();
      if (gate) return gate;
    } else if (lotSelectionMode !== 'photo') {
      for (const l of lines) {
        if (!l.lot_key) return `Lotto mancante per ${l.food_name}`;
      }
    }
    try { chillingCheck(); } catch (e) { return e instanceof Error ? e.message : 'Verifica la temperatura al cuore'; }
    if (!printerId) return 'Seleziona la stampante';
    if (parseCopyCountInput(copiesInput, collettivaEnabled ? 500 : 20) === null) {
      return `Inserisci un numero di copie da 1 a ${collettivaEnabled ? 500 : 20}`;
    }
    return null;
  }

  // Validazione per singolo passo del wizard (gate del bottone "Avanti").
  // Ritorna null se il passo è completo, altrimenti il messaggio da mostrare.
  function prepStepError(step: 1 | 2 | 3): string | null {
    if (step === 1) {
      if (mode === 'recipe' && !recipeId) return 'Scegli una ricetta';
      if (mode === 'free' && !preparationName.trim()) return 'Inserisci il nome della preparazione';
      const d = Number(shelfDays.replace(',', '.'));
      if (!Number.isFinite(d) || d <= 0) return 'Inserisci una shelf-life valida (giorni)';
      return null;
    }
    if (step === 2) {
      if (lines.length === 0) return singleProductLabel ? 'Scegli il prodotto da etichettare' : 'Aggiungi almeno un ingrediente';
      if (singleProductLabel && lines.length !== 1) return 'Per un prodotto singolo seleziona un solo alimento';
      const evidenceError = photoEvidenceError();
      if (evidenceError) return evidenceError;
      // T-221: con magazzino ON niente lotto per riga — vale il gate sul
      // totale disponibile per alimento (margine 10%, FEFO alla conferma).
      if (warehouseEnabled) return stockGateError();
      if (lotSelectionMode === 'photo') return null;
      for (const l of lines) {
        if (!l.lot_key) return `Lotto mancante per ${l.food_name}`;
      }
      return null;
    }
    if (!printerId) return 'Seleziona la stampante';
    if (parseCopyCountInput(copiesInput, collettivaEnabled ? 500 : 20) === null) {
      return `Inserisci un numero di copie da 1 a ${collettivaEnabled ? 500 : 20}`;
    }
    return null;
  }

  function goPrepNext(): void {
    const err = prepStepError(prepStep);
    if (err) { setToast(err); return; }
    setPrepStep((s) => (s < 3 ? ((s + 1) as 1 | 2 | 3) : s));
  }

  // Anteprima dati etichetta (per LabelPreview)
  const previewData: Record<string, unknown> = useMemo(() => {
    // T-253: l'anteprima riflette la data di produzione scelta (default oggi).
    const producedAt = composeProducedAt(producedDate);
    return {
      product: preparationName || '(preparazione)',
      prepared_at: producedAt.toISOString(),
      now: producedAt.toISOString(),
      expires_at: expiresAt.toISOString(),
      allergens: aggregatedAllergens,
      ingredients: singleProductLabel ? [] : lines.map((l) => ingName(l.food_id, l.food_name)).filter(Boolean),
      lp_code: 'LP-XXXX-XXXX',
      // T-070: placeholder non-URL così se l'utente scansiona la live preview
      // il suo QR reader mostra testo chiaro, non una pagina di errore.
      trace_url: PREVIEW_TRACE_PLACEHOLDER,
      lot: lotSelectionMode === 'photo' ? '—' : lines[0]?.batch_lot ?? '—',
    };
  }, [
    preparationName,
    expiresAt,
    aggregatedAllergens,
    lines,
    producedDate,
    ingName,
    lotSelectionMode,
    singleProductLabel,
  ]);

  // Dati anteprima per il flusso "Materia prima" (decongelato / aperto).
  // T-095: gestisce sia source='food' (lotto crudo da magazzino) sia
  // source='production' (preparazione del cuoco già abbattuta+congelata).
  // Quando l'utente seleziona una preparazione, l'anteprima eredita nome,
  // ingredienti e allergeni dal parent → identica a ciò che finirà stampato.
  const materialPreviewData: Record<string, unknown> = useMemo(() => {
    const now = new Date();
    const lot = materialLots.find((l) => l.key === materialLotKey);
    const hrs = Number(materialShelfHours.replace(',', '.'));
    const exp = new Date(now.getTime() + (Number.isFinite(hrs) && hrs > 0 ? hrs : 0) * 3_600_000);
    const previewTransformation = materialAction === 'thaw' ? 'thaw' : 'open';
    const previewLotCode = materialAction === 'thaw' ? 'SC-XXXXXXXX' : 'AP-XXXXXXXX';
    if (materialSource === 'production' && materialProduction) {
      const ingredientNames = (materialProduction.inputs ?? [])
        .map((i) => ingName(i.food_id, i.food_name ?? ''))
        .filter((s) => s.length > 0);
      return {
        product: materialDerivedFoodName(materialProduction.output_name, previewTransformation),
        prepared_at: now.toISOString(),
        now: now.toISOString(),
        expires_at: exp.toISOString(),
        allergens: materialProduction.allergens_aggregated ?? [],
        ingredients: ingredientNames.length > 0 ? ingredientNames : [materialProduction.output_name],
        lp_code: previewLotCode,
        trace_url: PREVIEW_TRACE_PLACEHOLDER,
        lot: warehouseEnabled ? previewLotCode : materialProduction.trace_token?.slice(0, 8) ?? '—',
      };
    }
    return {
      product: materialFood ? materialDerivedFoodName(materialFood.name, previewTransformation) : '(materia prima)',
      prepared_at: now.toISOString(),
      now: now.toISOString(),
      expires_at: exp.toISOString(),
      allergens: lot?.allergens ?? materialFood?.allergens ?? [],
      ingredients: materialFood ? [ingName(materialFood.id, materialFood.name)] : [],
      lp_code: previewLotCode,
      // T-070: placeholder non-URL così se l'utente scansiona la live preview
      // il suo QR reader mostra testo chiaro, non una pagina di errore.
      trace_url: PREVIEW_TRACE_PLACEHOLDER,
      lot: warehouseEnabled ? previewLotCode : lot?.batch_lot ?? materialFood?.current_batch_lot ?? '—',
    };
  }, [materialFood, materialLots, materialLotKey, materialShelfHours, materialAction, materialSource, materialProduction, ingName, warehouseEnabled]);

  // Conferma + commit
  async function commitAndPrint(): Promise<void> {
    if (!profile?.active_tenant_id || !profile.active_business_id) {
      setToast('Nessun locale attivo'); return;
    }
    const activeTenantId = profile.active_tenant_id;
    const pendingOperation = durablePrintOperations.current.get('preparation');
    if (pendingOperation) {
      setBusy(true);
      try {
        await executeDurableLabelPrint(pendingOperation);
        durablePrintOperations.current.delete('preparation');
        setToast(
          `Preparazione registrata · ${pendingOperation.labelPayload.lp_code ?? ''} · ${pendingOperation.copies}× stampate`,
        );
        closeAll();
      } catch (error) {
        if (shouldDiscardDurablePrintOperation(error)) {
          durablePrintOperations.current.delete('preparation');
        }
        if (error instanceof DurableLabelPrintError && error.physicalPrintCompleted) {
          closeAll();
        }
        setToast(toUserMessage(error));
      } finally {
        setBusy(false);
      }
      return;
    }
    const err = validate();
    if (err) { setToast(err); return; }
    setBusy(true);
    try {
      const printer = printers.find((p) => p.id === printerId);
      if (!printer) throw new Error('Stampante non trovata');

      // T-253: data di produzione = giorno scelto dall'utente (default oggi) +
      // ora corrente. produced_at è il dato dichiarato; l'evento append-only
      // conserva comunque created_at = ora reale di sistema.
      const producedAt = composeProducedAt(producedDate);
      const daysNum = Number(shelfDays.replace(',', '.'));
      const shelfMin = Number.isFinite(daysNum) && daysNum > 0
        ? Math.round(daysNum * 24 * 60)
        : 3 * 24 * 60;
      const expAt = new Date(producedAt.getTime() + shelfMin * 60_000);

      // trace_token client-side
      const traceToken = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');

      const productionId = productionDraftId.current;
      // T-221: lp_code generato PRIMA dell'insert production così finisce sia
      // sulla riga productions (lotto reale per i DDT collettiva) sia
      // sull'etichetta/evento label_printed.
      const lp = generateLpCode(producedAt);
      // T-240: scarico sul LOTTO ESPLICITO scelto riga per riga (no FEFO
      // silenzioso). Un input + uno stock_out per riga, sul lotto selezionato
      // dallo chef. Con magazzino OFF la qty viene dalla ricetta (base_qty)
      // perché non si inserisce in stampa.
      type LotInput = {
        input_id: string;
        food_id: string;
        food_name: string;
        batch_lot: string | null;
        supplier_id: string | null;
        supplier_name: string | null;
        document_id: string | null;
        storage_unit_id: string | null;
        expiration_date: string | null;
        qty_used: number;
        uom: string;
        stock_uom: string;
        unit_cost: number | null;
        currency: string;
        allergens: string[];
        stock_linked: boolean;
        // T-259: per i semilavorati → production di origine, così la trace-page
        // risale padre → semilavorato → ingredienti/fornitori del semilavorato.
        source_production_id?: string | null;
      };
      const lotInputs: LotInput[] = lines.map((l) => {
        const parsed = Number(l.qty_used.replace(',', '.'));
        const qtyUsed = warehouseEnabled
          ? (Number.isFinite(parsed) ? parsed : 0)
          : (Number.isFinite(parsed) && parsed > 0 ? parsed : (l.base_qty ?? 0));
        let sourceLot: LotRow | undefined;
        if (lotSelectionMode !== 'photo' && l.lot_key) {
          sourceLot = lots.find((lot) => lot.key === l.lot_key)
            ?? lots.find((lot) => (
              lot.food_id === l.food_id
              && lot.batch_lot === l.batch_lot
            ));
        }
        // La foto è l'evidenza primaria: prima dell'AI non esiste un lotto
        // selezionato. Non congeliamo un FEFO provvisorio né il suo DDT, perché
        // un collegamento legacy/fuori scope può altrimenti rifiutare tutta la
        // produzione dopo che l'etichetta è già uscita dalla stampante.
        const stockLinked = lotSelectionMode !== 'photo' && !!l.lot_key;
        const food = foods.find((candidate) => candidate.id === l.food_id);
        const stockUom = sourceLot?.uom?.trim() || food?.default_uom?.trim() || l.uom;
        return {
          input_id: l.uid,
          food_id: sourceLot?.food_id ?? l.food_id,
          food_name: l.food_name,
          batch_lot: sourceLot?.batch_lot ?? (lotSelectionMode === 'photo' ? null : l.batch_lot),
          supplier_id: sourceLot?.supplier_id ?? (lotSelectionMode === 'photo' ? null : l.supplier_id),
          supplier_name: sourceLot?.supplier_name ?? (lotSelectionMode === 'photo' ? null : l.supplier_name),
          document_id: sourceLot?.document_id ?? (lotSelectionMode === 'photo' ? null : l.document_id),
          storage_unit_id: sourceLot?.storage_unit_id ?? null,
          expiration_date: sourceLot?.expires ?? null,
          qty_used: qtyUsed,
          uom: l.uom,
          stock_uom: stockUom,
          unit_cost: sourceLot?.unit_cost ?? null,
          currency: sourceLot?.currency ?? 'EUR',
          allergens: l.allergens,
          stock_linked: stockLinked,
          source_production_id: sourceLot?.source_production_id
            ?? (lotSelectionMode === 'photo' ? null : l.source_production_id ?? null),
        };
      });

      const yieldQty = Number((singleProductLabel ? lines[0]?.qty_used ?? '' : outputQty).replace(',', '.'));
      const producedUom = singleProductLabel ? lines[0]?.uom ?? '' : outputUom;
      const operatorName = profile.full_name?.trim() || null;
      const masterEffects: StockCommandMasterEffectV1[] = [];

      // Il prodotto finito, la lavorazione, i movimenti e l'intent di stampa
      // vengono persistiti nella stessa busta compound.
      const recipe = recipeId ? recipes.find((candidate) => candidate.id === recipeId) : undefined;
      const isRecipeUsedAsIntermediate = recipeId
        ? recipes.some((candidate) => (
            (candidate.parent_recipes ?? []).some((parent) => parent.recipe_id === recipeId)
            || (candidate.parent_recipe_ids ?? []).includes(recipeId)
          ))
        : false;
      const shouldStockFinishedOutput = singleProductLabel || !collettivaEnabled || isRecipeUsedAsIntermediate;
      let finishedFood = recipeId
        ? foods.find((candidate) => !candidate.archived_at && candidate.recipe_id === recipeId)
        : singleProductLabel ? foods.find((candidate) => !candidate.archived_at && candidate.id === lines[0]?.food_id) : undefined;
      let finishedFoodId = finishedFood?.id ?? null;
      if (recipeId && !finishedFoodId) {
        if (!canCreateLabelFood) {
          throw new Error(
            'Il prodotto finito non è ancora presente in magazzino. Chiedi a un responsabile di creare o collegare la scheda alimento prima di stampare la lavorazione.',
          );
        }
        finishedFoodId = crypto.randomUUID();
        const finishedFoodUom = producedUom.trim() || 'porz';
        masterEffects.push(recipeFinishedFoodEffect({
          id: finishedFoodId, tenantId: profile.active_tenant_id,
          name: recipe?.name ?? preparationName.trim(), uom: finishedFoodUom,
          allergens: aggregatedAllergens, recipeId,
        }));
        finishedFood = {
          id: finishedFoodId,
          name: recipe?.name ?? preparationName.trim(),
          default_uom: finishedFoodUom,
          allergens: aggregatedAllergens,
          recipe_id: recipeId,
        };
      }

      // Production master: stesso commit logico del comando stock.
      masterEffects.push({
        table: 'productions',
        id: productionId,
        op: 'insert',
        base_version: 0,
        patch: {
          id: productionId,
          tenant_id: profile.active_tenant_id,
          business_id: profile.active_business_id,
          // T-066: operatore HACCP che ha registrato la lavorazione. Mostrato
          // sulla trace-page pubblica con join staff_profiles.display_name.
          created_by: profile.id,
          produced_at: producedAt.toISOString(),
          output_name: preparationName.trim(),
          output_qty: Number.isFinite(yieldQty) ? yieldQty : null,
          output_uom: producedUom || null,
          recipe_id: recipeId || null,
          inputs: lotInputs.map((input) => ({
            input_id: input.input_id,
            food_id: input.food_id,
            food_name: input.food_name,
            batch_lot: input.batch_lot,
            supplier_id: input.supplier_id,
            supplier_name: input.supplier_name,
            document_id: input.document_id,
            expiration_date: input.expiration_date,
            qty_used: input.qty_used,
            uom: input.uom,
            allergens: input.allergens,
            source_production_id: input.source_production_id ?? null,
          })),
          ...(lotSelectionMode === 'photo' && photoBatch
            ? { lot_photo_batch_v2: photoBatch.manifest }
            : {}),
          allergens_aggregated: aggregatedAllergens,
          shelf_life_minutes: shelfMin,
          expires_at: expAt.toISOString(),
          trace_token: traceToken,
          // T-221: lotto reale dell'etichetta — usato dai DDT collettiva.
          lp_code: lp,
          // T-229 (collettiva): porzioni prodotte, voce di menù e servizio
          // (pranzo/cena) per la scheda di produzione giornaliera.
          portions: collettivaEnabled && (producedUom === 'porz' || producedUom === 'porzioni') && Number.isFinite(yieldQty) && yieldQty > 0
            ? Math.round(yieldQty)
            : null,
          menu_label: collettivaEnabled && recipeId
            ? (recipes.find((r) => r.id === recipeId)?.menu_label ?? null)
            : null,
          meal_service: collettivaEnabled && mealService ? mealService : null,
        },
      });

      // 2) Un solo comando atomico contiene tutte le gambe quantitative della
      // lavorazione. Con warehouse OFF non esistono quantità: registriamo solo
      // la presenza del lotto finito tramite lot_state.
      let command: StockCommandV1;
      let stockCommandEventCount = 0;
      let stockMetadataKind: 'production' | 'lot_state' | 'none' = 'none';
      if (warehouseEnabled) {
        const commandInputs = lotSelectionMode === 'photo'
          // Il prelievo viene determinato dal match asincrono, non dal FEFO
          // locale. La produzione rimane valida anche se quel lotto non era
          // ancora caricato o sta sostituendo quello previsto.
          ? []
          : lotInputs.flatMap((input, index) => (
            input.stock_linked
              ? [productionStockLine({
                foodId: input.food_id,
                batchLot: input.batch_lot,
                expirationDate: input.expiration_date,
                supplierId: input.supplier_id,
                documentId: input.document_id,
                storageUnitId: input.storage_unit_id,
                qty: input.qty_used,
                sourceUom: input.uom,
                stockUom: input.stock_uom,
                unitCost: input.unit_cost,
                currency: input.currency,
                lineRef: `label:${productionId}:input:${index + 1}`,
                operatorName,
                lotAction: 'recorded_only',
              })]
              : []
          ));
        const valuedInputs = commandInputs.filter(
          (input): input is StockSourceLineV1 & { source_line_total: number } =>
            input.source_line_total != null,
        );
        const inputCurrencies = new Set(commandInputs.map((input) => input.currency));
        const allInputsStockLinked = lotSelectionMode === 'photo'
          ? false
          : lotInputs.every((input) => input.stock_linked);
        const outputValue = allInputsStockLinked
          && valuedInputs.length === commandInputs.length
          && commandInputs.length > 0
          && inputCurrencies.size === 1
          ? valuedInputs.reduce((total, input) => total + input.source_line_total, 0)
          : null;
        const outputCurrency = inputCurrencies.size === 1
          ? commandInputs[0]?.currency ?? 'EUR'
          : 'EUR';
        const commandOutputs: StockSourceLineV1[] = [];
        // T-425/T-481: nella ristorazione collettiva il PIATTO finito passa
        // direttamente alla spedizione e non è giacenza; i semilavorati usati
        // da altre ricette restano invece stock reale e devono poter essere
        // consumati dalla ricetta padre.
        if (shouldStockFinishedOutput && finishedFoodId && finishedFood) {
          if (!Number.isFinite(yieldQty) || yieldQty <= 0) {
            throw new Error('Quantità prodotta non valida');
          }
          const sourceUom = producedUom.trim() || finishedFood.default_uom.trim();
          commandOutputs.push(productionStockLine({
            foodId: finishedFoodId,
            batchLot: lp,
            expirationDate: expAt.toISOString().slice(0, 10),
            supplierId: null,
            documentId: null,
            storageUnitId: null,
            qty: yieldQty,
            sourceUom,
            stockUom: finishedFood.default_uom.trim() || sourceUom,
            unitCost: null,
            sourceLineTotal: outputValue,
            currency: outputCurrency,
            lineRef: `label:${productionId}:output:1`,
            operatorName,
            lotAction: 'updated_active',
          }));
        }
        command = StockCommandV1Schema.parse({
          version: 1,
          command_id: crypto.randomUUID(),
          tenant_id: profile.active_tenant_id,
          movement_class: 'production',
          economic_effect: 'internal',
          business_id: profile.active_business_id,
          production_id: productionId,
          reason: `Produzione etichetta ${lp}`,
          inputs: commandInputs,
          outputs: commandOutputs,
        });
        stockCommandEventCount = commandInputs.length + commandOutputs.length;
        stockMetadataKind = 'production';
      } else if (shouldStockFinishedOutput && finishedFoodId && finishedFood) {
        command = StockCommandV1Schema.parse({
          version: 1,
          command_id: crypto.randomUUID(),
          tenant_id: profile.active_tenant_id,
          movement_class: 'lot_state',
          economic_effect: 'stock_only',
          business_id: profile.active_business_id,
          reason: `Registrazione lotto finito etichetta ${lp}`,
          lines: [{
            food_id: finishedFoodId,
            batch_lot: lp,
            expiration_date: expAt.toISOString().slice(0, 10),
            supplier_id: null,
            document_id: null,
            storage_unit_id: null,
            source_production_id: productionId,
            line_ref: `label:${productionId}:output:1`,
            action: 'register',
            uom: finishedFood.default_uom.trim() || producedUom.trim() || 'porz',
            operator_name: operatorName,
            notify_expiration: true,
          }],
        });
        stockCommandEventCount = 1;
        stockMetadataKind = 'lot_state';
      } else {
        command = StockCommandV1Schema.parse({
          version: 1,
          command_id: crypto.randomUUID(),
          tenant_id: profile.active_tenant_id,
          movement_class: 'production',
          economic_effect: 'internal',
          business_id: profile.active_business_id,
          production_id: productionId,
          reason: `Produzione etichetta ${lp} senza movimentazione quantitativa`,
          inputs: [],
          outputs: [],
        });
      }

      const labelData: Record<string, unknown> = {
        product: preparationName.trim(),
        prepared_at: producedAt.toISOString(),
        now: producedAt.toISOString(),
        expires_at: expAt.toISOString(),
        allergens: aggregatedAllergens,
        ingredients: singleProductLabel ? [] : lines.map((l) => ingName(l.food_id, l.food_name)).filter(Boolean),
        lp_code: lp,
        trace_url: buildTraceUrl(traceToken),
        // In modalità foto il lotto FEFO è solo una gamba di scarico
        // provvisoria: non va stampato come se l'AI lo avesse già confermato.
        lot: lotSelectionMode === 'photo'
          ? null
          : lines[0]?.batch_lot ?? lotInputs[0]?.batch_lot ?? null,
      };
      const labelPayload: LabelPrintedPayload = {
        template_id: template.id,
        ...labelPrinterAudit(printer),
        preset: template.preset,
        data: {
          product: preparationName.trim(),
          ...(chillingRecipe ? { chilling_check: chillingCheck() } : {}),
          production_id: productionId,
          trace_token: traceToken,
          ingredients: singleProductLabel ? [] : lines.map((l) => ingName(l.food_id, l.food_name)).filter(Boolean),
          allergens: aggregatedAllergens,
          stock_command_version: 1,
          ...(stockMetadataKind === 'production'
            ? {
                stock_command_id: command.command_id,
                stock_command_event_count: stockCommandEventCount,
              }
            : stockMetadataKind === 'lot_state'
              ? {
                  stock_lot_state_command_id: command.command_id,
                  stock_lot_state_event_count: stockCommandEventCount,
                }
              : { stock_command_not_applicable: true }),
        },
        copies,
        lp_code: lp,
        produced_at: producedAt.toISOString(),
        expires_at: expAt.toISOString(),
      };
      const intentId = crypto.randomUUID();
      masterEffects.push(labelPrintIntentEffect({
        intentId,
        tenantId: profile.active_tenant_id,
        businessId: profile.active_business_id,
        productionId,
        commandId: command.command_id,
        auditClientUuid: crypto.randomUUID(),
        labelPayload,
        printJob: {
          printer,
          template,
          label_data: labelData,
          copies,
        },
        requestedAt: new Date().toISOString(),
      }));
      const operation: DurableLabelPrintOperation = {
        key: 'preparation',
        command,
        masterEffects,
        intentId,
        productionId,
        tenantId: profile.active_tenant_id,
        businessId: profile.active_business_id,
        printer,
        template,
        labelData,
        labelPayload,
        copies,
        afterPersist: lotSelectionMode === 'photo' && photoBatch != null
          ? async () => {
              await commitStagedProductionLotPhotos(productionId);
              void drainProductionLotPhotoOutbox(activeTenantId);
            }
          : undefined,
      };
      durablePrintOperations.current.set(operation.key, operation);
      await executeDurableLabelPrint(operation);
      durablePrintOperations.current.delete(operation.key);

      setToast(`Preparazione registrata · ${lp} · ${copies}× stampate`);
      closeAll();
    } catch (e) {
      if (shouldDiscardDurablePrintOperation(e)) {
        durablePrintOperations.current.delete('preparation');
      }
      if (e instanceof DurableLabelPrintError && e.physicalPrintCompleted) {
        closeAll();
      }
      setToast(toUserMessage(e));
    } finally {
      setBusy(false);
    }
  }

  // Materia prima: scongelamento o apertura confezione.
  // Crea una `productions` con UN solo input (il lotto padre congelato/chiuso),
  // emette uno `stock_out` di pari quantità sul lotto padre, e stampa l'etichetta
  // con scadenza calcolata dalle ore di vita utile dopo l'azione.
  // Se l'utente ha modificato lo SL durante la stampa rispetto al default del
  // food, lo scriviamo anche sul food master ("smart-update").
  async function commitMaterialOperation(): Promise<void> {
    const pendingOperation = durablePrintOperations.current.get('material');
    if (pendingOperation) {
      setBusy(true);
      try {
        await executeDurableLabelPrint(pendingOperation);
        durablePrintOperations.current.delete('material');
        setToast(
          `${pendingOperation.labelPayload.data.product as string} registrato · ${visibleLabelLotCode(pendingOperation.labelPayload) ?? ''} · ${pendingOperation.copies}× stampate`,
        );
        setMaterialOpen(false);
        resetMaterial();
      } catch (error) {
        if (shouldDiscardDurablePrintOperation(error)) {
          durablePrintOperations.current.delete('material');
        }
        if (error instanceof DurableLabelPrintError && error.physicalPrintCompleted) {
          setMaterialOpen(false);
          resetMaterial();
        }
        setToast(toUserMessage(error));
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!materialAction) { setToast('Scegli scongelamento o apertura'); return; }
    // Ramo source='production' (preparazione congelata del cuoco).
    // Con Magazzino attivo T-630 trasferisce soltanto la quantità indicata e
    // lascia il residuo sul lotto congelato. La chain HACCP resta garantita da
    // `inputs[0].source_production_id = parent.id`.
    if (materialSource === 'production') {
      if (!materialProductionId) { setToast('Scegli la preparazione congelata'); return; }
      const parent = materialProduction;
      if (!parent) { setToast('Preparazione non trovata'); return; }
      if (!parent.expires_at || !Number.isFinite(Date.parse(parent.expires_at)) || Date.parse(parent.expires_at) <= Date.now()) {
        setToast('La preparazione è scaduta o non ha una scadenza valida'); return;
      }
      const operationName = materialAction === 'thaw' ? 'Scongelamento' : 'Apertura confezione';
      const operationPreset = materialAction === 'thaw' ? 'decongelato' : 'ingrediente_aperto';
      const hours = Number(materialShelfHours.replace(',', '.'));
      if (!Number.isFinite(hours) || hours <= 0) { setToast('Ore validità non valide'); return; }
      if (!profile?.active_tenant_id || !profile.active_business_id) {
        setToast('Nessun locale attivo'); return;
      }
      const printer = printers.find((p) => p.id === materialPrinterId);
      if (!printer) { setToast('Scegli una stampante'); return; }
      const parentStockCandidates = warehouseEnabled
        ? lots.filter((lot) =>
            lot.source_production_id === parent.id
            && lot.available > 0.0001)
        : [];
      if (warehouseEnabled && parentStockCandidates.length !== 1) {
        setToast(
          parentStockCandidates.length === 0
            ? 'La preparazione non ha una giacenza univoca da trasformare: riconcilia il lotto in Magazzino'
            : 'La preparazione risulta su più lotti: scegli e riconcilia il lotto in Magazzino prima dell’operazione',
        );
        return;
      }
      const parentStockLot = parentStockCandidates[0] ?? null;
      const parentStockBalance = parentStockLot
        ? stockProjection.balances.find((balance) =>
            balance.foodId === parentStockLot.food_id
            && balance.batchLot === parentStockLot.batch_lot)
        : null;
      if (
        warehouseEnabled
        && (
          !parentStockLot
          || !parentStockBalance
          || parentStockBalance.availableQty <= 0.0001
        )
      ) {
        setToast('La preparazione non ha quantità disponibile da scongelare');
        return;
      }

      let transformedQty = parent.output_qty ?? 1;
      let transformedUom = parent.output_uom ?? null;
      if (warehouseEnabled && parentStockBalance) {
        try {
          transformedQty = resolveMaterialOperationQuantity(
            materialQty,
            parentStockBalance.availableQty,
            parentStockBalance.uom,
          ).quantity;
        } catch (error) {
          setToast(toUserMessage(error));
          return;
        }
        transformedUom = parentStockBalance.uom;
      }

      setBusy(true);
      try {
        const producedAt = new Date();
        const expAt = new Date(producedAt.getTime() + hours * 3_600_000);
        const traceToken = Array.from(crypto.getRandomValues(new Uint8Array(16)))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('');
        const productionId = crypto.randomUUID();
        const outputName = `${parent.output_name} (${materialAction === 'thaw' ? 'scongelato' : 'aperto'})`;
        const auditLp = generateLpCode(producedAt);
        const materialLotCode = generateMaterialLotCode(materialAction, producedAt);
        const masterEffects: StockCommandMasterEffectV1[] = [];
        let replacementCommand: StockCommandV1 | null = null;
        let transformedFoodId = parentStockLot?.food_id ?? null;
        if (warehouseEnabled) {
          const sourceFood = foods.find((candidate) => candidate.id === parentStockLot!.food_id);
          if (!sourceFood) {
            throw new Error('Articolo della preparazione non trovato in magazzino');
          }
          const derivedFoodPlan = await planMaterialDerivedFood({
            tenantId: profile.active_tenant_id,
            sourceFoodId: sourceFood.id,
            sourceName: parent.output_name,
            sourceUom: parentStockBalance!.uom,
            transformation: materialAction,
            foods,
          });
          transformedFoodId = derivedFoodPlan.foodId;
          if (derivedFoodPlan.kind === 'create') {
            masterEffects.push(materialDerivedFoodInsertEffect({
              foodId: derivedFoodPlan.foodId,
              tenantId: profile.active_tenant_id,
              name: derivedFoodPlan.name,
              sourceFood,
              action: materialAction,
              shelfHours: hours,
              allergens: parent.allergens_aggregated ?? [],
            }));
          }
        }
        if (warehouseEnabled && transformedFoodId) {
          const oldest = oldestMaterialLotToReplace(transformedFoodId);
          if (oldest && confirmedMaterialReplacementKey.current !== oldest.key) {
            setMaterialReplacementPrompt(oldest);
            return;
          }
          confirmedMaterialReplacementKey.current = null;
          replacementCommand = oldest ? materialReplacementCommand(oldest) : null;
        }

        // 1) productions row figlia, con inputs che PRESERVANO la traccia
        //    del padre. La trace-page walka `source_production_id` fino a
        //    6 livelli → vedrà ricetta → ingredienti → fornitori del padre.
        //    Manteniamo gli ingredienti reali (i food_name del parent.inputs)
        //    e in aggiunta una riga "puntatore" al parent con source_production_id.
        const parentInputs = (parent.inputs ?? []).map((i) => ({
          food_id: i.food_id ?? null,
          food_name: i.food_name ?? '',
          batch_lot: i.batch_lot ?? null,
          supplier_id: i.supplier_id ?? null,
          supplier_name: i.supplier_name ?? null,
          document_id: i.document_id ?? null,
          qty_used: i.qty_used ?? 0,
          uom: i.uom ?? '',
          allergens: i.allergens ?? [],
          // Puntatore alla preparazione padre: la trace-page lo userà per
          // mostrare la catena padre → figlio (decongelato).
          source_production_id: parent.id,
        }));
        // Se il parent non aveva inputs (preparazione libera senza ricetta),
        // aggiungiamo comunque un input "minimo" che punta al parent per non
        // perdere la chain.
        if (parentInputs.length === 0) {
          parentInputs.push({
            food_id: null,
            food_name: parent.output_name,
            batch_lot: parent.trace_token?.slice(0, 8) ?? null,
            supplier_id: null,
            supplier_name: null,
            document_id: null,
            qty_used: parent.output_qty ?? 1,
            uom: parent.output_uom ?? '',
            allergens: parent.allergens_aggregated ?? [],
            source_production_id: parent.id,
          });
        }

        let command: StockCommandV1;
        let materialStockCommandEventCount = 0;
        if (warehouseEnabled && parentStockLot && parentStockBalance) {
          const commandInput = productionStockLine({
            foodId: parentStockLot.food_id,
            batchLot: parentStockLot.batch_lot,
            expirationDate: parentStockLot.expires,
            supplierId: parentStockLot.supplier_id,
            documentId: parentStockLot.document_id,
            storageUnitId: parentStockLot.storage_unit_id,
            qty: transformedQty,
            sourceUom: transformedUom!,
            stockUom: parentStockBalance.uom,
            unitCost: parentStockLot.unit_cost,
            currency: parentStockLot.currency,
            lineRef: `label:${productionId}:input:1`,
            operatorName: profile.full_name?.trim() || null,
            lotAction: 'recorded_only',
          });
          const commandOutput = productionStockLine({
            foodId: transformedFoodId!,
            batchLot: materialLotCode,
            expirationDate: expAt.toISOString().slice(0, 10),
            supplierId: parentStockLot.supplier_id,
            documentId: parentStockLot.document_id,
            storageUnitId: parentStockLot.storage_unit_id,
            qty: transformedQty,
            sourceUom: transformedUom!,
            stockUom: parentStockBalance.uom,
            unitCost: null,
            sourceLineTotal: commandInput.source_line_total,
            currency: parentStockLot.currency,
            lineRef: `label:${productionId}:output:1`,
            operatorName: profile.full_name?.trim() || null,
            lotAction: 'updated_active',
          });
          command = StockCommandV1Schema.parse({
            version: 1,
            command_id: crypto.randomUUID(),
            tenant_id: profile.active_tenant_id,
            movement_class: 'production',
            economic_effect: 'internal',
            business_id: profile.active_business_id,
            production_id: productionId,
            reason: `${operationName} lavorazione ${parent.id}`,
            inputs: [commandInput],
            outputs: [commandOutput],
          });
          materialStockCommandEventCount = 2;
        } else {
          command = StockCommandV1Schema.parse({
            version: 1,
            command_id: crypto.randomUUID(),
            tenant_id: profile.active_tenant_id,
            movement_class: 'production',
            economic_effect: 'internal',
            business_id: profile.active_business_id,
            production_id: productionId,
            reason: `${operationName} lavorazione ${parent.id} senza quantità`,
            inputs: [],
            outputs: [],
          });
        }
        masterEffects.push({
          table: 'productions',
          id: productionId,
          op: 'insert',
          base_version: 0,
          patch: {
            id: productionId,
            tenant_id: profile.active_tenant_id,
            business_id: profile.active_business_id,
            created_by: profile.id,
            produced_at: producedAt.toISOString(),
            lp_code: materialLotCode,
            output_name: outputName,
            output_qty: transformedQty,
            output_uom: transformedUom,
            recipe_id: parent.recipe_id ?? null,
            inputs: parentInputs,
            allergens_aggregated: parent.allergens_aggregated ?? [],
            shelf_life_minutes: Math.round(hours * 60),
            expires_at: expAt.toISOString(),
            trace_token: traceToken,
          },
        });

        const ingredientNames = (parent.inputs ?? [])
          .map((i) => ingName(i.food_id, i.food_name ?? ''))
          .filter((s) => s.length > 0);
        const labelData: Record<string, unknown> = {
          product: outputName,
          prepared_at: producedAt.toISOString(),
          now: producedAt.toISOString(),
          expires_at: expAt.toISOString(),
          allergens: parent.allergens_aggregated ?? [],
          ingredients: ingredientNames.length > 0 ? ingredientNames : [parent.output_name],
          lp_code: materialLotCode,
          trace_url: buildTraceUrl(traceToken),
          lot: warehouseEnabled ? materialLotCode : parent.trace_token?.slice(0, 8) ?? null,
        };
        const labelPayload: LabelPrintedPayload = {
          template_id: template.id,
          ...labelPrinterAudit(printer),
          preset: operationPreset,
          data: {
            product: outputName,
            production_id: productionId,
            trace_token: traceToken,
            ingredients: ingredientNames,
            allergens: parent.allergens_aggregated ?? [],
            parent_production_id: parent.id,
            parent_trace_token: parent.trace_token ?? null,
            material_lot_code: materialLotCode,
            stock_command_version: 1,
            ...(materialStockCommandEventCount > 0
              ? {
                  stock_command_id: command.command_id,
                  stock_command_event_count: materialStockCommandEventCount,
                }
              : { stock_command_not_applicable: true }),
          },
          copies: materialCopies,
          lp_code: auditLp,
          produced_at: producedAt.toISOString(),
          expires_at: expAt.toISOString(),
        };
        const intentId = crypto.randomUUID();
        masterEffects.push(labelPrintIntentEffect({
          intentId,
          tenantId: profile.active_tenant_id,
          businessId: profile.active_business_id,
          productionId,
          commandId: command.command_id,
          auditClientUuid: crypto.randomUUID(),
          labelPayload,
          printJob: {
            printer,
            template: { ...template, preset: operationPreset },
            label_data: labelData,
            copies: materialCopies,
          },
          requestedAt: new Date().toISOString(),
        }));
        const operation: DurableLabelPrintOperation = {
          key: 'material',
          command,
          masterEffects,
          intentId,
          productionId,
          tenantId: profile.active_tenant_id,
          businessId: profile.active_business_id,
          printer,
          template: { ...template, preset: operationPreset },
          labelData,
          labelPayload,
          copies: materialCopies,
          afterPersist: replacementCommand
            ? async () => { await enqueueStockCommand.mutateAsync(replacementCommand); }
            : undefined,
        };
        durablePrintOperations.current.set(operation.key, operation);
        await executeDurableLabelPrint(operation);
        durablePrintOperations.current.delete(operation.key);

        setToast(`${operationName} registrata · ${materialLotCode} · ${materialCopies}× stampate`);
        setMaterialOpen(false);
        resetMaterial();
      } catch (e) {
        if (shouldDiscardDurablePrintOperation(e)) {
          durablePrintOperations.current.delete('material');
        }
        if (e instanceof DurableLabelPrintError && e.physicalPrintCompleted) {
          setMaterialOpen(false);
          resetMaterial();
        }
        setToast(toUserMessage(e));
      } finally {
        setBusy(false);
      }
      return;
    }

    // Ramo standard source='food' (alimento crudo da magazzino).
    if (!materialFoodId || !materialLotKey) { setToast('Scegli alimento e lotto'); return; }
    const lot = materialLots.find((l) => l.key === materialLotKey);
    if (!lot) { setToast('Lotto non trovato'); return; }
    const food = materialFood;
    if (!food) { setToast('Alimento non trovato'); return; }
    // Piano base: nessuna quantità chiesta, nessuno scarico. Usiamo qty=1 come
    // placeholder per la production (serve a popolare output_qty del record).
    let qty: number;
    if (warehouseEnabled) {
      try {
        qty = resolveMaterialOperationQuantity(materialQty, lot.available, lot.uom).quantity;
      } catch (error) {
        setToast(toUserMessage(error));
        return;
      }
    } else {
      qty = 1;
    }
    const hours = Number(materialShelfHours.replace(',', '.'));
    if (!Number.isFinite(hours) || hours <= 0) { setToast('Ore validità non valide'); return; }
    if (!profile?.active_tenant_id || !profile.active_business_id) {
      setToast('Nessun locale attivo'); return;
    }
    const printer = printers.find((p) => p.id === materialPrinterId);
    if (!printer) { setToast('Scegli una stampante'); return; }

    setBusy(true);
    try {
      const producedAt = new Date();
      const expAt = new Date(producedAt.getTime() + hours * 3_600_000);
      const traceToken = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
      const productionId = crypto.randomUUID();
      const actionLabel = materialAction === 'thaw' ? 'Scongelato' : 'Aperto';
      const auditLp = generateLpCode(producedAt);
      const materialLotCode = generateMaterialLotCode(materialAction, producedAt);
      // Nome figlio chiaro per pagina QR e ricerca futura.
      const outputName = materialDerivedFoodName(food.name, materialAction);
      const masterEffects: StockCommandMasterEffectV1[] = [];
      let replacementCommand: StockCommandV1 | null = null;
      const derivedFoodPlan = warehouseEnabled
        ? await planMaterialDerivedFood({
            tenantId: profile.active_tenant_id,
            sourceFoodId: food.id,
            sourceName: food.name,
            sourceUom: lot.uom,
            transformation: materialAction,
            foods,
          })
        : null;
      const transformedFoodId = derivedFoodPlan?.foodId ?? food.id;
      if (derivedFoodPlan?.kind === 'create') {
        masterEffects.push(materialDerivedFoodInsertEffect({
          foodId: derivedFoodPlan.foodId,
          tenantId: profile.active_tenant_id,
          name: derivedFoodPlan.name,
          sourceFood: food,
          action: materialAction,
          shelfHours: hours,
          allergens: lot.allergens ?? food.allergens ?? [],
        }));
      }
      if (warehouseEnabled) {
        const oldest = oldestMaterialLotToReplace(transformedFoodId);
        if (oldest && confirmedMaterialReplacementKey.current !== oldest.key) {
          setMaterialReplacementPrompt(oldest);
          return;
        }
        confirmedMaterialReplacementKey.current = null;
        replacementCommand = oldest ? materialReplacementCommand(oldest) : null;
      }

      // 1) Smart-update SL. Alla prima trasformazione il valore viene scritto
      // direttamente sul nuovo articolo; dalle stampe successive aggiorniamo
      // quello stesso articolo, senza cambiare la materia prima originale.
      const linkedRecipe = food.recipe_id
        ? recipes.find((recipe) => recipe.id === food.recipe_id) ?? null
        : null;
      const recipeDefaultHours = recipeOperationShelfLifeHours(linkedRecipe, materialAction);
      const existingDerivedFood = derivedFoodPlan?.kind === 'existing'
        ? derivedFoodPlan.food
        : null;
      const expectedDefaultHours = recipeDefaultHours
        ?? foodOperationShelfLifeHours(existingDerivedFood ?? food, materialAction);
      if (
        existingDerivedFood
        && recipeDefaultHours == null
        && (expectedDefaultHours == null || Math.round(hours) !== Math.round(expectedDefaultHours))
      ) {
        const patch: Record<string, unknown> = { id: existingDerivedFood.id };
        if (materialAction === 'thaw') {
          patch.shelf_life_hours_thawed = Math.round(hours);
        } else {
          // Per apertura usiamo i giorni (campo esistente). Se non rotondo, arrotondo per eccesso.
          patch.shelf_life_days_opened = Math.max(1, Math.round(hours / 24));
        }
        masterEffects.push({
          table: 'foods',
          id: existingDerivedFood.id,
          op: 'update',
          base_version: existingDerivedFood.version ?? 0,
          patch,
        });
      }

      // 2) productions row con singolo input dal lotto padre.
      masterEffects.push({
        table: 'productions',
        id: productionId,
        op: 'insert',
        base_version: 0,
        patch: {
          id: productionId,
          tenant_id: profile.active_tenant_id,
          business_id: profile.active_business_id,
          // T-066: operatore HACCP (decongelamento/apertura materia prima).
          created_by: profile.id,
          produced_at: producedAt.toISOString(),
          lp_code: materialLotCode,
          output_name: outputName,
          output_qty: qty,
          output_uom: lot.uom,
          recipe_id: null,
          inputs: [{
            food_id: food.id,
            food_name: food.name,
            batch_lot: lot.batch_lot,
            supplier_id: lot.supplier_id,
            supplier_name: lot.supplier_name,
            document_id: lot.document_id,
            qty_used: qty,
            uom: lot.uom,
            allergens: lot.allergens ?? food.allergens ?? [],
          }],
          allergens_aggregated: lot.allergens ?? food.allergens ?? [],
          shelf_life_minutes: Math.round(hours * 60),
          expires_at: expAt.toISOString(),
          trace_token: traceToken,
        },
      });

      // 3) Apertura/scongelamento trasforma il lotto: scarica la quantità dal
      // lotto origine e la ricarica sul nuovo AP/SC con la nuova scadenza. Le due
      // gambe hanno stesso alimento, UdM e valore totale, quindi quantità e
      // costo non spariscono e il comando resta reversibile. Sul piano base non
      // esiste invece una quantità stock da muovere.
      let command: StockCommandV1;
      let materialStockCommandEventCount = 0;
      if (warehouseEnabled) {
        const commandInput = productionStockLine({
          foodId: food.id,
          batchLot: lot.batch_lot,
          expirationDate: lot.expires,
          supplierId: lot.supplier_id,
          documentId: lot.document_id,
          storageUnitId: lot.storage_unit_id,
          qty,
          sourceUom: lot.uom,
          stockUom: lot.uom,
          unitCost: lot.unit_cost,
          currency: lot.currency,
          lineRef: `label:${productionId}:input:1`,
          operatorName: profile.full_name?.trim() || null,
          lotAction: 'recorded_only',
        });
        const commandOutput = productionStockLine({
          foodId: transformedFoodId,
          batchLot: materialLotCode,
          expirationDate: expAt.toISOString().slice(0, 10),
          supplierId: lot.supplier_id,
          documentId: lot.document_id,
          storageUnitId: lot.storage_unit_id,
          qty,
          sourceUom: lot.uom,
          stockUom: lot.uom,
          unitCost: null,
          sourceLineTotal: commandInput.source_line_total,
          currency: lot.currency,
          lineRef: `label:${productionId}:output:1`,
          operatorName: profile.full_name?.trim() || null,
          lotAction: 'updated_active',
        });
        command = StockCommandV1Schema.parse({
          version: 1,
          command_id: crypto.randomUUID(),
          tenant_id: profile.active_tenant_id,
          movement_class: 'production',
          economic_effect: 'internal',
          business_id: profile.active_business_id,
          production_id: productionId,
          reason: `${actionLabel} materia prima ${food.name}`,
          inputs: [commandInput],
          outputs: [commandOutput],
        });
        materialStockCommandEventCount = 2;
      } else {
        command = StockCommandV1Schema.parse({
          version: 1,
          command_id: crypto.randomUUID(),
          tenant_id: profile.active_tenant_id,
          movement_class: 'production',
          economic_effect: 'internal',
          business_id: profile.active_business_id,
          production_id: productionId,
          reason: `${actionLabel} materia prima ${food.name} senza quantità`,
          inputs: [],
          outputs: [],
        });
      }

      const labelData: Record<string, unknown> = {
        product: outputName,
        prepared_at: producedAt.toISOString(),
        now: producedAt.toISOString(),
        expires_at: expAt.toISOString(),
        allergens: lot.allergens ?? food.allergens ?? [],
        ingredients: [ingName(food.id, food.name)],
        lp_code: materialLotCode,
        trace_url: buildTraceUrl(traceToken),
        lot: warehouseEnabled ? materialLotCode : lot.batch_lot ?? null,
      };
      const labelPreset = materialAction === 'thaw' ? 'decongelato' : 'ingrediente_aperto';
      const labelPayload: LabelPrintedPayload = {
        template_id: template.id,
        ...labelPrinterAudit(printer),
        preset: labelPreset,
        data: {
          product: outputName,
          production_id: productionId,
          trace_token: traceToken,
          ingredients: [ingName(food.id, food.name)],
          allergens: lot.allergens ?? food.allergens ?? [],
          material_lot_code: materialLotCode,
          stock_command_version: 1,
          ...(materialStockCommandEventCount > 0
            ? {
                stock_command_id: command.command_id,
                stock_command_event_count: materialStockCommandEventCount,
              }
            : { stock_command_not_applicable: true }),
        },
        copies: materialCopies,
        lp_code: auditLp,
        produced_at: producedAt.toISOString(),
        expires_at: expAt.toISOString(),
      };
      const intentId = crypto.randomUUID();
      masterEffects.push(labelPrintIntentEffect({
        intentId,
        tenantId: profile.active_tenant_id,
        businessId: profile.active_business_id,
        productionId,
        commandId: command.command_id,
        auditClientUuid: crypto.randomUUID(),
        labelPayload,
        printJob: {
          printer,
          template: { ...template, preset: labelPreset },
          label_data: labelData,
          copies: materialCopies,
        },
        requestedAt: new Date().toISOString(),
      }));
      const operation: DurableLabelPrintOperation = {
        key: 'material',
        command,
        masterEffects,
        intentId,
        productionId,
        tenantId: profile.active_tenant_id,
        businessId: profile.active_business_id,
        printer,
        template: { ...template, preset: labelPreset },
        labelData,
        labelPayload,
        copies: materialCopies,
        afterPersist: replacementCommand
          ? async () => { await enqueueStockCommand.mutateAsync(replacementCommand); }
          : undefined,
      };
      durablePrintOperations.current.set(operation.key, operation);
      await executeDurableLabelPrint(operation);
      durablePrintOperations.current.delete(operation.key);

      setToast(`${actionLabel} registrato · ${materialLotCode} · ${materialCopies}× stampate`);
      setMaterialOpen(false);
      resetMaterial();
    } catch (e) {
      if (shouldDiscardDurablePrintOperation(e)) {
        durablePrintOperations.current.delete('material');
      }
      if (e instanceof DurableLabelPrintError && e.physicalPrintCompleted) {
        setMaterialOpen(false);
        resetMaterial();
      }
      setToast(toUserMessage(e));
    } finally {
      setBusy(false);
    }
  }

  // Ristampa
  // T-101: ristampa IDENTICA — stesso lp_code, stesso trace_token, stessa
  // production. Genera N copie fisiche della stessa etichetta. Caso d'uso:
  // ho 3 vaschette dello stesso lotto, voglio mettere la stessa etichetta su
  // tutte. Logghiamo un event label_printed append-only con `is_reprint=true`
  // per audit (mai modificare l'evento originale, vedi invariant #4 CLAUDE.md).
  const reprintIdentical = useCallback(
    async (evt: LabelEvent, copies: number, printerId: string | null): Promise<void> => {
      if (!stockEventMatchesBusiness(evt, activeBusinessId, allowLegacyUnscopedBusiness)) {
        setToast('L’etichetta appartiene a un’altra sede: operazione bloccata');
        return;
      }
      const printer = printers.find((p) => p.id === printerId)
        ?? printers.find((p) => p.id === evt.payload.printer_id)
        ?? printers[0];
      const tmpl = realTemplates.find((tt) => tt.id === evt.payload.template_id) ?? template;
      if (!printer) { setToast('Nessuna stampante'); return; }
      if (copies < 1) { setToast('Copie non valide'); return; }
      const tenantId = profile?.active_tenant_id;
      const productionId = evt.payload.data?.production_id;
      const production = typeof productionId === 'string'
        ? scopedProductions.find((candidate) => candidate.id === productionId)
        : null;
      const businessId = activeBusinessId;
      if (
        !tenantId
        || typeof productionId !== 'string'
        || !UUID_PATTERN.test(productionId)
        || !production
        || !businessId
      ) {
        setToast('Ristampa senza lavorazione o sede valida');
        return;
      }
      const operationKey = `reprint:${evt.id}:${String(evt.payload.data.lot_correction_event_id ?? evt.payload.expires_at ?? "original")}:${printer.id}:${copies}`;
      const visibleLotCode = visibleLabelLotCode(evt.payload);
      const pendingOperation = durablePrintOperations.current.get(operationKey);
      if (pendingOperation) {
        setBusy(true);
        try {
          await executeDurableLabelPrint(pendingOperation);
          durablePrintOperations.current.delete(operationKey);
          setToast(`Ristampate · ${copies}× ${visibleLotCode ?? ''}`);
        } catch (error) {
          if (shouldDiscardDurablePrintOperation(error)) {
            durablePrintOperations.current.delete(operationKey);
          }
          setToast(toUserMessage(error));
        } finally {
          setBusy(false);
        }
        return;
      }
      const traceToken = evt.payload.data?.trace_token as string | undefined;
      const data: Record<string, unknown> = {
        ...(evt.payload.data ?? {}),
        // Stesso lotto e QR; scadenza dalla vista rettificata, originale nell'audit.
        ...(typeof evt.payload.data.corrected_quantity === 'number' ? {
          qty: evt.payload.data.corrected_quantity,
          quantity: evt.payload.data.corrected_quantity,
          uom: evt.payload.data.corrected_uom,
        } : {}),
        prepared_at: evt.payload.produced_at,
        now: new Date().toISOString(),
        expires_at: evt.payload.expires_at,
        lp_code: visibleLotCode,
        trace_url: traceToken ? buildTraceUrl(traceToken) : '',
      };
      const command: StockCommandV1 = StockCommandV1Schema.parse({
        version: 1,
        command_id: crypto.randomUUID(),
        tenant_id: tenantId,
        movement_class: 'production',
        economic_effect: 'internal',
        business_id: businessId,
        production_id: productionId,
        reason: `Ristampa etichetta ${evt.payload.lp_code ?? productionId}`,
        inputs: [],
        outputs: [],
      });
      const labelPayload: LabelPrintedPayload = {
        template_id: tmpl.id,
        ...labelPrinterAudit(printer),
        preset: tmpl.preset,
        data: { ...(evt.payload.data ?? {}), is_reprint: true },
        copies,
        lp_code: evt.payload.lp_code,
        produced_at: evt.payload.produced_at,
        expires_at: evt.payload.expires_at,
      };
      const intentId = crypto.randomUUID();
      const masterEffects = [labelPrintIntentEffect({
        intentId,
        tenantId,
        businessId,
        productionId,
        commandId: command.command_id,
        auditClientUuid: crypto.randomUUID(),
        labelPayload,
        printJob: {
          printer,
          template: tmpl,
          label_data: data,
          copies,
        },
        requestedAt: new Date().toISOString(),
      })];
      const operation: DurableLabelPrintOperation = {
        key: operationKey,
        command,
        masterEffects,
        intentId,
        productionId,
        tenantId,
        businessId,
        printer,
        template: tmpl,
        labelData: data,
        labelPayload,
        copies,
      };
      durablePrintOperations.current.set(operationKey, operation);
      setBusy(true);
      try {
        await executeDurableLabelPrint(operation);
        durablePrintOperations.current.delete(operationKey);
        setToast(`Ristampate · ${copies}× ${visibleLotCode ?? ''}`);
      } catch (err: unknown) {
        if (shouldDiscardDurablePrintOperation(err)) {
          durablePrintOperations.current.delete(operationKey);
        }
        setToast(toUserMessage(err));
      } finally {
        setBusy(false);
      }
    },
    [
      printers,
      realTemplates,
      template,
      profile,
      scopedProductions,
      activeBusinessId,
      allowLegacyUnscopedBusiness,
      executeDurableLabelPrint,
    ],
  );

  const correctionRequest = location.pathname === '/haccp/labels' ? location.state?.lotCorrection : undefined;
  const correctionMatch = useMemo(() => {
    if (!correctionRequest) return null;
    const matches = scopedHistory.filter((event) => {
      if (voidedIds.has(event.id) || event.payload.preset === 'campione' || event.payload.data.sample === true) return false;
      const identity = labelStockIdentity(event, correctionIndex);
      return identity?.foodId === correctionRequest.foodId && identity.lot === correctionRequest.lot;
    });
    return matches.length && new Set(matches.map(event => event.payload.data.production_id)).size === 1
      ? matches[0].id : null;
  }, [correctionRequest, scopedHistory, voidedIds, correctionIndex]);
  const correctionPayloadQuery = useQuery({
    queryKey: ['events', 'label-history-correction', activeTenantId, activeBusinessId,
      allowLegacyUnscopedBusiness, correctionRequest?.id, correctionMatch],
    queryFn: () => fetchLabelHistoryEvent(historyScope, correctionMatch!),
    enabled: !!correctionRequest && !!correctionMatch && !historyDataMissing && !historyReadError,
    networkMode: 'always',
    retry: false,
  });
  useEffect(() => {
    const request = correctionRequest;
    if (!request || handledCorrectionPrint.current === request.id) return;
    setTab('history');
    if (historyDataMissing || historyReadError || stockInLoading || productionsLoading
      || templatesLoading || !devicePrintersLoaded) return;
    const consumeRequest = (): void => {
      handledCorrectionPrint.current = request.id;
      navigation.replace(location.pathname, {});
    };
    if (request.tenantId !== activeTenantId || request.businessId !== activeBusinessId
      || !Number.isInteger(request.copies) || request.copies < 1 || request.copies > 500) {
      consumeRequest();
      setToast('Rettifica salvata. Richiesta di ristampa non valida per questa sede.'); return;
    }
    if (!correctionMatch) {
      consumeRequest();
      setToast('Rettifica salvata. Nessuna etichetta univoca collegata a questo lotto: ristampa non eseguita.'); return;
    }
    // Keep the request until its full immutable payload is available. Read errors
    // offer an explicit retry; a page beyond the visible 30 days needs no scrolling.
    if (correctionPayloadQuery.isPending || correctionPayloadQuery.isFetching || correctionPayloadQuery.isError) return;
    const original = correctionPayloadQuery.data;
    if (!original) {
      consumeRequest();
      setToast('Rettifica salvata. Etichetta non disponibile: ristampa non eseguita.'); return;
    }
    const event = applyLotCorrections(original, correctionIndex);
    const corrected = { ...event, payload: { ...event.payload,
      expires_at: request.date ?? event.payload.expires_at,
      data: { ...event.payload.data, lot_correction_reason: request.reason,
        lot_correction_event_id: request.id,
        ...(request.date ? { original_expires_at: event.payload.data.original_expires_at ?? event.payload.expires_at } : {}),
        ...(request.quantity !== undefined ? { corrected_quantity: request.quantity, corrected_uom: request.uom } : {}),
      },
    } };
    consumeRequest();
    const printerId = resolveDefaultPrinterId(printers, deviceDefaultId, PREVIEW_PRINTER_ID);
    void reprintIdentical(corrected, request.copies, printerId);
  }, [correctionRequest, correctionMatch, correctionPayloadQuery.data, correctionPayloadQuery.isPending,
    correctionPayloadQuery.isFetching, correctionPayloadQuery.isError, historyDataMissing, historyReadError,
    navigation, location.pathname, stockInLoading, productionsLoading, templatesLoading, devicePrintersLoaded,
    activeTenantId, activeBusinessId, correctionIndex, printers, deviceDefaultId, reprintIdentical]);

  // T-279: ristampa dell'etichetta del PASTO CAMPIONE dalla sezione apposita del
  // dialog di ristampa del lotto di base (il campione è integrato nel lotto, non
  // è una voce a sé nello Storico). Solo stampa fisica: NESSUN evento
  // label_printed → non intasa lo Storico. Prelievo letto da
  // productions.sample_taken_at (fallback ora) → scadenza 72h coerente.
  const reprintCampione = useCallback(
    async (evt: LabelEvent, printerId: string | null): Promise<void> => {
      if (!stockEventMatchesBusiness(evt, activeBusinessId, allowLegacyUnscopedBusiness)) {
        setToast('L’etichetta appartiene a un’altra sede: operazione bloccata');
        return;
      }
      const printer = printers.find((p) => p.id === printerId)
        ?? printers.find((p) => p.driver !== 'preview');
      if (!printer) { setToast('Configura una stampante reale'); return; }
      if (printer.driver === 'preview') { setToast('Seleziona una stampante reale per il campione'); return; }
      const tenantId = profile?.active_tenant_id;
      const productionId = evt.payload.data?.production_id;
      const production = typeof productionId === 'string'
        ? scopedProductions.find((candidate) => candidate.id === productionId)
        : null;
      const businessId = activeBusinessId;
      if (
        !tenantId
        || typeof productionId !== 'string'
        || !UUID_PATTERN.test(productionId)
        || !production
        || !businessId
      ) {
        setToast('Campione senza lavorazione o sede valida');
        return;
      }
      const operationKey = `sample:${evt.id}:${printer.id}`;
      const pendingOperation = durablePrintOperations.current.get(operationKey);
      if (pendingOperation) {
        setBusy(true);
        try {
          await executeDurableLabelPrint(pendingOperation);
          durablePrintOperations.current.delete(operationKey);
          setToast(`Etichetta campione ristampata · ${pendingOperation.labelPayload.data.product as string}`);
        } catch (error) {
          if (shouldDiscardDurablePrintOperation(error)) {
            durablePrintOperations.current.delete(operationKey);
          }
          setToast(toUserMessage(error));
        } finally {
          setBusy(false);
        }
        return;
      }
      setBusy(true);
      try {
        const product = (evt.payload.data?.product as string) ?? evt.payload.preset ?? '';
        const lp = (evt.payload.lp_code as string | null) ?? null;
        const prodId = evt.payload.data?.production_id as string | undefined;
        const prod = prodId ? scopedProductions.find((p) => p.id === prodId) : undefined;
        const prelievo = (prod as { sample_taken_at?: string | null } | undefined)?.sample_taken_at
          ?? new Date().toISOString();
        const labelData = buildCampioneLabelData({ product, lp_code: lp, prelievo_at: prelievo });
        const command: StockCommandV1 = StockCommandV1Schema.parse({
          version: 1,
          command_id: crypto.randomUUID(),
          tenant_id: tenantId,
          movement_class: 'production',
          economic_effect: 'internal',
          business_id: businessId,
          production_id: productionId,
          reason: `Ristampa campione ${evt.payload.lp_code ?? productionId}`,
          inputs: [],
          outputs: [],
        });
        const labelPayload: LabelPrintedPayload = {
          template_id: CAMPIONE_TEMPLATE.id,
          ...labelPrinterAudit(printer),
          preset: CAMPIONE_TEMPLATE.preset,
          data: {
            product,
            production_id: productionId,
            sample: true,
            is_reprint: true,
            stock_command_version: 1,
            stock_command_not_applicable: true,
          },
          copies: 1,
          lp_code: lp,
          produced_at: prelievo,
          expires_at: String(labelData.expires_at ?? ''),
        };
        const intentId = crypto.randomUUID();
        const masterEffects = [labelPrintIntentEffect({
          intentId,
          tenantId,
          businessId,
          productionId,
          commandId: command.command_id,
          auditClientUuid: crypto.randomUUID(),
          labelPayload,
          printJob: {
            printer,
            template: { ...CAMPIONE_TEMPLATE, business_id: null },
            label_data: labelData,
            copies: 1,
          },
          requestedAt: new Date().toISOString(),
        })];
        const operation: DurableLabelPrintOperation = {
          key: operationKey,
          command,
          masterEffects,
          intentId,
          productionId,
          tenantId,
          businessId,
          printer,
          template: { ...CAMPIONE_TEMPLATE, business_id: null },
          labelData,
          labelPayload,
          copies: 1,
        };
        durablePrintOperations.current.set(operationKey, operation);
        await executeDurableLabelPrint(operation);
        durablePrintOperations.current.delete(operationKey);
        setToast(`Etichetta campione ristampata · ${product}`);
      } catch (err: unknown) {
        if (shouldDiscardDurablePrintOperation(err)) {
          durablePrintOperations.current.delete(operationKey);
        }
        setToast(toUserMessage(err));
      } finally {
        setBusy(false);
      }
    },
    [
      printers,
      scopedProductions,
      profile,
      activeBusinessId,
      allowLegacyUnscopedBusiness,
      executeDurableLabelPrint,
    ],
  );

  // T-101: "Nuovo lotto" — clona la production parent creando una nuova
  // production indipendente con nuovo trace_token + nuovo lp_code + nuovo
  // production_id. La pagina QR sarà diversa dall'originale (nuovo lotto =
  // nuova catena di tracciabilità). Caso d'uso: ho cucinato di nuovo lo
  // stesso piatto e voglio riusarne ricetta + ingredienti senza ripercorrere
  // il wizard. Stampa 1 sola copia (per copie multiple → reprintIdentical
  // dopo aver creato il nuovo lotto).
  const reprintAsNewLot = useCallback(
    async (evt: LabelEvent): Promise<void> => {
      if (!stockEventMatchesBusiness(evt, activeBusinessId, allowLegacyUnscopedBusiness)) {
        setToast('L’etichetta appartiene a un’altra sede: operazione bloccata');
        return;
      }
      const printer = printers.find((p) => p.id === evt.payload.printer_id) ?? printers[0];
      const tmpl = realTemplates.find((tt) => tt.id === evt.payload.template_id) ?? template;
      if (!printer) { setToast('Nessuna stampante'); return; }
      if (!profile?.active_tenant_id || !profile.active_business_id) {
        setToast('Nessun locale attivo'); return;
      }
      const parentProductionId = evt.payload.data?.production_id as string | undefined;
      const parentProd = parentProductionId
        ? scopedProductions.find((p) => p.id === parentProductionId)
        : null;
      // Con giacenza attiva "Nuovo lotto" è una NUOVA produzione fisica:
      // non può clonare l'audit senza ripetere scarichi/output. Riapriamo quindi
      // il wizard con quantità e lotti correnti da ricontrollare; il successivo
      // commit costruirà un vero StockCommand atomico.
      if (warehouseEnabled || recipes.some(recipe => recipe.id === parentProd?.recipe_id && recipe.chilling_enabled)) {
        setTab('new');
        setPrepStep(1);
        setProducedDate(todayDateStr());
        setCopies(1);
        setCopiesInput('1');
        if (parentProd?.recipe_id && recipes.some((recipe) => recipe.id === parentProd.recipe_id)) {
          setMode('recipe');
          loadFromRecipe(parentProd.recipe_id);
          if (parentProd.output_qty != null && parentProd.output_qty > 0) {
            setOutputQty(String(parentProd.output_qty));
          }
          if (parentProd.output_uom?.trim()) setOutputUom(parentProd.output_uom);
        } else {
          abandonProductionLotPhotoDraft();
          setMode('free');
          setFreeLabelKind('preparation');
          setRecipeId('');
          setPreparationName(
            ((evt.payload.data?.product as string | undefined)
              ?? parentProd?.output_name
              ?? 'Preparazione'),
          );
          setOutputQty(
            parentProd?.output_qty != null && parentProd.output_qty > 0
              ? String(parentProd.output_qty)
              : '',
          );
          setOutputUom(parentProd?.output_uom?.trim() || 'porz');
          setLines([]);
        }
        setOpen(true);
        setToast('Nuovo lotto: conferma quantità e lotti correnti prima di stampare');
        return;
      }
      const operationKey = `new-lot:${evt.id}`;
      const pendingOperation = durablePrintOperations.current.get(operationKey);
      if (pendingOperation) {
        setBusy(true);
        try {
          await executeDurableLabelPrint(pendingOperation);
          durablePrintOperations.current.delete(operationKey);
          setToast(`Nuovo lotto creato · ${pendingOperation.labelPayload.lp_code ?? ''}`);
        } catch (error) {
          if (shouldDiscardDurablePrintOperation(error)) {
            durablePrintOperations.current.delete(operationKey);
          }
          setToast(toUserMessage(error));
        } finally {
          setBusy(false);
        }
        return;
      }
      setBusy(true);
      try {
        // T-102: produced_at = data del click "Nuovo lotto" (now), così
        // l'etichetta riflette quando il cuoco l'ha effettivamente fatta.
        const producedAt = new Date();
        const newProductionId = crypto.randomUUID();
        const newTraceToken = Array.from(crypto.getRandomValues(new Uint8Array(16)))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('');
        const newLp = generateLpCode(producedAt);
        const outputName = (evt.payload.data?.product as string) ?? parentProd?.output_name ?? 'Preparazione';
        const ingredientNames = (evt.payload.data?.ingredients as string[]) ?? [];
        const allergens = (evt.payload.data?.allergens as string[]) ?? [];
        // T-102: shelf life RICALCOLATA dalla ricetta del parent. Se la
        // ricetta è stata aggiornata dopo la stampa dell'originale, il nuovo
        // lotto prende la SL più recente — esattamente come se l'utente avesse
        // ripercorso il wizard preparazione. Fallback al parent.shelf_life_minutes
        // solo se la ricetta non esiste (preparazione libera).
        // Stessa precedenza del wizard preparazione (vedi shelfLifeDaysFromRecipe).
        const recipe = parentProd?.recipe_id
          ? recipes.find((r) => r.id === parentProd.recipe_id) ?? null
          : null;
        const shelfMin = recipe
          ? recipeShelfLifeMinutes(recipe)
          : parentProd?.shelf_life_minutes ?? 72 * 60;
        const expAt = new Date(producedAt.getTime() + shelfMin * 60_000);

        // 1) nuova production (clone dei parent inputs, senza source_production_id
        //    perché è un nuovo lotto indipendente, non una figlia).
        const clonedInputs = (parentProd?.inputs ?? []).map((i) => ({
          food_id: i.food_id ?? null,
          food_name: i.food_name ?? '',
          batch_lot: i.batch_lot ?? null,
          supplier_id: i.supplier_id ?? null,
          supplier_name: i.supplier_name ?? null,
          document_id: i.document_id ?? null,
          qty_used: i.qty_used ?? 0,
          uom: i.uom ?? '',
          allergens: i.allergens ?? [],
          // niente source_production_id → nuovo lotto, no chain di scongelamento
        }));
        const command: StockCommandV1 = StockCommandV1Schema.parse({
          version: 1,
          command_id: crypto.randomUUID(),
          tenant_id: profile.active_tenant_id,
          movement_class: 'production',
          economic_effect: 'internal',
          business_id: profile.active_business_id,
          production_id: newProductionId,
          reason: `Nuovo lotto etichetta ${newLp}`,
          inputs: [],
          outputs: [],
        });
        const masterEffects: StockCommandMasterEffectV1[] = [{
          table: 'productions',
          id: newProductionId,
          op: 'insert',
          base_version: 0,
          patch: {
            id: newProductionId,
            tenant_id: profile.active_tenant_id,
            business_id: profile.active_business_id,
            created_by: profile.id,
            produced_at: producedAt.toISOString(),
            output_name: outputName,
            output_qty: parentProd?.output_qty ?? null,
            output_uom: parentProd?.output_uom ?? null,
            recipe_id: parentProd?.recipe_id ?? null,
            inputs: clonedInputs,
            allergens_aggregated: allergens,
            shelf_life_minutes: shelfMin,
            expires_at: expAt.toISOString(),
            trace_token: newTraceToken,
          },
        }];

        const labelData: Record<string, unknown> = {
          product: outputName,
          prepared_at: producedAt.toISOString(),
          now: producedAt.toISOString(),
          expires_at: expAt.toISOString(),
          allergens,
          ingredients: ingredientNames,
          lp_code: newLp,
          trace_url: buildTraceUrl(newTraceToken),
          lot: null,
        };
        const labelPayload: LabelPrintedPayload = {
          template_id: tmpl.id,
          ...labelPrinterAudit(printer),
          preset: tmpl.preset,
          data: {
            product: outputName,
            production_id: newProductionId,
            trace_token: newTraceToken,
            ingredients: ingredientNames,
            allergens,
            stock_command_version: 1,
            stock_command_not_applicable: true,
          },
          copies: 1,
          lp_code: newLp,
          produced_at: producedAt.toISOString(),
          expires_at: expAt.toISOString(),
        };
        const intentId = crypto.randomUUID();
        masterEffects.push(labelPrintIntentEffect({
          intentId,
          tenantId: profile.active_tenant_id,
          businessId: profile.active_business_id,
          productionId: newProductionId,
          commandId: command.command_id,
          auditClientUuid: crypto.randomUUID(),
          labelPayload,
          printJob: {
            printer,
            template: tmpl,
            label_data: labelData,
            copies: 1,
          },
          requestedAt: new Date().toISOString(),
        }));
        const operation: DurableLabelPrintOperation = {
          key: operationKey,
          command,
          masterEffects,
          intentId,
          productionId: newProductionId,
          tenantId: profile.active_tenant_id,
          businessId: profile.active_business_id,
          printer,
          template: tmpl,
          labelData,
          labelPayload,
          copies: 1,
        };
        durablePrintOperations.current.set(operationKey, operation);
        await executeDurableLabelPrint(operation);
        durablePrintOperations.current.delete(operationKey);
        setToast(`Nuovo lotto creato · ${newLp}`);
      } catch (err: unknown) {
        if (shouldDiscardDurablePrintOperation(err)) {
          durablePrintOperations.current.delete(operationKey);
        }
        setToast(toUserMessage(err));
      } finally {
        setBusy(false);
      }
    },
    [
      printers,
      realTemplates,
      template,
      scopedProductions,
      recipes,
      profile,
      executeDurableLabelPrint,
      warehouseEnabled,
      activeBusinessId,
      allowLegacyUnscopedBusiness,
      loadFromRecipe,
    ],
  );

  const [cancelTarget, setCancelTarget] = useState<LabelEvent | null>(null);
  // T-101: due azioni distinte sulla riga storico:
  //   - "Nuovo lotto" (reprintAsNewLot): clona la production con nuovo
  //     trace_token+LP+production_id → nuova etichetta indipendente.
  //   - "Ristampa N copie" (reprintIdentical): stesso trace_token+LP+production
  //     → solo nuove copie fisiche della STESSA etichetta (es. 3 vaschette
  //     dello stesso lotto). Modal piccolo per scegliere quante.
  const [reprintTarget, setReprintTarget] = useState<LabelEvent | null>(null);
  const [reprintCopiesInput, setReprintCopiesInput] = useState<string>('1');
  const reprintCopies = parseCopyCountInput(reprintCopiesInput, 500);
  // T-279: ristampa del pasto campione INTEGRATA nella ristampa del prodotto:
  // se attiva, l'azione "Stampa" ristampa anche l'etichetta campione (1 copia).
  const [reprintWithSample, setReprintWithSample] = useState<boolean>(false);
  // T-273: la ristampa storica era inchiodata alla stampante originale (spesso
  // l'anteprima, usata prima di configurare una stampante reale). Ora è
  // selezionabile, con default sulla stampante reale configurata.
  const [reprintPrinterId, setReprintPrinterId] = useState<string | null>(null);

  // Annulla una preparazione: il comando reverse ricostruisce tutte le gambe
  // reali del comando originario (input e output), poi emette label_voided.
  // T-109 (fix bug "etichetta fantasma"): il vecchio codice faceva DELETE locale
  // su events_local — l'etichetta spariva dal telefono di chi annullava ma
  // restava viva sul server, quindi gli altri device la rivedevano al pull e il
  // QR rimandava a 404 (production archiviata). Ora emettiamo un evento
  // `label_voided` append-only: lo storico filtra via le label_printed che
  // hanno un voided corrispondente, e la cancellazione propaga a tutti i device.
  // T-100: oltre al voided event, ARCHIVIA la production master correlata così
  // non riappare in frozenProductions ("10 lotti disponibili" gonfiato).
  const cancelLabel = useCallback(async (evt: LabelEvent): Promise<void> => {
    const productionId = evt.payload.data?.production_id as string | undefined;
    setBusy(true);
    try {
      const tenantId = profile?.active_tenant_id;
      if (!stockEventMatchesBusiness(evt, activeBusinessId, allowLegacyUnscopedBusiness)) {
        throw new Error('L’etichetta appartiene a un’altra sede: annullamento bloccato');
      }
      if (!tenantId || !productionId || !UUID_PATTERN.test(productionId)) {
        throw new Error('Etichetta senza lavorazione valida: annullamento bloccato');
      }
      const pendingVoid = durableVoidOperations.current.get(evt.id);
      if (pendingVoid) {
        await enqueueStockCommand.mutateAsync({
          command: pendingVoid.command,
          master_effects: pendingVoid.masterEffects,
        });
        await getSyncEngine().finalizeProductionLabelPrintIntent({
          intentId: pendingVoid.intentId,
          productionId: pendingVoid.productionId,
          tenantId: pendingVoid.tenantId,
          businessId: pendingVoid.businessId,
          outcome: 'voided',
          occurredAt: new Date().toISOString(),
        });
        durableVoidOperations.current.delete(evt.id);
        void queryClient.invalidateQueries({ queryKey: ['events'] });
        void queryClient.invalidateQueries({ queryKey: ['masters'] });
        setToast('Preparazione annullata');
        return;
      }
      const metadata = evt.payload.data ?? {};
      if (metadata.stock_command_version !== 1) {
        throw new Error(
          'Etichetta pre-T481 senza metadati StockCommand: annullamento bloccato per evitare uno storno sintetico.',
        );
      }
      const stockCommandId = typeof metadata.stock_command_id === 'string'
        ? metadata.stock_command_id
        : null;
      const stockCommandEventCount = metadata.stock_command_event_count;
      const lotStateCommandId = typeof metadata.stock_lot_state_command_id === 'string'
        ? metadata.stock_lot_state_command_id
        : null;
      const lotStateEventCount = metadata.stock_lot_state_event_count;
      const stockNotApplicable = metadata.stock_command_not_applicable === true;
      const commandIds = [stockCommandId, lotStateCommandId].filter(Boolean);
      if (
        commandIds.length > 1
        || (stockNotApplicable && commandIds.length > 0)
      ) {
        throw new Error('Metadati StockCommand dell’etichetta incoerenti');
      }

      // Preferisci la sede della production; se il master non è disponibile
      // localmente, usa la sede dell'evento materializzato del comando.
      const prodRow = productionId ? scopedProductions.find((p) => p.id === productionId) : undefined;
      const commandEvent = commandIds.length === 1
        ? stockCommandEvents.find((event) => event.payload.command_id === commandIds[0])
        : undefined;
      const originalBusinessId = activeBusinessId;
      if (!originalBusinessId || !UUID_PATTERN.test(originalBusinessId)) {
        throw new Error('Sede originale della lavorazione non disponibile');
      }
      if (
        prodRow == null
        || (
          commandEvent
          && !stockEventMatchesBusiness(
            commandEvent,
            originalBusinessId,
            allowLegacyUnscopedBusiness,
          )
        )
      ) {
        throw new Error('Catena della lavorazione non coerente con la sede attiva');
      }

      let restoredStock = false;
      let voidCommand: StockCommandV1 | null = null;
      if (stockCommandId != null) {
        if (
          !UUID_PATTERN.test(stockCommandId)
          || typeof stockCommandEventCount !== 'number'
          || !Number.isInteger(stockCommandEventCount)
          || stockCommandEventCount <= 0
        ) {
          throw new Error('Metadati del comando di produzione non validi');
        }
        const reversedCount = reversedCommandEventCount(
          stockCommandEvents,
          stockCommandId,
          originalBusinessId,
        );
        if (reversedCount !== 0 && reversedCount !== stockCommandEventCount) {
          throw new Error('Storno precedente incompleto: sincronizza prima di riprovare');
        }
        if (reversedCount === 0) {
          const originals = originalCommandEvents(
            stockCommandEvents,
            stockCommandId,
            originalBusinessId,
            'production',
            stockCommandEventCount,
            productionId,
          );
          const movements = originals.map(reverseMovementFromEvent);
          voidCommand = StockCommandV1Schema.parse({
            version: 1,
            command_id: crypto.randomUUID(),
            tenant_id: tenantId,
            movement_class: 'reverse',
            economic_effect: 'reversal',
            reason: `Annullamento etichetta ${evt.payload.lp_code ?? productionId}`,
            reverses_command_id: stockCommandId,
            movements,
          });
        }
        restoredStock = true;
      } else if (lotStateCommandId != null) {
        if (
          !UUID_PATTERN.test(lotStateCommandId)
          || typeof lotStateEventCount !== 'number'
          || !Number.isInteger(lotStateEventCount)
          || lotStateEventCount !== 1
        ) {
          throw new Error('Metadati del comando lotto non validi');
        }
        const closeLineRef = `label:${evt.id}:void`;
        const priorClose = stockCommandEvents.filter((event) => (
          eventBusinessId(event) === originalBusinessId
          && event.payload.movement_class === 'lot_state'
          && event.payload.lot_state_action === 'close'
          && event.payload.source_line_ref === closeLineRef
          && event.payload.source_production_id === productionId
        ));
        if (priorClose.length > 1) {
          throw new Error('Chiusura lotto duplicata: sincronizza prima di riprovare');
        }
        if (priorClose.length === 0) {
          const [original] = originalCommandEvents(
            stockCommandEvents,
            lotStateCommandId,
            originalBusinessId,
            'lot_state',
            lotStateEventCount,
            productionId,
          );
          if (
            !original
            || original.kind !== 'stock_in'
            || original.payload.lot_state_action !== 'register'
          ) {
            throw new Error('Registrazione lotto originale non valida');
          }
          const foodId = original.payload.food_id;
          const batchLot = original.payload.batch_lot;
          const stockUom = original.payload.stock_uom ?? original.payload.uom;
          if (
            typeof foodId !== 'string'
            || !UUID_PATTERN.test(foodId)
            || typeof batchLot !== 'string'
            || batchLot.trim().length === 0
            || typeof stockUom !== 'string'
            || stockUom.trim().length === 0
          ) {
            throw new Error('Registrazione lotto originale incompleta');
          }
          const expirationDate = original.payload.expiration_date;
          if (expirationDate != null && typeof expirationDate !== 'string') {
            throw new Error('Scadenza del lotto originale non valida');
          }
          voidCommand = StockCommandV1Schema.parse({
            version: 1,
            command_id: crypto.randomUUID(),
            tenant_id: tenantId,
            movement_class: 'lot_state',
            economic_effect: 'stock_only',
            business_id: originalBusinessId,
            reason: `Annullamento lotto etichetta ${evt.payload.lp_code ?? productionId}`,
            lines: [{
              food_id: foodId,
              batch_lot: batchLot,
              expiration_date: expirationDate ?? null,
              supplier_id: nullableUuid(original.payload, 'supplier_id'),
              document_id: nullableUuid(original.payload, 'document_id'),
              storage_unit_id: nullableUuid(original.payload, 'storage_unit_id'),
              source_production_id: productionId,
              line_ref: closeLineRef,
              action: 'close',
              uom: stockUom,
              operator_name: profile.full_name?.trim() || null,
            }],
          });
        }
        restoredStock = true;
      } else if (!stockNotApplicable) {
        throw new Error(
          'Etichetta pre-T481 senza comando versionato: annullamento bloccato, nessuno storno sintetico emesso.',
        );
      }

      if (!voidCommand) {
        voidCommand = StockCommandV1Schema.parse({
          version: 1,
          command_id: crypto.randomUUID(),
          tenant_id: tenantId,
          movement_class: 'production',
          economic_effect: 'internal',
          business_id: originalBusinessId,
          production_id: productionId,
          reason: `Annullamento etichetta ${evt.payload.lp_code ?? productionId} senza movimento residuo`,
          inputs: [],
          outputs: [],
        });
      }

      const voidedAt = new Date().toISOString();
      const masterEffects: StockCommandMasterEffectV1[] = [];
      if (prodRow && !prodRow.archived_at) {
        masterEffects.push({
          table: 'productions',
          id: productionId,
          op: 'archive',
          base_version: prodRow.version ?? 0,
          patch: {
            id: productionId,
            tenant_id: tenantId,
            archived_at: voidedAt,
          },
        });
      }
      const intentId = crypto.randomUUID();
      masterEffects.push(labelPrintIntentEffect({
        intentId,
        tenantId,
        businessId: originalBusinessId,
        productionId,
        commandId: voidCommand.command_id,
        auditClientUuid: crypto.randomUUID(),
        auditKind: 'label_voided',
        labelPayload: {
          original_event_id: evt.id,
          reason: 'user_cancel',
          voided_at: voidedAt,
        },
        requestedAt: voidedAt,
      }));
      const pending: DurableLabelVoidOperation = {
        command: voidCommand,
        masterEffects,
        intentId,
        productionId,
        tenantId,
        businessId: originalBusinessId,
      };
      durableVoidOperations.current.set(evt.id, pending);
      await enqueueStockCommand.mutateAsync({
        command: voidCommand,
        master_effects: masterEffects,
      });
      await getSyncEngine().finalizeProductionLabelPrintIntent({
        intentId,
        productionId,
        tenantId,
        businessId: originalBusinessId,
        outcome: 'voided',
        occurredAt: voidedAt,
      });
      durableVoidOperations.current.delete(evt.id);
      void queryClient.invalidateQueries({ queryKey: ['events'] });
      void queryClient.invalidateQueries({ queryKey: ['masters'] });
      setToast(restoredStock
        ? 'Preparazione annullata · giacenze ripristinate'
        : 'Preparazione annullata');
    } catch (e) {
      setToast(toUserMessage(e));
    } finally {
      setBusy(false);
      setCancelTarget(null);
    }
  }, [
    enqueueStockCommand,
    scopedProductions,
    profile,
    queryClient,
    stockCommandEvents,
    activeBusinessId,
    allowLegacyUnscopedBusiness,
  ]);

  // ───────── render ─────────
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons>
          <IonTitle>{t('haccp.labels')}</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment
            value={tab}
            onIonChange={(ev) => {
              const next = ev.detail.value as typeof tab;
              setTab(next);
              // T-099: reset del filtro storico al cambio tab. Difensivo:
              // l'utente segnalava "lo storico è diventato solo 2 righe di
              // tonno scongelato" — probabilmente la searchbar conservava un
              // filtro da una sessione precedente. Resettando al cambio tab
              // evitiamo che una ricerca precedente nasconda le nuove etichette.
              if (next === 'history') setHistorySearch('');
            }}
          >
            <IonSegmentButton value="new"><IonLabel>Nuova preparazione</IonLabel></IonSegmentButton>
            <IonSegmentButton value="history"><IonLabel>Storico{!historyDataMissing && ` (${labelStats.distinctLots})`}</IonLabel></IonSegmentButton>
            {isCollPrelabel && (
              <IonSegmentButton value="collprelabel"><IonLabel>Pre-stampa</IonLabel></IonSegmentButton>
            )}
          </IonSegment>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding te-page">
        {(labelReconciliations.length + pendingLabelSync.length + recoverablePrintIntents.length) > 0 && (
          <details className="te-status-summary">
            <summary>
              <span className="te-status-summary__count">
                {labelReconciliations.length + recoverablePrintIntents.length > 0
                  ? labelReconciliations.length + recoverablePrintIntents.length
                  : '✓'}
              </span>
              {labelReconciliations.length + recoverablePrintIntents.length > 0
                ? 'Stampe che richiedono attenzione'
                : 'Stampe salvate · sincronizzazione in corso'}
              <span style={{ marginLeft: 'auto', color: 'var(--te-text-muted)', fontSize: 12 }}>
                Apri
              </span>
            </summary>
            <div className="te-status-summary__body">
        {labelReconciliations.length > 0 && (
          <div style={{
            marginBottom: 14,
            padding: 12,
            borderRadius: 12,
            border: '1px solid #dc2626',
            background: '#fef2f2',
            color: '#7f1d1d',
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Etichette da riconciliare ({labelReconciliations.length})
            </div>
            <div style={{ fontSize: 12, marginBottom: 10 }}>
              Il server ha rifiutato l’operazione dopo l’esito fisico. La giacenza
              ottimistica è stata rimossa e non è stato creato alcuno storno
              sintetico. Rimuovi l’etichetta non valida, oppure verifica la
              situazione, poi chiudi l’incidente.
            </div>
            {labelReconciliations.map((incident) => {
              const payload = incident.intent_snapshot.label_payload;
              const data = isRecord(payload.data) ? payload.data : {};
              const product = typeof data.product === 'string'
                ? data.product
                : typeof payload.lp_code === 'string'
                  ? payload.lp_code
                  : 'Etichetta';
              const busyIncident = reconciliationBusyId === incident.intent_id;
              return (
                <div
                  key={incident.intent_id}
                  style={{
                    paddingTop: 10,
                    marginTop: 10,
                    borderTop: '1px solid #fca5a5',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{product}</div>
                  <div style={{ fontSize: 11, marginTop: 2 }}>
                    {incident.outcome === 'printed'
                      ? 'Etichetta stampata, comando non accettato'
                      : 'Annullamento locale non accettato dal server'}
                    {' · '}
                    {incident.reason}
                  </div>
                  <IonButton
                    size="small"
                    color="danger"
                    fill="outline"
                    disabled={busyIncident}
                    onClick={() => void resolveLabelReconciliation(incident)}
                    style={{ marginTop: 8 }}
                  >
                    Etichetta rimossa / risolta
                  </IonButton>
                </div>
              );
            })}
          </div>
        )}
        {pendingLabelSync.length > 0 && (
          <div style={{
            marginBottom: 14,
            padding: 12,
            borderRadius: 12,
            border: '1px solid #60a5fa',
            background: '#eff6ff',
            color: '#1e3a8a',
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Etichette in attesa di sincronizzazione ({pendingLabelSync.length})
            </div>
            <div style={{ fontSize: 12 }}>
              L’esito fisico è salvato sul dispositivo. Puoi continuare a lavorare:
              la registrazione sarà completata automaticamente quando la connessione
              sarà disponibile. Non occorre ristampare.
            </div>
          </div>
        )}
        {recoverablePrintIntents.length > 0 && (
          <div style={{
            marginBottom: 14,
            padding: 12,
            borderRadius: 12,
            border: '1px solid #d97706',
            background: '#fffbeb',
            color: '#78350f',
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Stampe da verificare ({recoverablePrintIntents.length})
            </div>
            <div style={{ fontSize: 12, marginBottom: 10 }}>
              Il lavoro è salvato. Per queste etichette manca un esito locale:
              se l’etichetta è uscita, usa “Conferma già stampata” senza ristampare.
              Riprova soltanto se non è uscita. Nessuna ristampa è automatica.
            </div>
            {recoverablePrintIntents.map((intent) => {
              const product = typeof intent.label_payload?.data?.product === 'string'
                ? intent.label_payload.data.product
                : intent.label_payload.lp_code ?? 'Etichetta';
              const isBusy = recoveryBusyId === intent.id;
              const definitelyNotPrinted = intent.error?.startsWith('Stampa non avviata:') === true;
              return (
                <div
                  key={intent.id}
                  style={{
                    paddingTop: 10,
                    marginTop: 10,
                    borderTop: '1px solid #fcd34d',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{product}</div>
                  <div style={{ fontSize: 11, marginTop: 2 }}>
                    {definitelyNotPrinted
                      ? 'Stampa non avviata'
                      : 'Esito della stampa da verificare'}
                    {intent.error ? ` · ${intent.error}` : ''}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {!definitelyNotPrinted && (
                      <IonButton
                        size="small"
                        disabled={isBusy}
                        onClick={() => void resolveRecoverablePrintIntent(intent)}
                      >
                        Conferma già stampata
                      </IonButton>
                    )}
                    <IonButton
                      size="small"
                      fill="outline"
                      disabled={isBusy}
                      onClick={() => void retryRecoverablePrintIntent(intent)}
                    >
                      Riprova stampa
                    </IonButton>
                  </div>
                </div>
              );
            })}
          </div>
        )}
            </div>
          </details>
        )}
        {tab === 'new' && (
          <>
            <div className="te-screen-intro">
              <div>
                <h1>Cosa devi stampare?</h1>
                <p>Scegli il tipo di lavorazione. Quantità, validità e copie vengono richieste nel passaggio successivo.</p>
              </div>
            </div>
            {hasPrintableRecipes && (
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <h2 style={{ margin: '0 4px 8px', fontSize: 16, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <IonIcon icon={star} aria-hidden="true" style={{ color: '#f59e0b' }} />
                    Stampa rapida
                  </h2>
                  <IonButton fill="clear" routerLink="/haccp/recipes">
                    Personalizza
                  </IonButton>
                </div>
                {favoriteOwnerLabel && (
                  <div style={{ margin: '-4px 4px 8px', fontSize: 12, color: '#64748b' }}>
                    Preferiti personali di {favoriteOwnerLabel}
                  </div>
                )}
                {quickPrintRecipes.length === 0 ? (
                  <div style={{ padding: '10px 4px', fontSize: 13, color: '#64748b' }}>
                    Nessun preferito personale. Tocca Personalizza per scegliere le ricette.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                    {quickPrintRecipes.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => {
                          loadFromRecipe(r.id);
                          setMode('recipe');
                          setOpen(true);
                        }}
                        style={{
                          padding: 14,
                          borderRadius: 12,
                          border: '1px solid #cbd5e1',
                          background: favoriteIds.has(r.id) ? '#fef3c7' : '#fff',
                          textAlign: 'left',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                          minHeight: 64,
                        }}
                      >
                        <span style={{ fontWeight: 600, fontSize: 14, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 4 }}>
                          {favoriteIds.has(r.id) && <IonIcon icon={star} aria-hidden="true" style={{ color: '#f59e0b' }} />}
                          {r.name}
                        </span>
                        <span style={{ fontSize: 11, color: '#64748b' }}>
                          {r.ingredients?.length ?? 0} ingred. · {r.yield_qty} {r.yield_uom}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="te-workflow-grid">
              <button
                type="button"
                onClick={() => { setOpen(true); setSearchPicker({ kind: 'recipe' }); }}
                className="te-workflow-card"
              >
                <span className="te-workflow-card__icon">
                  <IonIcon icon={restaurantOutline} />
                </span>
                <span className="te-workflow-card__title">
                  Preparazione
                </span>
                <span className="te-workflow-card__description">
                  Ricetta o semilavorato
                </span>
              </button>
              <button
                type="button"
                onClick={() => setMaterialOpen(true)}
                className="te-workflow-card"
              >
                <span className="te-workflow-card__icon">
                  <IonIcon icon={cubeOutline} />
                </span>
                <span className="te-workflow-card__title">
                  Materia prima
                </span>
                <span className="te-workflow-card__description">
                  Scongelamento o apertura
                </span>
              </button>
            </div>

            {/* Editor + salva-predefinito: SOLO Consulente HACCP. Il
                ristoratore è esonerato dalla configurazione layout. */}
            {isConsultant && (
              <details className="te-status-summary" style={{ marginTop: 16 }}>
                <summary>
                  Impostazioni etichetta
                  <span style={{ marginLeft: 'auto', color: 'var(--te-text-muted)', fontSize: 12 }}>
                    Solo consulente
                  </span>
                </summary>
                <div className="te-status-summary__body">
                {/* Selettore ristorante per editing layout — visibile solo se
                    l'account ha accesso a più tenant (owner/consulente multi-locale). */}
                {allTenantsData.length > 1 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{
                      fontSize: 11, fontWeight: 700, color: '#64748b',
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                      marginBottom: 6,
                    }}>
                      Ristorante da configurare
                    </div>
                    <select
                      value={labelTenantId ?? ''}
                      onChange={(e) => {
                        setLabelTenantId(e.target.value || null);
                        setTemplateVersion((v) => v + 1);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: 10,
                        border: '1.5px solid #cbd5e1',
                        background: '#fff',
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#0f172a',
                        appearance: 'none',
                        backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'8\' viewBox=\'0 0 12 8\'%3E%3Cpath d=\'M1 1l5 5 5-5\' stroke=\'%2394a3b8\' stroke-width=\'1.8\' fill=\'none\' stroke-linecap=\'round\'/%3E%3C/svg%3E")',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        paddingRight: 32,
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(15,23,42,.07)',
                      }}
                    >
                      {allTenantsData.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setEditorOpen(true)}
                  style={{
                    marginTop: 16, width: '100%',
                    background: '#fff',
                    border: '1px solid #cbd5e1',
                    borderRadius: 12,
                    padding: '12px 14px',
                    display: 'flex', alignItems: 'center', gap: 10,
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                  }}
                >
                  <span style={{ fontSize: 22 }}>✏️</span>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                      Modifica layout etichetta
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                      Sposta QR e testi, cambia dimensioni, anteprima live
                    </div>
                  </div>
                  <span style={{ color: '#cbd5e1', fontSize: 18 }}>›</span>
                </button>
                <button
                  type="button"
                  onClick={() => { void saveLayoutAsDefault(); }}
                  style={{
                    marginTop: 10, width: '100%',
                    background: '#16a34a', color: '#fff',
                    border: 'none', borderRadius: 12,
                    padding: '13px 14px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(22,163,74,.35)',
                  }}
                >
                  <IonIcon icon={checkmarkCircle} style={{ fontSize: 20 }} />
                  Salva questo layout come predefinito (azienda)
                </button>
                </div>
              </details>
            )}
          </>
        )}

        {tab === 'history' && (() => {
          const now = historyNow;
          const filtersOn = historySearch.trim().length > 0 || historyStatus !== 'all' || historyPeriod !== '30d';
          const readError = historyReadError || historyPayloadError;
          const canLoadMore = !historyDataMissing && historyVisibleIds.length < historySelection.sorted.length;
          const canLoadEarlier = !historyDataMissing && !canLoadMore && historySelection.hasEarlier;
          const loadingMore = historyPayloadQuery.isFetching;

          const pill = (label: string, active: boolean, onClick: () => void, accent = '#2563eb'): JSX.Element => (
            <button
              key={label}
              type="button"
              onClick={() => { setHistoryPage(null); onClick(); }}
              style={{
                padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                border: `1px solid ${active ? accent : '#cbd5e1'}`,
                background: active ? accent : '#fff',
                color: active ? '#fff' : '#475569',
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              {label}
            </button>
          );

          const dayLabel = (ts: number): string => {
            const d = new Date(ts);
            const today = new Date(now);
            const yest = new Date(now - HISTORY_DAY_MS);
            if (d.toDateString() === today.toDateString()) return 'Oggi';
            if (d.toDateString() === yest.toDateString()) return 'Ieri';
            return d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
          };

          const renderRow = (l: LabelEvent): JSX.Element => {
            const lp = visibleLabelLotCode(l.payload);
            const exp = l.payload.expires_at;
            const tk = l.payload.data?.trace_token as string | undefined;
            const st = labelHistoryStatus(l, now);
            const expColor = st === 'expired' ? '#dc2626' : st === 'expiring' ? '#b45309' : '#888';
            return (
              <IonItem key={l.id}>
                <IonLabel>
                  <h3>{(l.payload.data?.product as string) ?? l.payload.preset}</h3>
                  <p style={{ fontSize: 12 }}>
                    {lp ? `${lp} · ` : ''}
                    Prod. {new Date(labelProducedTs(l)).toLocaleDateString('it-IT')}
                  </p>
                  {typeof l.payload.data.lot_correction_reason === 'string' && (
                    <p style={{ whiteSpace: 'normal', fontSize: 12 }}>
                      Rettifica: {l.payload.data.lot_correction_reason}
                      {typeof l.payload.data.original_expires_at === 'string' && ` · scadenza originale ${new Date(l.payload.data.original_expires_at).toLocaleDateString('it-IT')}`}
                      {typeof l.payload.data.corrected_quantity === 'number' && ` · quantità rettificata ${l.payload.data.corrected_quantity} ${l.payload.data.corrected_uom ?? ''}`}
                    </p>
                  )}
                  {exp && (
                    <p style={{ color: expColor, fontSize: 11, fontWeight: st === 'valid' ? 400 : 700 }}>
                      {st === 'expired' ? '⚠️ Scaduta il ' : st === 'expiring' ? '⏳ Scade ' : 'Scade '}
                      {new Date(exp).toLocaleDateString('it-IT')}
                    </p>
                  )}
                </IonLabel>
                {tk && (
                  <IonButton slot="end" fill="clear" size="small" onClick={() => openTracePage(buildTraceUrl(tk))} aria-label="Apri pagina QR">
                    <IonIcon icon={openOutline} />
                  </IonButton>
                )}
                {/* 🖨️ Ristampa = N copie identiche · 🆕 Nuovo lotto = nuovo trace+LP */}
                <IonButton slot="end" fill="clear" disabled={busy} onClick={() => {
                  setReprintCopiesInput('1');
                  setReprintWithSample(false);
                  // Default: stampante originale se reale, altrimenti la
                  // predefinita del dispositivo (poi prima reale, poi anteprima).
                  const orig = printers.find((p) => p.id === l.payload.printer_id);
                  setReprintPrinterId((orig && orig.driver !== 'preview')
                    ? orig.id
                    : resolveDefaultPrinterId(printers, deviceDefaultId, PREVIEW_PRINTER_ID));
                  setReprintTarget(l);
                }} aria-label="Ristampa N copie identiche" title="Ristampa N copie (stesso lotto)">
                  <IonIcon icon={copyOutline} />
                </IonButton>
                <IonButton slot="end" fill="clear" disabled={busy} onClick={() => { void reprintAsNewLot(l); }} aria-label="Crea nuovo lotto da questa etichetta" title="Nuovo lotto (etichetta indipendente)">
                  <IonIcon icon={refreshOutline} />
                </IonButton>
                <IonButton slot="end" fill="clear" color="danger" disabled={busy} onClick={() => setCancelTarget(l)} aria-label="Annulla preparazione">
                  <IonIcon icon={trashOutline} />
                </IonButton>
              </IonItem>
            );
          };

          // Righe: raggruppate per giorno di produzione (header data) quando l'ordine
          // è "data produzione"; lista piatta quando l'ordine è "scadenza".
          const rows: JSX.Element[] = [];
          if (historySort === 'produced') {
            let lastDay = '';
            for (const l of historyRows) {
              const key = new Date(labelProducedTs(l)).toDateString();
              if (key !== lastDay) {
                lastDay = key;
                rows.push(
                  <div key={`h-${key}`} style={{
                    position: 'sticky', top: 0, zIndex: 2,
                    background: '#eef2f7', padding: '8px 16px 6px',
                    fontSize: 12, fontWeight: 800, color: '#475569',
                    textTransform: 'capitalize', borderBottom: '1px solid #e2e8f0',
                  }}>
                    {dayLabel(labelProducedTs(l))}
                  </div>,
                );
              }
              rows.push(renderRow(l));
            }
          } else {
            for (const l of historyRows) rows.push(renderRow(l));
          }

          return (
            <>
              <IonSearchbar
                value={historySearch}
                debounce={150}
                onIonInput={(e) => { setHistoryPage(null); setHistorySearch(String(e.detail.value ?? '')); }}
                placeholder="Cerca per nome prodotto, lotto LP, preset…"
                showClearButton="focus"
                style={{ '--background': '#fff' } as React.CSSProperties}
              />
              {/* Ordina + filtri */}
              <div style={{ padding: '4px 12px 8px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflowX: 'auto' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Ordina</span>
                  {pill('📅 Data produzione', historySort === 'produced', () => setHistorySort('produced'))}
                  {pill('⏳ Scadenza', historySort === 'expiry', () => setHistorySort('expiry'))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflowX: 'auto' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Stato</span>
                  {pill('Tutte', historyStatus === 'all', () => setHistoryStatus('all'))}
                  {pill('Valide', historyStatus === 'valid', () => setHistoryStatus('valid'), '#16a34a')}
                  {pill('In scadenza', historyStatus === 'expiring', () => setHistoryStatus('expiring'), '#b45309')}
                  {pill('Scadute', historyStatus === 'expired', () => setHistoryStatus('expired'), '#dc2626')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflowX: 'auto' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Periodo</span>
                  {pill('Sempre', historyPeriod === 'all', () => setHistoryPeriod('all'))}
                  {pill('Oggi', historyPeriod === 'today', () => setHistoryPeriod('today'))}
                  {pill('7 giorni', historyPeriod === '7d', () => setHistoryPeriod('7d'))}
                  {pill('Ultimi 30 giorni', historyPeriod === '30d', () => setHistoryPeriod('30d'))}
                </div>
              </div>
              {!historyDataMissing && (
                <IonNote style={{ display: 'block', padding: '0 16px 8px', fontSize: 11, color: '#64748b' }}>
                  {labelStats.distinctLots} lotti di produzione nello storico
                  {' · '}{historyRows.length} di {historySelection.sorted.length} righe nel periodo
                  {' · '}{historyPeriod === 'all' ? 'Tutto lo storico' : historyPeriod === 'today' ? 'Oggi' : historyPeriod === '7d' ? 'Ultimi 7 giorni' : 'Ultimi 30 giorni'}
                  {' · '}{historySort === 'produced' ? 'per data di produzione' : 'per scadenza'}
                  {filtersOn && (
                    <button type="button" onClick={() => {
                      setHistorySearch(''); setHistoryStatus('all'); setHistoryPeriod('30d'); setHistoryPage(null);
                    }} style={{ marginLeft: 8, color: '#2563eb', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11 }}>
                      ✕ Azzera filtri
                    </button>
                  )}
                </IonNote>
              )}
              {(historyLoading || readError) && (
                <IonItem lines="none">
                  {!readError && <IonSpinner slot="start" name="dots" />}
                  <IonLabel className="ion-text-wrap" role={readError ? 'alert' : 'status'}>
                    {readError ? historyRows.length
                      ? 'Aggiornamento dello storico non riuscito. Mostro le etichette già caricate.'
                      : 'Impossibile leggere lo storico etichette. Riprova.'
                      : historyRows.length ? 'Caricamento di altre etichette…' : 'Caricamento dello storico etichette…'}
                  </IonLabel>
                  {readError && <IonButton slot="end" onClick={retryHistoryRead}>Riprova</IonButton>}
                </IonItem>
              )}
              {correctionRequest && correctionPayloadQuery.isError && (
                <IonItem lines="none">
                  <IonLabel className="ion-text-wrap" role="alert">Rettifica salvata. Impossibile leggere l’etichetta da ristampare.</IonLabel>
                  <IonButton slot="end" disabled={correctionPayloadQuery.isFetching} onClick={() => void correctionPayloadQuery.refetch()}>Riprova ristampa</IonButton>
                </IonItem>
              )}
              <IonList>
                {historyRows.length ? rows : !historyLoading && !readError ? (
                  <IonItem><IonLabel className="ion-text-wrap" color="medium">
                    {visibleHistory.length === 0 ? 'Nessuna etichetta stampata.' : 'Nessuna etichetta nel periodo e con i filtri scelti.'}
                  </IonLabel></IonItem>
                ) : null}
              </IonList>
              {(canLoadMore || canLoadEarlier) && (
                <IonButton expand="block" fill="outline" disabled={loadingMore || historyLoading || readError} onClick={() => {
                  if (canLoadMore) {
                    setHistoryPage({ key: historyPageKey, limit: historyLimit + LABEL_HISTORY_PAGE_SIZE });
                  } else {
                    setHistoryPeriod('all');
                    setHistoryPage({
                      key: JSON.stringify([activeTenantId, activeBusinessId, historySearch, historySort, historyStatus, 'all']),
                      limit: historyVisibleIds.length + LABEL_HISTORY_PAGE_SIZE,
                    });
                  }
                }}>
                  {loadingMore ? <IonSpinner name="dots" /> : canLoadMore ? 'Mostra di più' : 'Mostra etichette precedenti'}
                </IonButton>
              )}
            </>
          );
        })()}

        {/* ───── T-303: tab Pre-stampa collettiva (solo Bluecoop) ───── */}
        {tab === 'collprelabel' && isCollPrelabel && (
          <div style={{ marginTop: 16, paddingBottom: 24 }}>
            {/* Header: data del giorno → lotto */}
            <div style={{ background: '#0f172a', color: '#fff', borderRadius: 14, padding: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 12, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Lotto della giornata di produzione
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 26, fontWeight: 800 }}>{collDateLabel}</div>
                <input
                  type="date"
                  value={collDate}
                  onChange={(e) => setCollDate(e.target.value)}
                  style={{ border: 'none', borderRadius: 8, padding: '8px 10px', fontSize: 15, fontWeight: 600 }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                {(['pranzo', 'cena'] as const).map((mealOpt) => (
                  <button
                    key={mealOpt}
                    type="button"
                    onClick={() => setCollMeal(mealOpt)}
                    style={{
                      flex: 1, padding: '7px 0', borderRadius: 8, fontSize: 13, fontWeight: 700,
                      cursor: 'pointer', textTransform: 'capitalize',
                      border: collMeal === mealOpt ? '1px solid #38bdf8' : '1px solid rgba(255,255,255,0.25)',
                      background: collMeal === mealOpt ? '#38bdf8' : 'transparent',
                      color: collMeal === mealOpt ? '#0f172a' : '#fff',
                    }}
                  >
                    {mealOpt}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
                Lotto = data di produzione. I 3 ordinari mostrano i piatti del giorno (rotazione), modificabili.
              </div>
            </div>

            {/* Stampante */}
            <IonList inset>
              <IonItem>
                <IonLabel>Stampante</IonLabel>
                <IonSelect value={collPrinterId} onIonChange={(e) => setCollPrinterId(e.detail.value as string)} interface="action-sheet">
                  {printers.map((p) => (
                    <IonSelectOption key={p.id} value={p.id}>{p.name}</IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            </IonList>

            {/* Toolbar lista */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '10px 4px 8px' }}>
              <h2 style={{ margin: 0, fontSize: 16, color: '#475569' }}>Alimenti da etichettare</h2>
              <button
                type="button"
                onClick={openCollManage}
                style={{ color: '#2563eb', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
              >
                Modifica lista
              </button>
            </div>

            {collRows.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#94a3b8', padding: '28px 16px', border: '1px dashed #cbd5e1', borderRadius: 12 }}>
                <div style={{ fontSize: 14, marginBottom: 10 }}>Nessun alimento in lista.</div>
                <IonButton size="small" onClick={openCollManage}>Crea la lista</IonButton>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {(() => {
                  // T-406 (A4): raggruppa la lista pre-stampa per categoria come
                  // l'ordine preposto (Primi/Secondi/…), per leggibilità staff.
                  const CAT_ORDER = ['PRIMI', 'SECONDI', 'CONTORNI', 'FRUTTA', 'ALTRO'];
                  const CAT_LABEL: Record<string, string> = { PRIMI: 'Primi', SECONDI: 'Secondi', CONTORNI: 'Contorni', FRUTTA: 'Frutta', ALTRO: 'Altro' };
                  const byCat = new Map<string, typeof collRows>();
                  for (const r of collRows) {
                    const c = (r.category as string) || 'ALTRO';
                    const arr = byCat.get(c) ?? [];
                    arr.push(r); byCat.set(c, arr);
                  }
                  const cats = [...CAT_ORDER.filter((c) => byCat.has(c)), ...[...byCat.keys()].filter((c) => !CAT_ORDER.includes(c))];
                  return cats.map((cat) => (
                    <div key={cat}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 2px 6px' }}>
                        {CAT_LABEL[cat] ?? cat}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {(byCat.get(cat) ?? []).map((r) => {
                          const n = collCount(r.id);
                          const { product, ordinario, recipeId } = resolveRowDish(r);
                          return (
                            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: ordinario ? '#f0f9ff' : '#fff', border: `1px solid ${ordinario ? '#bae6fd' : '#e2e8f0'}`, borderRadius: 10, padding: '8px 10px' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{product}</span>
                                {!recipeId && !ordinario && <span title="Nessuna ricetta agganciata" style={{ fontSize: 10, color: '#b45309', marginLeft: 6 }}>⚠ senza ricetta</span>}
                                {ordinario && (
                                  <button
                                    type="button"
                                    onClick={() => setCollEditOrd(ordinario)}
                                    style={{ display: 'block', marginTop: 2, fontSize: 11, color: '#0284c7', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
                                  >
                                    {r.name} · piatto del giorno ✎
                                  </button>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <button type="button" aria-label="meno" onClick={() => setCollCount(r.id, n - 1)} style={COLL_STEP_BTN_STYLE}>−</button>
                                <input
                                  inputMode="numeric"
                                  value={collCounts[r.id] ?? ''}
                                  placeholder="0"
                                  onChange={(e) => { const v = parseInt(e.target.value, 10); setCollCount(r.id, Number.isFinite(v) ? v : 0); }}
                                  style={{ width: 48, textAlign: 'center', fontSize: 16, fontWeight: 700, border: 'none', background: 'transparent', color: n > 0 ? '#0f172a' : '#cbd5e1' }}
                                />
                                <button type="button" aria-label="più" onClick={() => setCollCount(r.id, n + 1)} style={COLL_STEP_BTN_STYLE}>+</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}

            {/* CTA stampa in serie */}
            <div style={{ position: 'sticky', bottom: 0, paddingTop: 12, background: 'linear-gradient(to top, var(--ion-background-color,#fff) 60%, transparent)' }}>
              <IonButton expand="block" disabled={collBusy || collTotalLabels === 0} onClick={() => void printAllCollettiva()}>
                {collBusy ? <IonSpinner name="dots" /> : (<><IonIcon icon={printOutline} slot="start" /> Stampa tutte ({collTotalLabels})</>)}
              </IonButton>
            </div>
          </div>
        )}

        {/* ───── T-303: override del piatto del giorno per un ordinario ───── */}
        <IonAlert
          isOpen={collEditOrd !== null}
          onDidDismiss={() => setCollEditOrd(null)}
          header={collEditOrd === 'PRIMI' ? 'Primo del giorno' : collEditOrd === 'SECONDI' ? 'Secondo del giorno' : 'Contorno del giorno'}
          message="Cambia il piatto da stampare per questo ordinario (solo per questa stampa)."
          inputs={[{
            name: 'dish',
            type: 'text',
            value: collEditOrd ? (collOrdOverride[collEditOrd]?.trim() || collDayDishes[collEditOrd] || '') : '',
            placeholder: 'Nome del piatto',
          }]}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            ...(collEditOrd && collOrdOverride[collEditOrd]
              ? [{
                  text: 'Rotazione',
                  handler: () => {
                    const cat = collEditOrd;
                    setCollOrdOverride((o) => { const c = { ...o }; if (cat) delete c[cat]; return c; });
                  },
                }]
              : []),
            {
              text: 'OK',
              handler: (data: { dish?: string }) => {
                const cat = collEditOrd;
                if (cat) setCollOrdOverride((o) => ({ ...o, [cat]: (data?.dish ?? '').trim() }));
              },
            },
          ]}
        />

        {/* ───── T-303: modal "Modifica lista" pre-stampa collettiva ───── */}
        <IonModal isOpen={collManageOpen} onDidDismiss={() => setCollManageOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start"><IonButton onClick={() => setCollManageOpen(false)}>Annulla</IonButton></IonButtons>
              <IonTitle>Modifica lista</IonTitle>
              <IonButtons slot="end"><IonButton strong onClick={() => void saveCollList()}>Salva</IonButton></IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <IonButton
                size="small"
                fill="outline"
                onClick={() => setCollDraft((p) => [...p, { id: crypto.randomUUID(), name: '', suggested_copies: 0, sort_order: p.length }])}
              >
                <IonIcon icon={add} slot="start" /> Aggiungi riga
              </IonButton>
            </div>
            <IonNote style={{ display: 'block', marginBottom: 10, fontSize: 12 }}>
              Il numero di copie che imposti qui (o sulla pagina principale) resta salvato come default per le prossime volte. Aggancia a ogni riga la ricetta reale: in stampa vanno gli ingredienti e la scadenza di quella ricetta.
            </IonNote>
            {collDraft.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>
                Lista vuota. Usa "Aggiungi riga" per creare la lista pre-stampa.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {collDraft.map((r, idx) => {
                  const linkedRecipe = r.recipe_id ? recipes.find((x) => x.id === r.recipe_id) : undefined;
                  return (
                  <div key={r.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        value={r.name}
                        placeholder="Nome piatto (es. 1° Ordinario)"
                        onChange={(e) => { const v = e.target.value; setCollDraft((p) => p.map((x, i) => (i === idx ? { ...x, name: v } : x))); }}
                        style={{ flex: 1, border: 'none', fontSize: 14, fontWeight: 600, background: 'transparent' }}
                      />
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>copie</span>
                      <input
                        inputMode="numeric"
                        value={r.suggested_copies ? String(r.suggested_copies) : ''}
                        placeholder="0"
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          const c = Number.isFinite(v) && v > 0 ? Math.min(999, v) : 0;
                          setCollDraft((p) => p.map((x, i) => (i === idx ? { ...x, suggested_copies: c } : x)));
                        }}
                        style={{ width: 52, textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, padding: '4px 0' }}
                      />
                      <button type="button" aria-label="rimuovi" onClick={() => setCollDraft((p) => p.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', display: 'flex' }}>
                        <IonIcon icon={trashOutline} />
                      </button>
                    </div>
                    {/* T-406 FIX: ricetta agganciata (in stampa va questa, con ingredienti/scadenza) + categoria */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      {r.ordinario_cat ? (
                        <span style={{ flex: 1, fontSize: 12, color: '#0284c7', fontWeight: 600 }}>🔁 Ordinario · ricetta = rotazione del giorno</span>
                      ) : (
                        <button type="button" onClick={() => setCollRecipePickFor(r.id)} style={{ flex: 1, textAlign: 'left', background: linkedRecipe ? '#eff6ff' : '#fff7ed', border: `1px solid ${linkedRecipe ? '#bfdbfe' : '#fed7aa'}`, borderRadius: 8, padding: '6px 8px', fontSize: 13, fontWeight: 600, color: linkedRecipe ? '#1e3a8a' : '#b45309', cursor: 'pointer' }}>
                          🍽 {linkedRecipe ? linkedRecipe.name : 'Scegli ricetta →'}
                        </button>
                      )}
                      <select
                        value={r.category ?? ''}
                        onChange={(e) => { const v = (e.target.value || null) as CollettivaPrelabelRow['category']; setCollDraft((p) => p.map((x, i) => (i === idx ? { ...x, category: v } : x))); }}
                        style={{ border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, padding: '6px', background: '#fff', color: '#334155' }}
                      >
                        <option value="">Categoria…</option>
                        <option value="PRIMI">Primi</option>
                        <option value="SECONDI">Secondi</option>
                        <option value="CONTORNI">Contorni</option>
                        <option value="FRUTTA">Frutta</option>
                        <option value="ALTRO">Altro</option>
                      </select>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </IonContent>
        </IonModal>

        {/* T-406 FIX: selettore ricetta per una riga della lista pre-stampa */}
        <SearchPickerModal
          isOpen={collRecipePickFor !== null}
          title="Scegli la ricetta"
          items={recipes
            // T-441: nascondi le varianti dieta (ricette figlie) dal picker collettiva.
            .filter((r) => !r.is_food_cost_only && r.variant_of_recipe_id == null)
            .map((r) => ({ id: r.id, label: r.name }))}
          searchPlaceholder="Cerca ricetta…"
          emptyHint="Nessuna ricetta in anagrafica."
          onSelect={(id) => { setCollDraft((p) => p.map((x) => (x.id === collRecipePickFor ? { ...x, recipe_id: id } : x))); setCollRecipePickFor(null); }}
          onClose={() => setCollRecipePickFor(null)}
        />

        {/* ───── modal preparazione ───── */}
        <IonModal
          data-testid="prep-editor"
          isOpen={open}
          onDidDismiss={closeAll}
          onDidPresent={() => { if (mode === 'free') focusInput(prepNameRef.current); }}
        >
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={closeAll}>Annulla</IonButton>
              </IonButtons>
              <IonTitle>{mode === 'free' ? 'Etichetta veloce' : 'Nuova preparazione'}</IonTitle>
            </IonToolbar>
            <StepBar step={prepStep} total={3} labels={[singleProductLabel ? 'Prodotto' : 'Cosa prepari', singleProductLabel ? 'Alimento e lotto' : 'Ingredienti e lotti', 'Stampa']} />
          </IonHeader>
          {/* Step 3 (Stampa): scroll SEMPRE attivo. L'anteprima (raster TSPL reale)
              vive ora DENTRO lo step 3, in una colonna affiancata ai controlli su
              viewport larghi (PWA/tablet) e impilata sopra su telefono. Prima lo
              scroll era disattivato allo step 3 assumendo che tutto entrasse a
              schermo: sul modal più corto della PWA i controlli (stampante/copie/
              data) finivano tagliati e irraggiungibili. */}
          <IonContent className="te-modal-page">
            <div className="te-flow-shell">
            {prepStep === 1 && (<>
            {mode === 'free' && <IonSegment value={freeLabelKind} onIonChange={(event) => {
              const kind = event.detail.value;
              if (kind === 'product' || kind === 'preparation') setFreeLabelKind(kind);
            }}>
              <IonSegmentButton value="product"><IonLabel>Prodotto singolo</IonLabel></IonSegmentButton>
              <IonSegmentButton value="preparation"><IonLabel>Preparazione</IonLabel></IonSegmentButton>
            </IonSegment>}
            {/* Step 1: nome / ricetta */}
            <h2 style={{ margin: '4px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              🍝 Cosa stai preparando
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '4px 0', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              {mode === 'recipe' ? (
                <IonItem className={fieldClass(!!recipeId)}>
                  <PickerTriggerField
                    label="Ricetta *"
                    placeholder="Tocca per scegliere ricetta o etichetta libera"
                    value={recipes.find((r) => r.id === recipeId)?.name}
                    onClick={() => setSearchPicker({ kind: 'recipe' })}
                  />
                </IonItem>
              ) : (
                <IonItem className={fieldClass(preparationName.trim().length > 0)}>
                  <IonInput
                    ref={prepNameRef}
                    label={singleProductLabel ? "Nome prodotto *" : "Nome preparazione *"}
                    labelPlacement="stacked"
                    placeholder={singleProductLabel ? "Es. Salsiccia" : "Es. Pasta al pomodoro"}
                    value={preparationName}
                    maxlength={200}
                    counter
                    counterFormatter={(inputLength, maxLength) => `${inputLength}/${maxLength}`}
                    onIonInput={(e) => setPreparationName(String(e.detail.value ?? '').slice(0, 200))}
                  />
                </IonItem>
              )}
              {/* T-070: gestione quantità solo se magazzino ON. Con magazzino
                  OFF outputQty resta in stato (= yield ricetta) ma non si vede;
                  così commitAndPrint scrive comunque output_qty nella production. */}
              {warehouseEnabled && !singleProductLabel && (
                <IonItem className={fieldClass(!!outputQty.trim() && Number(outputQty.replace(',', '.')) > 0)}>
                  {/* T-229 (collettiva): ricetta con porzioni base → il campo
                      diventa "Porzioni prodotte" (scaling per porzione). */}
                  <IonInput
                    label={portionFlow ? 'Porzioni prodotte *' : 'Quantità prodotta *'}
                    labelPlacement="stacked"
                    type="number"
                    inputmode={portionFlow ? 'numeric' : 'decimal'}
                    value={outputQty}
                    onIonInput={(e) => setOutputQty(String(e.detail.value ?? ''))}
                  />
                  {/* T-221: con ricetta l'unità NON si sceglie — è definita
                      dalla ricetta (yield_uom) ed è il riferimento del
                      moltiplicatore ingredienti. Il select resta solo per le
                      preparazioni libere. */}
                  {mode === 'recipe' && recipeId ? (
                    <IonBadge color="primary" slot="end" style={{ fontSize: 13, padding: '6px 10px', alignSelf: 'flex-end' }}>
                      {outputUom}
                    </IonBadge>
                  ) : (
                    <IonSelect
                      slot="end"
                      value={outputUom}
                      onIonChange={(e) => setOutputUom(String(e.detail.value))}
                      interface="popover"
                    >
                      <IonSelectOption value="kg">kg</IonSelectOption>
                      <IonSelectOption value="pz">pz</IonSelectOption>
                      <IonSelectOption value="porz">porz</IonSelectOption>
                      <IonSelectOption value="l">l</IonSelectOption>
                    </IonSelect>
                  )}
                </IonItem>
              )}
              {/* T-242: rimossi i due tip in prima pagina stampa etichette
                  (richiesta utente) — l'hint 💡 "Porzioni prodotte dalla ricetta"
                  e la nota ⚖️ di scaling. Lo scaling degli ingredienti resta
                  invariato (avviene nell'useEffect "riproporziona", più sopra);
                  qui si toglie solo il testo esplicativo, non il comportamento. */}
              <IonItem
                lines="none"
                className={fieldClass(!!shelfDays.trim() && Number(shelfDays.replace(',', '.')) > 0)}
                style={{ '--background': 'transparent' } as React.CSSProperties}
              >
                <IonInput
                  label="Shelf-life (giorni) *"
                  labelPlacement="stacked"
                  type="number"
                  inputmode="numeric"
                  min={1}
                  value={shelfDays}
                  onIonInput={(e) => setShelfDays(String(e.detail.value ?? ''))}
                />
                <IonNote slot="end" style={{ fontSize: 11 }}>
                  Scade {expiresAt.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}
                </IonNote>
              </IonItem>
              {/* T-229 (collettiva): servizio del pasto — finisce su
                  productions.meal_service per la scheda di produzione. */}
              {collettivaEnabled && (
                <div style={{ padding: '8px 16px 14px' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>
                    Servizio (per la scheda di produzione)
                  </div>
                  <IonSegment
                    value={mealService || undefined}
                    onIonChange={(e) => {
                      const v = e.detail.value;
                      setMealService(v === 'pranzo' || v === 'cena' ? v : '');
                    }}
                  >
                    <IonSegmentButton value="pranzo" style={{ minHeight: 34 }}>
                      <IonLabel style={{ fontSize: 13 }}>Pranzo</IonLabel>
                    </IonSegmentButton>
                    <IonSegmentButton value="cena" style={{ minHeight: 34 }}>
                      <IonLabel style={{ fontSize: 13 }}>Cena</IonLabel>
                    </IonSegmentButton>
                  </IonSegment>
                </div>
              )}
            </div>
            </>)}

            {prepStep === 2 && (<>
            {/* Step 2: ingredienti / lotti — card "prodotto" con stripe verde laterale,
                header marcato, layout chiaro per ogni linea ingrediente. */}
            {productionLotPhotoAvailable && (
              <div style={{
                background: '#fff',
                border: '1px solid #bfdbfe',
                borderRadius: 14,
                padding: 12,
                marginBottom: 12,
                boxShadow: '0 1px 3px rgba(15,23,42,.05)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <IonIcon icon={cameraOutline} color="primary" style={{ fontSize: 20 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 750, color: '#0f172a' }}>
                      Come vuoi indicare i lotti?
                    </div>
                    <div style={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.4 }}>
                      Con le foto non devi scegliere i lotti: scatta tutto in una sola sessione e continua.
                    </div>
                  </div>
                </div>
                <IonSegment
                  value={lotSelectionMode}
                  onIonChange={(event) => {
                    const value = event.detail.value;
                    if (value === 'manual' || value === 'photo') switchLotSelectionMode(value);
                  }}
                >
                  <IonSegmentButton value="manual" style={{ minHeight: 48 }}>
                    <IonLabel>Seleziona</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="photo" style={{ minHeight: 48 }}>
                    <IonIcon icon={cameraOutline} />
                    <IonLabel>Fotografa</IonLabel>
                  </IonSegmentButton>
                </IonSegment>
                {lotSelectionMode === 'photo' && (
                  <div style={{ marginTop: 12 }}>
                    <IonButton
                      expand="block"
                      fill={photoBatch ? 'outline' : 'solid'}
                      onClick={() => setPhotoCaptureOpen(true)}
                      style={{ minHeight: 52, '--border-radius': '14px', fontWeight: 750 } as React.CSSProperties}
                    >
                      <IonIcon icon={cameraOutline} slot="start" />
                      {photoBatch ? 'Rifai la sessione fotografica' : 'Fotografa tutte le confezioni'}
                    </IonButton>
                    {photoBatch && (
                      <>
                        <div
                          role="status"
                          aria-live="polite"
                          style={{
                            marginTop: 10, padding: '10px 12px', borderRadius: 12,
                            background: '#ecfdf5', border: '1px solid #a7f3d0',
                            color: '#166534', fontSize: 12.5, lineHeight: 1.45,
                          }}
                        >
                          <b>{photoBatch.manifest.captures.length} {photoBatch.manifest.captures.length === 1 ? 'foto salvata' : 'foto salvate'}.</b>
                          {' '}Puoi continuare subito: upload, lettura AI e match avverranno dopo la stampa.
                        </div>
                        <div style={{
                          display: 'flex', gap: 8, overflowX: 'auto', marginTop: 10,
                          paddingBottom: 2,
                        }}>
                          {photoBatch.preview_data_urls.map((url, index) => (
                            <a
                              key={photoBatch.manifest.captures[index]?.capture_id ?? url.slice(0, 24)}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`Apri foto confezione ${index + 1}`}
                              style={{
                                width: 76, height: 76, flex: '0 0 auto', display: 'block',
                                borderRadius: 10, border: '1px solid #cbd5e1', overflow: 'hidden',
                              }}
                            >
                              <img
                                src={url}
                                alt={`Foto confezione ${index + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                              />
                            </a>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            {lines.length === 0 ? (
              <div style={{
                background: '#fff', borderRadius: 14, padding: '20px 16px',
                boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                textAlign: 'center', color: '#94a3b8', fontSize: 13, fontStyle: 'italic',
              }}>
                {lotSelectionMode === 'photo'
                  ? 'Aggiungi il prodotto da etichettare oppure gli ingredienti della preparazione.'
                  : singleProductLabel ? 'Scegli l’alimento da etichettare e indica il lotto o fotografa la confezione.' : 'Nessun ingrediente. Aggiungi i lotti che stai usando per questa preparazione.'}
              </div>
            ) : (
              lines.map((l) => {
                const candidates = recipeLotsByFood.get(l.food_id) ?? [];
                const myLot = lots.find((candidate) => candidate.key === l.lot_key);
                const allCandidates = myLot && !candidates.find((candidate) => (
                  candidate.key === myLot.key
                ))
                  ? [myLot, ...candidates]
                  : candidates;
                const food = foods.find((f) => f.id === l.food_id);
                const stockUom = food?.default_uom ?? l.uom;
                const totalAvail = foodTotalAvailable(l.food_id);
                const reqQty = Number(l.qty_used.replace(',', '.')) || 0;
                // T-240: scarico sul LOTTO scelto. `lots.available` deriva dal
                // projector ed è già al netto delle riserve documento, ma non
                // delle righe ancora nel form corrente.
                const selLot = l.lot_key
                  ? lots.find((candidate) => candidate.key === l.lot_key)
                  : undefined;
                const lotAvailable = selLot?.available ?? 0;
                const overStock = warehouseEnabled && !!l.lot_key && reqQty > lotAvailable * 1.1;
                const maxAvail = warehouseEnabled
                  ? (selLot ? lotAvailable : totalAvail)
                  : l.available + reqQty;
                const noCandidates = allCandidates.length === 0;
                const lotMissing = !l.lot_key;
                // T-070: "missing qty" è un errore solo a magazzino ON. A
                // magazzino OFF la qty proviene da ricetta, non si chiede in stampa.
                const qtyMissing = warehouseEnabled
                  && (!l.qty_used.trim() || Number(l.qty_used.replace(',', '.')) <= 0);
                const cardNeedsAttention = lotSelectionMode === 'photo'
                  ? qtyMissing
                  : lotMissing || noCandidates || overStock || qtyMissing;
                const stripeColor = cardNeedsAttention ? '#f59e0b' : '#10b981';
                return (
                  <div key={l.uid} style={{
                    display: 'flex',
                    background: '#fff', borderRadius: 14, marginBottom: 10,
                    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                    overflow: 'hidden',
                  }}>
                    {/* Stripe laterale: ambra se manca lotto/giacenza, verde se ok */}
                    <div style={{ width: 4, background: stripeColor, flexShrink: 0, alignSelf: 'stretch' }} />
                    <div style={{ flex: 1, minWidth: 0, padding: '12px 14px' }}>
                      {/* Header: nome + cestino */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.005em' }}>
                            {l.food_name}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            {lotSelectionMode === 'photo' ? (
                              <>{singleProductLabel ? 'Prodotto da etichettare' : 'Ingrediente'} · lotto analizzato dopo</>
                            ) : warehouseEnabled ? (
                              <>
                                {selLot
                                  ? <>Nel lotto: <b>{Number.isFinite(maxAvail) ? `${maxAvail.toFixed(2)} ${stockUom}` : '—'}</b></>
                                  : <>Totale alimento: <b>{totalAvail.toFixed(2)} {stockUom}</b></>}
                                {candidates.length > 0 ? ` · ${candidates.length} ${candidates.length === 1 ? 'lotto' : 'lotti'}` : ''}
                              </>
                            ) : (
                              l.base_qty != null
                                ? <>Da ricetta: <b>{l.base_qty} {stockUom}</b></>
                                : <>Unità: <b>{stockUom}</b></>
                            )}
                          </div>
                        </div>
                        {lotSelectionMode !== 'photo' && (
                          <IonButton
                            fill="clear" color="danger" size="small"
                            onClick={() => removeLine(l.uid)}
                            aria-label="Rimuovi"
                            style={{ marginTop: -6, marginInlineEnd: -8 }}
                          >
                            <IonIcon icon={trashOutline} slot="icon-only" />
                          </IonButton>
                        )}
                      </div>
                      {/* Lotto + Quantità. Se l'alimento non ha NESSUN lotto
                          (tipico: ricetta vecchia con alimento mai caricato)
                          mostra il form "primo lotto" con lo stesso schema.
                          T-221 (magazzino ON): il lotto NON si sceglie più —
                          si mostra il disponibile totale e lo scarico viene
                          ripartito FEFO sui lotti alla conferma. */}
                      <div style={{ marginTop: 8 }}>
                        {lotSelectionMode === 'photo' ? (
                          renderProductionLotPhotoControls(
                            l,
                            stockUom,
                          )
                        ) : noCandidates ? (
                          <FirstLotForm
                            foodName={l.food_name}
                            busy={newLotBusy}
                            askQty={warehouseEnabled}
                            uom={stockUom}
                            onSubmit={(b, e, q) => { void handleLineFirstLot(l.uid, l.food_id, b, e, q); }}
                          />
                        ) : (
                          <>
                            {/* T-240: selettore lotto SEMPRE visibile (anche a
                                magazzino ON): in produzione lo chef sceglie e
                                ricontrolla il lotto da cui scarica. */}
                            <IonItem
                              lines="full"
                              className={lotMissing ? 'te-field-missing' : 'te-field-ok'}
                              style={{ '--background': 'transparent' } as React.CSSProperties}
                            >
                              <PickerTriggerField
                                label="Lotto *"
                                wrapValue
                                placeholder="Tocca per scegliere il lotto"
                                value={lotTriggerValue(allCandidates.find((c) => c.key === l.lot_key))}
                                onClick={() => setSearchPicker({ kind: 'lineLot', uid: l.uid, foodId: l.food_id })}
                              />
                            </IonItem>
                            {/* T-070: input qty visibile SOLO a magazzino ON.
                                Con magazzino OFF il `qty_used` resta in stato
                                (= base_qty da ricetta) per finire in productions.inputs. */}
                            {warehouseEnabled && (
                              <>
                                <IonItem
                                  lines="none"
                                  className={qtyMissing || overStock ? 'te-field-missing' : 'te-field-ok'}
                                  style={{ '--background': 'transparent', marginTop: 8 } as React.CSSProperties}
                                >
                                  <IonInput
                                    label="Quantità usata *"
                                    labelPlacement="stacked"
                                    type="number"
                                    inputmode="decimal"
                                    value={l.qty_used}
                                    onIonInput={(e) => changeLineQty(l.uid, String(e.detail.value ?? ''))}
                                  />
                                  <IonBadge color="primary" slot="end" style={{ fontSize: 13, padding: '6px 10px', alignSelf: 'flex-end' }}>
                                    {stockUom}
                                  </IonBadge>
                                </IonItem>
                                {overStock && (
                                  <div style={{ fontSize: 11.5, color: '#b45309', marginTop: 6 }}>
                                    ⚠️ Supera la giacenza del lotto scelto oltre la tolleranza del 10%:
                                    riduci la quantità, scegli un altro lotto o aggiungi una riga con un secondo lotto.
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {/* Step 2.5: AGGIUNGI INGREDIENTE — CTA collassabile evidente.
                Stato chiuso: bottone tratteggiato con "+ Aggiungi ingrediente"
                Stato aperto: form a card con bordo blu pieno per indicare azione attiva. */}
            {(!pickerOpen ? (
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  width: '100%', marginTop: 8,
                  background: '#eff6ff', color: '#0066cc',
                  border: '2px dashed #93c5fd', borderRadius: 14,
                  padding: '16px 12px',
                  fontSize: 15, fontWeight: 700, cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <span style={{
                  width: 28, height: 28, borderRadius: 14,
                  background: '#0066cc', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, fontWeight: 700, lineHeight: 1,
                }}>+</span>
                {singleProductLabel ? 'Scegli alimento' : 'Aggiungi ingrediente'}
              </button>
            ) : (
              <div style={{
                background: '#fff', borderRadius: 14, marginTop: 8,
                border: '2px solid #0066cc',
                boxShadow: '0 4px 12px rgba(0,102,204,.15)',
                overflow: 'hidden',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 14px',
                  background: '#0066cc', color: '#fff',
                  fontWeight: 700, fontSize: 14,
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <IonIcon icon={add} style={{ fontSize: 18 }} />
                    {singleProductLabel ? 'Scegli alimento' : 'Aggiungi ingrediente'}
                  </span>
                  <IonButton
                    fill="clear" size="small"
                    onClick={() => { setPickerOpen(false); setPickFoodId(''); setPickLotKey(''); setPickQty(''); }}
                    aria-label="Chiudi"
                    style={{ '--color': '#fff', margin: 0 } as React.CSSProperties}
                  >
                    <IonIcon icon={closeOutline} slot="icon-only" />
                  </IonButton>
                </div>
                <div style={{ padding: '4px 0' }}>
                  <IonItem
                    lines="full"
                    className={pickFoodId ? 'te-field-ok' : 'te-field-missing'}
                    style={{ '--background': 'transparent' } as React.CSSProperties}
                  >
                    <PickerTriggerField
                      label="Alimento *"
                      placeholder="Tocca per cercare un alimento"
                      value={foods.find((f) => f.id === pickFoodId)?.name}
                      onClick={() => setSearchPicker({ kind: 'food' })}
                    />
                  </IonItem>
                  {pickFoodId && (() => {
                    const pickFood = foods.find((f) => f.id === pickFoodId);
                    const pickUom = pickFood?.default_uom ?? '';
                    const noLots = availableLotsForPick.length === 0;
                    if (noLots && lotSelectionMode !== 'photo') {
                      // Nessun lotto per questo alimento → form "primo lotto"
                      // riusabile (piano base: presenza, qty=0; magazzino ON:
                      // chiede anche la quantità caricata — T-221).
                      return (
                        <div style={{ padding: '0 14px' }}>
                          <FirstLotForm
                            foodName={pickFood?.name ?? 'questo alimento'}
                            busy={newLotBusy}
                            askQty={warehouseEnabled}
                            uom={pickUom}
                            onSubmit={(b, e, q) => { void handlePickerFirstLot(b, e, q); }}
                          />
                        </div>
                      );
                    }
                    const pickTotal = foodTotalAvailable(pickFoodId);
                    return (
                      <>
                        {/* T-240: si sceglie sempre il LOTTO esplicito (anche a
                            magazzino ON): in produzione lo chef deve confermare
                            da quale lotto attinge. Disponibile totale mostrato
                            come contesto. */}
                        {lotSelectionMode !== 'photo' && <IonItem
                          lines="full"
                          className={pickLotKey ? 'te-field-ok' : 'te-field-missing'}
                          style={{ '--background': 'transparent' } as React.CSSProperties}
                        >
                          <PickerTriggerField
                            label="Lotto disponibile *"
                            wrapValue
                            placeholder="Tocca per scegliere il lotto"
                            value={lotTriggerValue(availableLotsForPick.find((c) => c.key === pickLotKey))}
                            onClick={() => setSearchPicker({ kind: 'pickLot' })}
                          />
                        </IonItem>}
                        {warehouseEnabled && (
                          <div style={{ fontSize: 11.5, color: '#64748b', padding: '0 14px 2px' }}>
                            Disponibile totale alimento: <b>{pickTotal.toFixed(2)} {pickUom}</b> su {availableLotsForPick.length} {availableLotsForPick.length === 1 ? 'lotto' : 'lotti'}
                          </div>
                        )}
                        <IonItem
                          lines="none"
                          className={pickQty.trim() ? 'te-field-ok' : 'te-field-missing'}
                          style={{ '--background': 'transparent' } as React.CSSProperties}
                        >
                          <IonInput
                            label="Quantità *"
                            labelPlacement="stacked"
                            type="number"
                            inputmode="decimal"
                            value={pickQty}
                            onIonInput={(e) => setPickQty(String(e.detail.value ?? ''))}
                          />
                          <IonBadge color="primary" slot="end" style={{ fontSize: 13, padding: '6px 10px' }}>
                            {pickUom || '—'}
                          </IonBadge>
                        </IonItem>
                        <div style={{ padding: '12px 14px' }}>
                          <IonButton
                            expand="block" fill="solid" color="primary"
                            disabled={!pickFoodId || (lotSelectionMode !== 'photo' && !pickLotKey) || !pickQty}
                            onClick={() => { addLineFromPicker(); }}
                            style={{ '--border-radius': '12px', height: 48, fontWeight: 700 } as React.CSSProperties}
                          >
                            <IonIcon icon={add} slot="start" />
                            Conferma e aggiungi
                          </IonButton>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            ))}

            </>)}

            {prepStep === 3 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            {/* Colonna anteprima — affiancata ai controlli su viewport larghi
                (PWA/tablet), impilata sopra su telefono (flex-wrap, basis 300px). */}
            <div style={{ flex: '1 1 300px', minWidth: 0 }}>
              <InlineLabelPreview
                template={template}
                data={previewData}
                copies={copies}
                rasterOptions={labelRasterOptions}
                onZoom={() => setShowVisual(true)}
              />
              <div style={{ textAlign: 'center', marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
                Template: {template.name} · {template.width_mm}×{template.height_mm} mm
              </div>
            </div>
            {/* Colonna controlli stampa */}
            <div style={{ flex: '1 1 320px', minWidth: 0 }}>
            {/* Step 3: stampante / copie */}
            <h2 style={{ margin: '0 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              🖨️ Stampa
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '4px 0', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              {/* T-104: bordo rosso quando l'anteprima è selezionata, così
                  l'utente vede a colpo d'occhio che NON sta puntando alla
                  sua stampante reale. */}
              <IonItem
                lines="full"
                className={fieldClass(!!printerId)}
                style={{
                  '--background': printerId === PREVIEW_PRINTER_ID ? '#fef2f2' : 'transparent',
                  '--border-color': printerId === PREVIEW_PRINTER_ID ? '#dc2626' : undefined,
                } as React.CSSProperties}
              >
                <PickerTriggerField
                  label="Stampante *"
                  placeholder="Tocca per scegliere la stampante"
                  wrapValue
                  value={(() => {
                    const p = printers.find((x) => x.id === printerId);
                    if (!p) return undefined;
                    if (p.driver === 'preview') return `⚠️ ${p.name} — non stampa fisicamente`;
                    return `${p.name} (${p.driver})`;
                  })()}
                  onClick={() => setSearchPicker({ kind: 'printer' })}
                />
              </IonItem>
              <PreviewWarningBanner visible={printerId === PREVIEW_PRINTER_ID} />
              <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonInput
                  label="Copie"
                  labelPlacement="stacked"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  max={collettivaEnabled ? '500' : '20'}
                  value={copiesInput}
                  onIonInput={(e) => {
                    const rawValue = String(e.detail.value ?? '');
                    setCopiesInput(rawValue);
                    const parsed = parseCopyCountInput(rawValue, collettivaEnabled ? 500 : 20);
                    if (parsed !== null) setCopies(parsed);
                  }}
                  aria-invalid={parseCopyCountInput(copiesInput, collettivaEnabled ? 500 : 20) === null}
                />
              </IonItem>
              {parseCopyCountInput(copiesInput, collettivaEnabled ? 500 : 20) === null && (
                <IonNote color="danger" style={{ display: 'block', padding: '0 16px 10px', fontSize: 12 }}>
                  Inserisci un numero intero da 1 a {collettivaEnabled ? 500 : 20}.
                </IonNote>
              )}
              {/* T-253: data di produzione modificabile (etichette retrodatate).
                  Default = oggi; l'ora resta quella di stampa. La scadenza si
                  retrodata di conseguenza. */}
              <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Data di produzione</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    Predefinito: oggi · cambia solo per etichette retrodatate
                  </div>
                </IonLabel>
                <IonDatetimeButton datetime="prep-produced-date" slot="end" />
              </IonItem>
              <IonModal keepContentsMounted>
                <IonDatetime
                  id="prep-produced-date"
                  presentation="date"
                  locale="it-IT"
                  value={producedDate}
                  max={todayDateStr()}
                  onIonChange={(e) => {
                    const v = e.detail.value;
                    if (typeof v === 'string') setProducedDate(v.slice(0, 10));
                  }}
                />
              </IonModal>
              {/* T-229 (collettiva, flusso a porzioni): conferma visiva che le
                  copie corrispondono alle porzioni prodotte. */}
              {(() => {
                if (!collettivaEnabled || !portionFlow) return null;
                const portions = Math.round(Number(outputQty.replace(',', '.')));
                if (!Number.isFinite(portions) || portions <= 0 || copies !== portions) return null;
                return (
                  <div style={{ padding: '0 16px 10px', fontSize: 12, color: '#64748b' }}>
                    1 etichetta per porzione (collettiva)
                  </div>
                );
              })()}
              {chillingRecipe && <section style={{ padding: 16, border: '2px solid #2563eb', borderRadius: 12 }} aria-label="Conferma abbattimento">
                <h3>Temperatura al cuore a fine abbattimento</h3>
                <p>Misura con la sonda e inserisci il valore effettivo. Obiettivo della ricetta: {chillingRecipe.temp_end_c ?? 'da configurare'} °C.</p>
                <IonInput label="Temperatura misurata (°C) *" labelPlacement="stacked" inputmode="decimal" value={coreTemperature} onIonInput={e => {
                  setCoreTemperature(String(e.detail.value ?? ''));
                  if (!coreMeasuredAt) { const now = new Date(); setCoreMeasuredAt(new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0,16)); }
                }} />
                <IonInput label="Data e ora della misura *" labelPlacement="stacked" type="datetime-local" value={coreMeasuredAt} onIonInput={e => setCoreMeasuredAt(String(e.detail.value ?? ''))} />
                <p>La misura sarà riportata nel registro. Rispetta anche tempi e modalità previsti dal piano del locale.</p>
              </section>}
              {/* T-243: rimosso il blocco "Allergeni aggregati" dal fondo dello
                  step Stampa (richiesta utente). Gli allergeni restano sull'etichetta
                  e nell'anteprima reale qui sopra; aggregatedAllergens continua a
                  finire nel payload della produzione/etichetta. */}
            </div>
            </div>
            </div>
            )}
            </div>
          </IonContent>
          <IonFooter>
            <IonToolbar>
              <div style={{ display: 'flex', gap: 10, padding: '10px 12px' }}>
                <IonButton
                  fill="outline"
                  disabled={busy || prepStep === 1}
                  onClick={() => setPrepStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3) : s))}
                  style={{ flex: '0 0 38%', height: 52, '--border-radius': '14px' } as React.CSSProperties}
                >
                  ← Indietro
                </IonButton>
                {prepStep < 3 ? (
                  <IonButton
                    color="primary"
                    disabled={busy}
                    onClick={goPrepNext}
                    style={{ flex: 1, height: 52, '--border-radius': '14px', fontWeight: 700 } as React.CSSProperties}
                  >
                    Avanti →
                  </IonButton>
                ) : (() => {
                  // T-104: bottone in colore warning (arancio) quando l'utente
                  // sta per "stampare" sull'anteprima, così è chiaro che NON
                  // sta facendo una stampa reale. Colore primary solo quando
                  // c'è una stampante reale selezionata.
                  const sel = printers.find((p) => p.id === printerId);
                  const isPreview = sel?.driver === 'preview';
                  return (
                    <IonButton
                      color={isPreview ? 'warning' : 'primary'}
                      disabled={busy || parseCopyCountInput(copiesInput, collettivaEnabled ? 500 : 20) === null}
                      onClick={() => {
                        const err = validate();
                        if (err) { setToast(err); return; }
                        void commitAndPrint();
                      }}
                      // Testo più piccolo + text-transform none: "Mostra anteprima
                      // (non stampa)" è lungo e, a peso 700/uppercase, toccava i
                      // bordi arrotondati del bottone. Così sta comodo dentro.
                      style={{
                        flex: 1, height: 52, '--border-radius': '14px',
                        fontWeight: 700, fontSize: '0.82rem', textTransform: 'none',
                        '--padding-start': '8px', '--padding-end': '8px',
                      } as React.CSSProperties}
                    >
                      {busy ? <IonSpinner name="dots" /> : (
                        <>
                          <IonIcon icon={isPreview ? eyeOutline : printOutline} slot="start" />
                          {isPreview ? 'Mostra anteprima (non stampa)' : 'Conferma e stampa'}
                        </>
                      )}
                    </IonButton>
                  );
                })()}
              </div>
            </IonToolbar>
          </IonFooter>
        </IonModal>

        {/* FoodEditorModal: creazione "al volo" di un alimento dal picker
            ingrediente, senza uscire dalla preparazione. Al salvataggio viene
            auto-selezionato e si apre il form "primo lotto" inline. */}
        <FoodEditorModal
          isOpen={pickFoodEditorOpen && canCreateLabelFood}
          creationSource="quick_label"
          // Qui oggi si crea soltanto; il lock rende comunque fail-closed un
          // futuro riuso in modifica. Il cambio UdM di un food esistente deve
          // passare dal rebase atomico in Magazzino.
          lockDefaultUom
          onDismiss={() => setPickFoodEditorOpen(false)}
          onSaved={handlePickFoodCreated}
        />

        <ProductionLotCaptureModal
          isOpen={photoCaptureOpen}
          onDismiss={() => setPhotoCaptureOpen(false)}
          onSave={saveProductionLotPhotoBatch}
        />

        {/* Selettore ricerca: RICETTA (step 1) */}
        <SearchPickerModal
          isOpen={searchPicker?.kind === 'recipe'}
          title="Scegli una ricetta"
          searchPlaceholder="Cerca ricetta…"
          selectedId={recipeId}
          emptyHint="Nessuna ricetta nel sistema. Creane una in HACCP → Ricette."
          primaryAction={{
            label: 'Etichetta veloce (senza ricetta)',
            onClick: () => {
              abandonProductionLotPhotoDraft();
              setMode('free');
              setFreeLabelKind('product');
              setRecipeId('');
              setLines([]);
              setSearchPicker(null);
            },
          }}
          items={recipes
            // T-337: escludi le ricette di solo food cost dal picker di preparazione.
            // T-441: escludi anche le varianti dieta (ricette figlie).
            .filter((r) => !r.archived_at && !r.is_food_cost_only && r.variant_of_recipe_id == null)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((r) => ({
              id: r.id,
              label: r.name,
              sublabel: `${r.ingredients?.length ?? 0} ingred. · ${r.yield_qty} ${r.yield_uom}`,
              badge: favoriteIds.has(r.id) ? '★' : undefined,
            }))}
          onSelect={(id) => { setMode('recipe'); loadFromRecipe(id); setSearchPicker(null); }}
          onClose={() => setSearchPicker(null)}
        />

        {/* Selettore ricerca: ALIMENTO (picker ingrediente).
            "Crea nuovo alimento" è l'azione primaria in cima. */}
        <SearchPickerModal
          isOpen={searchPicker?.kind === 'food'}
          title="Scegli un alimento"
          wrapItems
          focusSearchOnOpen
          searchPlaceholder="Cerca alimento…"
          selectedId={pickFoodId}
          emptyHint={canCreateLabelFood
            ? 'Nessun alimento. Creane uno con il pulsante verde qui sopra.'
            : 'Nessun alimento disponibile. Carica prima un DDT dalla scheda Documenti.'}
          primaryAction={canCreateLabelFood ? {
            label: 'Crea nuovo alimento (non in lista)',
            onClick: () => { setSearchPicker(null); setPickFoodEditorOpen(true); },
          } : undefined}
          items={foods
            .filter((f) => !f.archived_at && !isAutoMaterialDerivedFood(f))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((f) => ({
              id: f.id,
              label: f.name,
              badge: (recipeLotsByFood.get(f.id)?.length ?? 0) > 0 ? undefined : 'nessun lotto',
            }))}
          onSelect={(id) => { setPickFoodId(id); setPickLotKey(''); setSearchPicker(null); }}
          onClose={() => setSearchPicker(null)}
        />

        {/* Selettore ricerca: LOTTO su una riga ingrediente esistente */}
        {searchPicker?.kind === 'lineLot' && (() => {
          const ctx = searchPicker;
          const line = lines.find((l) => l.uid === ctx.uid);
          const cands = recipeLotsByFood.get(ctx.foodId) ?? [];
          const selectedId = line?.lot_key;
          const myLot = selectedId
            ? lots.find((candidate) => candidate.key === selectedId)
            : undefined;
          const all = myLot && !cands.find((candidate) => candidate.key === myLot.key)
            ? [myLot, ...cands]
            : cands;
          return (
            <SearchPickerModal
              isOpen
              title="Scegli il lotto"
              preserveOrder
              wrapItems
              searchPlaceholder="Cerca lotto…"
              selectedId={selectedId}
              emptyHint="Nessun lotto disponibile per questo alimento."
              items={all.map(lotToItem)}
              onSelect={(id) => { changeLineLot(ctx.uid, id); setSearchPicker(null); }}
              onClose={() => setSearchPicker(null)}
            />
          );
        })()}

        {/* Selettore ricerca: LOTTO nel picker "aggiungi ingrediente" */}
        <SearchPickerModal
          isOpen={searchPicker?.kind === 'pickLot'}
          title="Scegli il lotto"
          preserveOrder
          wrapItems
          searchPlaceholder="Cerca lotto…"
          selectedId={pickLotKey}
          emptyHint="Nessun lotto disponibile per questo alimento."
          items={availableLotsForPick.map(lotToItem)}
          onSelect={(id) => { setPickLotKey(id); setSearchPicker(null); }}
          onClose={() => setSearchPicker(null)}
        />

        {/* Selettore ricerca: STAMPANTE (step Stampa).
            T-104: item "Anteprima" marcato con ⚠️ e sublabel esplicita —
            altrimenti l'utente che cerca "stampante" potrebbe non distinguerla
            visivamente dalle stampanti reali. */}
        <SearchPickerModal
          isOpen={searchPicker?.kind === 'printer'}
          title="Scegli la stampante"
          searchPlaceholder="Cerca stampante…"
          selectedId={printerId}
          emptyHint="Nessuna stampante configurata."
          items={printers.map((p) => {
            const isPreview = p.driver === 'preview';
            return {
              id: p.id,
              label: isPreview ? `⚠️ ${p.name}` : p.name,
              sublabel: isPreview ? 'NON STAMPA fisicamente · solo anteprima a schermo' : p.driver,
            };
          })}
          onSelect={(id) => { setPrinterId(id); setSearchPicker(null); }}
          onClose={() => setSearchPicker(null)}
        />

        {/* Selettore ricerca: LOTTO materia prima */}
        <SearchPickerModal
          isOpen={searchPicker?.kind === 'materialLot'}
          title="Scegli il lotto"
          preserveOrder
          wrapItems
          searchPlaceholder="Cerca lotto…"
          selectedId={materialLotKey}
          emptyHint="Nessun lotto disponibile."
          items={materialLots.map(lotToItem)}
          onSelect={(id) => { setMaterialLotKey(id); setSearchPicker(null); }}
          onClose={() => setSearchPicker(null)}
        />

        {/* Selettore ricerca: LOTTO PRECEDENTE (rimanenze, piano base) */}
        {searchPicker?.kind === 'materialPrevLot' && (() => {
          const activeLot = materialFood?.current_batch_lot ?? null;
          const others = materialLots.filter((l) => l.batch_lot !== activeLot);
          return (
            <SearchPickerModal
              isOpen
              title="Lotto precedente"
              preserveOrder
              wrapItems
              searchPlaceholder="Cerca lotto storico…"
              selectedId={materialLotKey}
              emptyHint="Nessun lotto storico."
              items={others.map(lotToItem)}
              onSelect={(id) => { setMaterialLotKey(id); setSearchPicker(null); }}
              onClose={() => setSearchPicker(null)}
            />
          );
        })()}

        {/* Selettore ricerca: STAMPANTE materia prima.
            T-104: stesso pattern del picker stampante preparazione. */}
        <SearchPickerModal
          isOpen={searchPicker?.kind === 'materialPrinter'}
          title="Scegli la stampante"
          searchPlaceholder="Cerca stampante…"
          selectedId={materialPrinterId}
          emptyHint="Nessuna stampante configurata."
          items={printers.map((p) => {
            const isPreview = p.driver === 'preview';
            return {
              id: p.id,
              label: isPreview ? `⚠️ ${p.name}` : p.name,
              sublabel: isPreview ? 'NON STAMPA fisicamente · solo anteprima a schermo' : p.driver,
            };
          })}
          onSelect={(id) => { setMaterialPrinterId(id); setSearchPicker(null); }}
          onClose={() => setSearchPicker(null)}
        />

        {/* T-099: Selettore ricerca: LOTTO di una preparazione congelata.
            L'utente ha tappato un articolo aggregato (es. "Ragù della
            domenica"), che ha N production diverse in vita. Qui sceglie
            quale lotto specifico scongelare (data + LP + scadenza). */}
        <SearchPickerModal
          isOpen={searchPicker?.kind === 'materialProductionLot'}
          title={materialProductionGroupName || 'Scegli il lotto'}
          preserveOrder
          wrapItems
          searchPlaceholder="Cerca per data, lotto LP…"
          selectedId={materialProductionId}
          emptyHint="Nessun lotto disponibile per questo articolo."
          items={materialProductionGroupLots.map((p) => {
            const prod = p.produced_at ? new Date(p.produced_at) : null;
            const exp = p.expires_at ? new Date(p.expires_at) : null;
            const parts: string[] = [];
            if (prod) parts.push(`prep. ${prod.toLocaleDateString('it-IT')}`);
            if (exp) parts.push(`scade ${exp.toLocaleDateString('it-IT')}`);
            const traceShort = p.trace_token?.slice(0, 8);
            return {
              id: p.id,
              label: prod
                ? prod.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short', year: '2-digit' })
                : 'Lotto senza data',
              details: parts,
              badge: traceShort,
            };
          })}
          onSelect={(id) => {
            setMaterialProductionId(id);
            setSearchPicker(null);
          }}
          onClose={() => setSearchPicker(null)}
        />

        {/* ───── ANTEPRIMA VISIVA REALE (no stampa, no scarico) ─────
            Render del template identico a quello inviato in stampa
            (stesso renderer + QR reale). Permette di vedere "come viene"
            senza stampare un'etichetta. */}
        <IonModal isOpen={showVisual} onDidDismiss={() => setShowVisual(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Anteprima stampa</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowVisual(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent style={{ '--background': '#0f172a' } as React.CSSProperties}>
            <div style={{
              minHeight: '100%', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              padding: '24px 16px', gap: 16,
            }}>
              <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
                {template.name} · {template.width_mm}×{template.height_mm} mm
              </div>
              <div style={{
                background: '#fff', borderRadius: 10, padding: 16,
                boxShadow: '0 8px 32px rgba(0,0,0,.5)',
                maxWidth: '100%', overflow: 'auto',
              }}>
                <TsplLabelPreview
                  template={template}
                  data={materialOpen ? materialPreviewData : previewData}
                  rasterOptions={labelRasterOptions}
                />
              </div>
              <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', maxWidth: 320, margin: 0 }}>
                Stesso renderer dei driver raster, con il profilo della stampante TSPL selezionata.
                Il QR prima della conferma è un esempio; il codice definitivo viene generato alla stampa.
                La qualità fisica dipende dalla stampante e dalla carta.
              </p>
              <IonButton onClick={() => setShowVisual(false)} style={{ '--border-radius': '12px' } as React.CSSProperties}>
                Chiudi
              </IonButton>
            </div>
          </IonContent>
        </IonModal>

        {/* ───── ANTEPRIMA IN-APP della stampa virtuale ───── */}
        {/* Modal mostrato dopo "Conferma e mostra anteprima" quando la stampante
            selezionata ha driver "preview". Riceve il PNG dal canvas renderer
            via evento window 'trackeat:preview'. */}
        <IonModal isOpen={!!inAppPreview} onDidDismiss={() => setInAppPreview(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Anteprima stampa</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setInAppPreview(null)}><IonIcon icon={closeOutline} /></IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent style={{ '--background': '#0f172a' } as React.CSSProperties}>
            {inAppPreview && (
              <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', gap: 16 }}>
                <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
                  {inAppPreview.templateName} · {inAppPreview.widthMm}×{inAppPreview.heightMm} mm · {inAppPreview.copies}× cop{inAppPreview.copies > 1 ? 'ie' : 'ia'}
                </div>
                <div style={{
                  background: '#fff', borderRadius: 10, padding: 20,
                  boxShadow: '0 8px 32px rgba(0,0,0,.5)',
                  width: 'min(calc(100vw - 32px), 480px)',
                }}>
                  <img
                    src={inAppPreview.png}
                    alt="Etichetta"
                    style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 2 }}
                  />
                </div>
                <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', maxWidth: 320, margin: 0 }}>
                  Rendering reale del job: questo è esattamente ciò che invierebbe la stampante fisica.
                </p>
                <IonButton onClick={() => setInAppPreview(null)} style={{ '--border-radius': '12px' } as React.CSSProperties}>
                  Chiudi
                </IonButton>
              </div>
            )}
          </IonContent>
        </IonModal>

        {/* ───── PAGINA TRACCIABILITÀ in-app (no Chrome esterno) ───── */}
        <IonModal isOpen={!!inAppTraceUrl} onDidDismiss={() => setInAppTraceUrl(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Pagina di tracciabilità</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setInAppTraceUrl(null)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            {inAppTraceUrl && (
              <iframe
                src={inAppTraceUrl}
                title="Pagina di tracciabilità"
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
            )}
          </IonContent>
        </IonModal>

        {/* ───── modal MATERIA PRIMA: scongelamento o apertura confezione ───── */}
        <IonModal
          data-testid="material-editor"
          isOpen={materialOpen}
          onDidDismiss={() => { setMaterialOpen(false); resetMaterial(); }}
        >
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={() => { setMaterialOpen(false); resetMaterial(); }}>Annulla</IonButton>
              </IonButtons>
              <IonTitle>Materia prima</IonTitle>

            </IonToolbar>
            <StepBar
              // T-095: step 2 = scelta oggetto (preparazione congelata o alimento).
              // Si avanza a step 3 quando l'utente ha selezionato ALMENO una delle
              // due fonti (production OR food). Niente "stuck su step 2" se l'altra
              // resta vuota.
              step={!materialAction ? 1 : (!materialFoodId && !materialProductionId) ? 2 : 3}
              total={3}
              labels={['Azione', materialAction === 'open' ? 'Cosa apri' : 'Cosa scongeli', 'Validità e stampa']}
            />
          </IonHeader>
          <IonContent className="te-modal-page">
            {/* T-503 follow-up: l'anteprima non è più sticky né aperta di
                default. Su schermi piccoli il vecchio blocco copriva i campi
                essenziali dello step 3. La preview resta disponibile tramite
                disclosure esplicita e continua ad aggiornarsi in tempo reale. */}
            {materialAction && (materialFood || materialProduction) && (() => {
              const hrs = Number(materialShelfHours.replace(',', '.'));
              const validShelfLife = Number.isFinite(hrs) && hrs > 0;
              const exp = new Date(Date.now() + (validShelfLife ? hrs : 0) * 3_600_000);
              const actionLabel = materialAction === 'thaw' ? 'scongelato' : 'aperto';
              const lot = materialLots.find((l) => l.key === materialLotKey);
              // Nome + ingredienti + allergeni: se source='production', li
              // prendiamo dal parent (preserva la traccia HACCP); altrimenti
              // dal food crudo (flusso storico).
              const product = materialSource === 'production' && materialProduction
                ? `${materialProduction.output_name} (${actionLabel})`
                : `${materialFood?.name ?? ''} (${actionLabel})`;
              const ingredients = materialSource === 'production' && materialProduction
                ? (materialProduction.inputs ?? [])
                    .map((i) => ingName(i.food_id, i.food_name ?? ''))
                    .filter((s) => s.length > 0)
                : materialFood ? [ingName(materialFood.id, materialFood.name)] : [];
              const allergens = materialSource === 'production' && materialProduction
                ? (materialProduction.allergens_aggregated ?? [])
                : (lot?.allergens ?? materialFood?.allergens ?? []);
              const lotCode = materialSource === 'production' && materialProduction
                ? (materialProduction.trace_token?.slice(0, 8) ?? null)
                : (lot?.batch_lot ?? materialFood?.current_batch_lot ?? null);
              return (
                <div style={{ background: 'var(--te-canvas, #eef2f7)', padding: '8px 16px 4px' }}>
                  {validShelfLife ? (
                    <>
                      <button
                        type="button"
                        aria-expanded={materialPreviewExpanded}
                        aria-controls="material-label-preview"
                        onClick={() => setMaterialPreviewExpanded((open) => !open)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                          width: '100%', minHeight: 46,
                          background: 'var(--te-surface, #fff)', color: 'var(--te-text, #0f172a)',
                          border: '1px solid var(--te-border, #cbd5e1)', borderRadius: 12,
                          padding: '10px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                        }}
                      >
                        <IonIcon icon={eyeOutline} style={{ fontSize: 18 }} />
                        {materialPreviewExpanded ? 'Nascondi anteprima' : 'Mostra anteprima etichetta'}
                        <span style={{ marginLeft: 'auto', color: 'var(--te-text-muted, #64748b)', fontSize: 12 }}>
                          {materialCopies}×
                        </span>
                      </button>
                      {materialPreviewExpanded && (
                        <div id="material-label-preview" style={{ marginTop: 8 }}>
                          <LabelTextPreview
                            product={product}
                            producedAt={new Date()}
                            expiresAt={exp}
                            lot={lotCode}
                            allergens={allergens}
                            ingredients={ingredients}
                            copies={materialCopies}
                          />
                          <IonButton
                            expand="block"
                            fill="clear"
                            onClick={() => setShowVisual(true)}
                            style={{ marginTop: 4, minHeight: 44, textTransform: 'none' }}
                          >
                            <IonIcon icon={eyeOutline} slot="start" />
                            Ingrandisci anteprima
                          </IonButton>
                        </div>
                      )}
                    </>
                  ) : (
                    <IonItem color="warning" lines="none" style={{ '--border-radius': '12px' } as React.CSSProperties}>
                      <IonLabel className="ion-text-wrap">
                        <strong>Shelf life da configurare</strong>
                        <p>Inserisci la validità qui sotto. Non viene applicato alcun valore automatico non verificato.</p>
                      </IonLabel>
                    </IonItem>
                  )}
                </div>
              );
            })()}
            <div className="te-flow-shell">
            {!materialAction ? (
              // Step 1: scelta azione
              <div className="te-workflow-grid">
                <button
                  type="button"
                  onClick={() => setMaterialAction('thaw')}
                  className="te-workflow-card te-workflow-card--cold"
                >
                  <span className="te-workflow-card__icon"><IonIcon icon={snowOutline} /></span>
                  <span className="te-workflow-card__title">Scongelamento</span>
                  <span className="te-workflow-card__description">
                    Sposto un lotto da congelato a fresco
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setMaterialAction('open')}
                  className="te-workflow-card te-workflow-card--warm"
                >
                  <span className="te-workflow-card__icon"><IonIcon icon={archiveOutline} /></span>
                  <span className="te-workflow-card__title">Apertura confezione</span>
                  <span className="te-workflow-card__description">
                    Apro un barattolo, una vaschetta, un sacchetto
                  </span>
                </button>
              </div>
            ) : (!materialFoodId && !materialProductionId) ? (
              // Step 2: scelta dell'oggetto da scongelare / aprire.
              // T-095: per il thaw mostriamo un segment a 2 source:
              //   1. 🧊 Preparazioni congelate (productions, NEW)
              //   2. 📦 Materia prima surgelata (foods, flusso storico)
              // Per 'open' restiamo sul solo flusso foods (non si "apre" una
              // preparazione del cuoco).
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <IonButton fill="clear" size="small" onClick={() => setMaterialAction(null)}>
                    ← Indietro
                  </IonButton>
                  <h3 style={{ margin: 0, fontSize: 14 }}>
                    {materialAction === 'thaw' ? 'Cosa stai scongelando?' : 'Scegli alimento da aprire'}
                  </h3>
                </div>

                <IonButton expand="block" onClick={() => { void scanMaterialLabelQr(); }} disabled={materialScanBusy} style={{ whiteSpace: 'normal', height: 'auto', minHeight: 48 }}>
                  {materialScanBusy ? <IonSpinner name="dots" slot="start" /> : <IonIcon icon={qrCodeOutline} slot="start" />}
                  {materialAction === 'thaw' ? 'Scansiona il QR del prodotto da scongelare' : 'Scansiona il QR della confezione da aprire'}
                </IonButton>
                <IonNote>Inquadra un’etichetta TrackEAT già stampata oppure scegli il prodotto qui sotto.</IonNote>

                {materialAction === 'thaw' && (
                  <>
                    {/* T-099: label cortissime — su iPhone SE/Mini quelle precedenti
                        ("Preparazioni" / "Materia prima") si tagliavano. La legenda
                        sotto chiarisce semanticamente cosa significano. */}
                    <IonSegment
                      value={materialSource}
                      onIonChange={(e) => {
                        const v = String(e.detail.value ?? 'food') as 'production' | 'food';
                        setMaterialSource(v);
                      }}
                      style={{ marginBottom: 4 }}
                    >
                      <IonSegmentButton value="production">
                        <IonLabel>
                          🧊 Prep.
                          {frozenGroups.length > 0 && (
                            <IonBadge color="primary" style={{ marginLeft: 6, fontSize: 10 }}>
                              {frozenGroups.length}
                            </IonBadge>
                          )}
                        </IonLabel>
                      </IonSegmentButton>
                      <IonSegmentButton value="food">
                        <IonLabel>📦 M. prima</IonLabel>
                      </IonSegmentButton>
                    </IonSegment>
                    <IonNote style={{ display: 'block', padding: '0 4px 12px', fontSize: 11, color: '#64748b' }}>
                      {materialSource === 'production'
                        ? 'Preparazioni con Congelamento attivo nella ricetta.'
                        : 'Materia prima surgelata acquistata dal fornitore.'}
                    </IonNote>
                  </>
                )}

                <IonSearchbar
                  value={materialFoodSearch}
                  onIonInput={(event) => setMaterialFoodSearch(String(event.detail.value ?? ''))}
                  placeholder={materialAction === 'thaw' ? 'Cerca prodotto da scongelare' : 'Cerca alimento da aprire'}
                  aria-label={materialAction === 'thaw' ? 'Cerca prodotto da scongelare' : 'Cerca alimento da aprire'}
                  showClearButton="focus"
                  debounce={0}
                  style={{
                    '--background': '#fff',
                    '--border-radius': '12px',
                    paddingInline: 0,
                    paddingTop: 0,
                    paddingBottom: 8,
                  } as React.CSSProperties}
                />

                {/* Lista in base al source. */}
                {(materialAction === 'thaw' && materialSource === 'production') ? (
                  visibleFrozenGroups.length === 0 ? (
                    <IonItem lines="none" color="light">
                      <IonLabel className="ion-text-wrap" style={{ fontSize: 13 }}>
                        {materialFoodSearch.trim()
                          ? `Nessuna preparazione corrisponde a “${materialFoodSearch.trim()}”. Prova nella scheda M. prima per i prodotti acquistati.`
                          : <>Nessuna preparazione congelata ancora in vita. Quando stampi un'etichetta
                            per una ricetta con <b>Congelamento attivo</b>, ricomparirà qui pronta
                            per lo scongelamento. Per i prodotti acquistati passa a “M. prima”.</>}
                      </IonLabel>
                    </IonItem>
                  ) : (
                    /* T-099: lista AGGREGATA per nome articolo (frozenGroups).
                       Al tap su un articolo, se c'è un solo lotto vivente lo
                       seleziono direttamente; se ne ho >1 apro il SearchPicker
                       con data + LP + scadenza per ciascuno. Stesso pattern
                       della scelta lotto in "Materia prima → Apertura". */
                    <IonList className="te-entity-list">
                      {visibleFrozenGroups.map((g) => {
                        const count = g.productions.length;
                        const newest = g.productions[0];
                        const prod = newest?.produced_at ? new Date(newest.produced_at) : null;
                        return (
                          <IonItem
                            key={g.name}
                            button
                            detail
                            onClick={() => {
                              if (count === 1 && newest) {
                                // Singolo lotto → niente picker, vai diretto.
                                setMaterialProductionId(newest.id);
                              } else {
                                // Più lotti dello stesso articolo → apri picker per scelta.
                                setMaterialProductionGroupName(g.name);
                                setSearchPicker({ kind: 'materialProductionLot' });
                              }
                            }}
                          >
                            <IonLabel>
                              <h3>{g.name}</h3>
                              <p style={{ fontSize: 11, color: '#64748b' }}>
                                {count} lott{count === 1 ? 'o' : 'i'} disponibil{count === 1 ? 'e' : 'i'}
                                {prod ? ` · più recente ${prod.toLocaleDateString('it-IT')}` : ''}
                              </p>
                            </IonLabel>
                          </IonItem>
                        );
                      })}
                    </IonList>
                  )
                ) : (
                  visibleMaterialFoods.length === 0 ? (
                    <IonItem lines="none" color="warning">
                      <IonLabel className="ion-text-wrap" style={{ fontSize: 13 }}>
                        {materialFoodSearch.trim()
                          ? `Nessun alimento disponibile corrisponde a “${materialFoodSearch.trim()}”.`
                          : materialAction === 'thaw'
                          ? 'Nessun alimento congelato in giacenza. Verifica il tipo di conservazione del Food in Magazzino.'
                          : 'Nessun lotto in giacenza.'}
                      </IonLabel>
                      {materialFoodSearch.trim() && (
                        <IonButton
                          slot="end"
                          fill="outline"
                          onClick={() => setMaterialFoodSearch('')}
                          aria-label="Cancella ricerca alimento"
                        >
                          Cancella
                        </IonButton>
                      )}
                    </IonItem>
                  ) : (
                    <IonList className="te-entity-list">
                      {visibleMaterialFoods.map((f) => {
                        const lotsCount = (lotsByFood.get(f.id) ?? []).length;
                        return (
                          <IonItem key={f.id} button detail onClick={() => setMaterialFoodId(f.id)}>
                            <IonLabel>
                              <h3>{f.name}</h3>
                              <p style={{ fontSize: 11, color: '#64748b' }}>
                                {lotsCount} lott{lotsCount === 1 ? 'o' : 'i'} disponibil{lotsCount === 1 ? 'e' : 'i'}
                              </p>
                            </IonLabel>
                          </IonItem>
                        );
                      })}
                    </IonList>
                  )
                )}
              </>
            ) : (
              // Step 3: form (lotto + qty + scadenza + stampante + copie).
              // T-095: anche per source='production' (preparazione congelata).
              // In quel caso saltiamo il picker lotto (la production È il lotto)
              // e mostriamo invece un banner read-only con info del parent.
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <IonButton
                    fill="clear"
                    size="small"
                    onClick={() => {
                      // T-095: torna a step 2 azzerando entrambi i source.
                      // T-099: azzera anche il gruppo articolo (per le prep).
                      setMaterialFoodId('');
                      setMaterialLotKey('');
                      setMaterialProductionId('');
                      setMaterialProductionGroupName('');
                      setMaterialPreviewExpanded(false);
                    }}
                  >
                    ← Indietro
                  </IonButton>
                  <h3 style={{ margin: 0, fontSize: 14 }}>
                    {materialAction === 'thaw' ? '❄️ Scongela' : '📭 Apri'} ·{' '}
                    {materialSource === 'production' && materialProduction
                      ? materialProduction.output_name
                      : materialFood?.name}
                  </h3>
                </div>

                {/* T-095: source='production' → banner read-only del parent
                    invece dei lot picker. La traccia HACCP è già "incollata"
                    al production parent, non c'è altro da scegliere. */}
                {materialSource === 'production' && materialProduction && (
                  <div style={{
                    background: 'linear-gradient(135deg,#dbeafe,#e0f2fe)',
                    border: '1px solid #93c5fd',
                    borderRadius: 12,
                    padding: '12px 14px',
                    marginBottom: 12,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                      {materialAction === 'thaw' ? 'Preparazione congelata' : 'Confezione da aprire'}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                      {materialProduction.output_name}
                    </div>
                    {materialProduction.produced_at && (
                      <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>
                        Preparata {new Date(materialProduction.produced_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </div>
                    )}
                    {warehouseEnabled && materialProductionStockLot && (
                      <div style={{ fontSize: 12, color: '#0f172a', marginTop: 6, fontWeight: 700 }}>
                        Disponibile: {materialProductionStockLot.available} {materialProductionStockLot.uom}
                      </div>
                    )}
                    {(materialProduction.inputs ?? []).length > 0 && (
                      <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>
                        <span style={{ color: '#94a3b8' }}>Ingredienti:</span>{' '}
                        {(materialProduction.inputs ?? []).map((i) => i.food_name).filter(Boolean).join(', ')}
                      </div>
                    )}
                    {(materialProduction.allergens_aggregated ?? []).length > 0 && (
                      <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(materialProduction.allergens_aggregated ?? []).map((a) => (
                          <span key={a} style={{
                            background: '#fef3c7', color: '#b45309',
                            fontSize: 10, fontWeight: 700,
                            padding: '2px 8px', borderRadius: 999,
                          }}>
                            {ALLERGEN_IT[a] ?? a}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Lot picker SOLO per source='food'. */}
                {materialSource === 'food' && warehouseEnabled && materialLots.length > 1 && (
                  <IonItem className={fieldClass(!!materialLotKey)}>
                    <PickerTriggerField
                      label="Lotto"
                      wrapValue
                      placeholder="Tocca per scegliere il lotto"
                      value={materialLots.find((l) => l.key === materialLotKey)?.batch_lot ?? undefined}
                      onClick={() => setSearchPicker({ kind: 'materialLot' })}
                    />
                  </IonItem>
                )}

                {materialSource === 'food' && warehouseEnabled && materialLots.length === 1 && (
                  <IonNote color="medium" style={{ display: 'block', padding: '8px 16px', fontSize: 12 }}>
                    Lotto: <strong>{materialLots[0]?.batch_lot ?? '—'}</strong> · {materialLots[0]?.available} {materialLots[0]?.uom}
                  </IonNote>
                )}

                {/* Piano base: mostra il lotto attivo + bottone per richiamare
                    un lotto precedente (rimanenze in frigo). Solo per food. */}
                {materialSource === 'food' && !warehouseEnabled && (() => {
                  const selected = materialLots.find((l) => l.key === materialLotKey);
                  const activeLot = materialFood?.current_batch_lot ?? null;
                  const isActive = selected?.batch_lot === activeLot;
                  const others = materialLots.filter((l) => l.batch_lot !== activeLot);
                  return (
                    <>
                      <IonItem lines="none">
                        <IonLabel className="ion-text-wrap">
                          <p style={{ fontSize: 11, color: '#64748b', margin: 0 }}>
                            {isActive ? 'Lotto attivo' : 'Lotto selezionato'}
                          </p>
                          <h3 style={{ margin: '2px 0', fontWeight: 600 }}>
                            {selected?.batch_lot ?? activeLot ?? '—'}
                          </h3>
                          {selected?.expires && (
                            <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>
                              Scade {new Date(selected.expires).toLocaleDateString('it-IT')}
                            </p>
                          )}
                        </IonLabel>
                      </IonItem>
                      {others.length > 0 && (
                        <IonItem lines="none">
                          <PickerTriggerField
                            label="Usa un lotto precedente (rimanenze)"
                            placeholder="Scegli un lotto storico"
                            value={isActive ? undefined : (others.find((l) => l.key === materialLotKey)?.batch_lot ?? undefined)}
                            onClick={() => setSearchPicker({ kind: 'materialPrevLot' })}
                          />
                        </IonItem>
                      )}
                      {!isActive && activeLot && (
                        <IonItem lines="none">
                          <IonButton
                            size="small"
                            fill="outline"
                            onClick={() => {
                              const found = materialLots.find((l) => l.batch_lot === activeLot);
                              if (found) setMaterialLotKey(found.key);
                            }}
                          >
                            ← Torna al lotto attivo
                          </IonButton>
                        </IonItem>
                      )}
                    </>
                  );
                })()}

                {/* T-630: quantità reale anche per le preparazioni congelate.
                    Il lotto origine conserva il residuo non scongelato. */}
                {warehouseEnabled && (
                  <IonItem>
                    <IonLabel position="stacked">
                      {materialSource === 'production'
                        ? materialAction === 'thaw' ? 'Quantità da scongelare' : 'Quantità da aprire'
                        : 'Quantità'}
                    </IonLabel>
                    <IonInput
                      type="number"
                      inputmode="decimal"
                      value={materialQty}
                      placeholder={(materialSource === 'production'
                        ? materialProductionStockLot?.available
                        : materialLots.find((l) => l.key === materialLotKey)?.available) != null
                        ? String(materialSource === 'production'
                          ? materialProductionStockLot?.available
                          : materialLots.find((l) => l.key === materialLotKey)?.available)
                        : ''}
                      onIonInput={(ev) => setMaterialQty(String(ev.detail.value ?? ''))}
                    />
                    <IonNote slot="end" color="medium">
                      {materialSource === 'production'
                        ? materialProductionStockLot?.uom ?? ''
                        : materialLots.find((l) => l.key === materialLotKey)?.uom ?? ''}
                    </IonNote>
                  </IonItem>
                )}
                {warehouseEnabled && materialSource === 'production' && materialProductionStockLot && (() => {
                  try {
                    const { remaining } = resolveMaterialOperationQuantity(
                      materialQty,
                      materialProductionStockLot.available,
                      materialProductionStockLot.uom,
                    );
                    return (
                      <IonNote color="medium" style={{ display: 'block', padding: '4px 16px 8px', fontSize: 11 }}>
                        Dopo {materialAction === 'thaw' ? 'lo scongelamento' : "l'apertura"} resteranno{' '}
                        {remaining} {materialProductionStockLot.uom} nel lotto origine.
                      </IonNote>
                    );
                  } catch {
                    return null;
                  }
                })()}

                <IonItem>
                  <IonLabel position="stacked">
                    Validità (ore)
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8' }}>
                      es. 48 = 2 giorni
                    </span>
                  </IonLabel>
                  <IonInput
                    type="number"
                    inputmode="numeric"
                    value={materialShelfHours}
                    onIonInput={(ev) => setMaterialShelfHours(String(ev.detail.value ?? ''))}
                  />
                </IonItem>
                {/* T-095: la nota smart-update vale solo per il food
                    (aggiorniamo shelf_life_*_thawed sul food master). Per la
                    preparazione congelata invece il valore vale solo per
                    questa singola etichetta — niente persistenza nel master. */}
                {materialSource === 'food' && (
                  <IonNote color="medium" style={{ display: 'block', padding: '4px 16px 8px', fontSize: 11 }}>
                    {materialFood?.recipe_id
                      ? 'Il default deriva dalla scheda ricetta. Una modifica qui vale solo per questa etichetta.'
                      : `Se modifichi questo valore, viene salvato anche come default per "${materialFood?.name ?? ''}".`}
                  </IonNote>
                )}
                {materialSource === 'production' && (
                  <IonNote color="medium" style={{ display: 'block', padding: '4px 16px 8px', fontSize: 11 }}>
                    Il default deriva dalla validità per {materialAction === 'thaw' ? 'scongelamento' : 'apertura'} della ricetta.
                    Una modifica qui vale solo per questa etichetta.
                  </IonNote>
                )}

                {/* T-104: bordo+sfondo rossi quando l'anteprima è selezionata. */}
                <IonItem
                  className={fieldClass(!!materialPrinterId)}
                  style={{
                    '--background': materialPrinterId === PREVIEW_PRINTER_ID ? '#fef2f2' : undefined,
                    '--border-color': materialPrinterId === PREVIEW_PRINTER_ID ? '#dc2626' : undefined,
                  } as React.CSSProperties}
                >
                  <PickerTriggerField
                    label="Stampante"
                    placeholder="Tocca per scegliere la stampante"
                    value={(() => {
                      const p = printers.find((x) => x.id === materialPrinterId);
                      if (!p) return undefined;
                      if (p.driver === 'preview') return `⚠️ ${p.name} — non stampa fisicamente`;
                      return p.name;
                    })()}
                    onClick={() => setSearchPicker({ kind: 'materialPrinter' })}
                  />
                </IonItem>
                <PreviewWarningBanner visible={materialPrinterId === PREVIEW_PRINTER_ID} compact />

                <IonItem>
                  <IonLabel position="stacked">Copie</IonLabel>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '4px 0' }}>
                    {[1, 2, 3, 5, 10].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setMaterialCopies(n)}
                        style={{
                          padding: '6px 14px', borderRadius: 999,
                          border: `1px solid ${materialCopies === n ? '#0066cc' : '#cbd5e1'}`,
                          background: materialCopies === n ? '#0066cc' : '#fff',
                          color: materialCopies === n ? '#fff' : '#475569',
                          fontSize: 13, cursor: 'pointer', fontWeight: 600,
                        }}
                      >
                        {n}×
                      </button>
                    ))}
                  </div>
                </IonItem>

                <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
                  {(() => {
                    // T-104: stesso pattern del wizard preparazione — bottone
                    // in colore warning quando l'anteprima è selezionata, così
                    // l'utente è avvisato che non sta stampando fisicamente.
                    const isPreview = materialPrinterId === PREVIEW_PRINTER_ID;
                    return (
                      <IonButton
                        expand="block"
                        color={isPreview ? 'warning' : 'primary'}
                        // T-095: per source='production' non è richiesto il lot key
                        // (la production È il lotto). Per 'food' resta il vincolo
                        // del lot picker + (warehouseEnabled → qty obbligatoria).
                        disabled={
                          busy ||
                          !materialPrinterId ||
                          (materialSource === 'food' && !materialLotKey) ||
                          (materialSource === 'production' && !materialProductionId) ||
                          (materialSource === 'food' && warehouseEnabled && !materialQty)
                        }
                        onClick={() => { void commitMaterialOperation(); }}
                        style={{ flex: 1, height: 52, '--border-radius': '14px', fontWeight: 700 } as React.CSSProperties}
                      >
                        {busy ? <IonSpinner name="dots" /> : (
                          <>
                            <IonIcon icon={isPreview ? eyeOutline : printOutline} slot="start" />
                            {isPreview ? 'Mostra anteprima (non stampa)' : 'Stampa etichetta'}
                          </>
                        )}
                      </IonButton>
                    );
                  })()}
                </div>
              </>
            )}
            </div>
          </IonContent>
        </IonModal>

        {/* T-101: modal "Ristampa N copie identiche".
            Mostra prodotto + LP code (immutabili) e chip per scegliere
            quante copie. La pressione di "Stampa" chiama reprintIdentical. */}
        <IonModal isOpen={!!reprintTarget} onDidDismiss={() => setReprintTarget(null)}>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={() => setReprintTarget(null)}>Annulla</IonButton>
              </IonButtons>
              <IonTitle>Ristampa N copie</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {reprintTarget && (
              <>
                <div style={{
                  background: 'linear-gradient(135deg,#f1f5f9,#e2e8f0)',
                  borderRadius: 12, padding: '14px 16px', marginBottom: 16,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                    🖨️ Stessa etichetta — copie multiple
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                    {(reprintTarget.payload.data?.product as string) ?? reprintTarget.payload.preset}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                    Lotto: <b>{reprintTarget.payload.lp_code ?? '—'}</b>
                    {reprintTarget.payload.expires_at && (
                      <> · scade {new Date(reprintTarget.payload.expires_at).toLocaleDateString('it-IT')}</>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                    Stampa più copie fisiche della STESSA etichetta (stesso lotto,
                    stesso QR). Utile per più contenitori dello stesso prodotto.
                  </div>
                </div>
                {/* T-273: selettore stampante per la ristampa (prima era
                    inchiodato alla stampante originale, spesso l'anteprima). */}
                <IonItem lines="full" style={{ marginBottom: 12 }}>
                  <IonLabel position="stacked">Stampante</IonLabel>
                  <IonSelect
                    value={reprintPrinterId}
                    placeholder="Scegli stampante"
                    onIonChange={(ev) => setReprintPrinterId(ev.detail.value as string)}
                  >
                    {printers.map((p) => (
                      <IonSelectOption key={p.id} value={p.id}>
                        {p.driver === 'preview' ? `${p.name} (solo anteprima)` : p.name}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>
                {/* Avviso se la stampante SCELTA è l'anteprima: non stampa fisicamente. */}
                <PreviewWarningBanner
                  visible={printers.find((p) => p.id === reprintPrinterId)?.driver === 'preview'}
                />
                <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 600, color: '#475569' }}>
                  Quante copie?
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                  {[1, 2, 3, 5, 10].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setReprintCopiesInput(String(n))}
                      style={{
                        padding: '10px 18px', borderRadius: 999,
                        border: `1px solid ${reprintCopies === n ? '#0066cc' : '#cbd5e1'}`,
                        background: reprintCopies === n ? '#0066cc' : '#fff',
                        color: reprintCopies === n ? '#fff' : '#475569',
                        fontSize: 15, fontWeight: 700, cursor: 'pointer',
                      }}
                    >
                      {n}×
                    </button>
                  ))}
                </div>
                {/* T-296: numero copie PERSONALIZZATO (oltre ai chip fissi) — per
                    non dipendere dai numeri preimpostati. Sincronizzato coi chip
                    (un chip attivo si riflette qui; un valore fuori-preset
                    de-seleziona i chip). Clamp 1–500 come negli altri flussi. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>o personalizzato:</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={500}
                    value={reprintCopiesInput}
                    onChange={(ev) => {
                      setReprintCopiesInput(ev.target.value);
                    }}
                    aria-label="Numero di copie personalizzato"
                    aria-invalid={reprintCopies === null}
                    style={{
                      width: 92, padding: '9px 12px', borderRadius: 10,
                      border: '1px solid #cbd5e1', fontSize: 15, fontWeight: 700,
                      color: '#0f172a', textAlign: 'center', background: '#fff',
                    }}
                  />
                  <span style={{ fontSize: 13, color: '#64748b' }}>copie</span>
                </div>
                {reprintCopies === null && (
                  <div role="alert" style={{ margin: '-16px 0 20px', fontSize: 12, color: '#b91c1c' }}>
                    Inserisci un numero intero da 1 a 500.
                  </div>
                )}
                {/* T-279: ristampa del pasto campione INTEGRATA nella ristampa
                    del prodotto — la spunta fa sì che "Stampa" emetta ANCHE
                    l'etichetta campione (1 copia). Solo per le etichette di una
                    produzione (con production_id). */}
                {reprintTarget.payload.data?.production_id ? (
                  <button
                    type="button"
                    onClick={() => setReprintWithSample((v) => !v)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                      margin: '2px 0 12px', padding: '10px 12px', borderRadius: 12,
                      border: `1px solid ${reprintWithSample ? '#b45309' : '#cbd5e1'}`,
                      background: reprintWithSample ? '#fffbeb' : '#fff',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <span style={{
                      width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                      border: `2px solid ${reprintWithSample ? '#b45309' : '#94a3b8'}`,
                      background: reprintWithSample ? '#b45309' : '#fff',
                      color: '#fff', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 13, fontWeight: 900,
                    }}>{reprintWithSample ? '✓' : ''}</span>
                    <span style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                      🧪 Stampa anche l'etichetta del pasto campione (1 copia, HACCP)
                    </span>
                  </button>
                ) : null}
                {(() => {
                  const isPreviewReprint = printers.find((p) => p.id === reprintPrinterId)?.driver === 'preview';
                  return (
                    <IonButton
                      expand="block"
                      color={isPreviewReprint ? 'warning' : 'primary'}
                      disabled={busy || reprintCopies === null}
                      onClick={async () => {
                        if (!reprintTarget || reprintCopies === null) return;
                        const t = reprintTarget;
                        const n = reprintCopies;
                        const withSample = reprintWithSample && !!t.payload.data?.production_id;
                        setReprintTarget(null);
                        await reprintIdentical(t, n, reprintPrinterId);
                        // T-279: stessa azione → anche l'etichetta campione (1 copia).
                        if (withSample) await reprintCampione(t, reprintPrinterId);
                      }}
                      style={{ height: 52, '--border-radius': '14px', fontWeight: 700 } as React.CSSProperties}
                    >
                      {busy ? <IonSpinner name="dots" /> : (
                        <>
                          <IonIcon icon={isPreviewReprint ? eyeOutline : printOutline} slot="start" />
                          {isPreviewReprint
                            ? `Mostra ${reprintCopies}× anteprima (non stampa)`
                            : `Stampa ${reprintCopies}× cop${reprintCopies === 1 ? 'ia' : 'ie'}${reprintWithSample ? ' + campione' : ''}`}
                        </>
                      )}
                    </IonButton>
                  );
                })()}
              </>
            )}
          </IonContent>
        </IonModal>

        <IonAlert
          isOpen={materialReplacementPrompt !== null}
          header={`Hai già 2 lotti ${materialReplacementPrompt?.material_transformation === 'thaw' ? 'scongelati' : 'aperti'}`}
          message={materialReplacementPrompt
            ? `Per crearne un terzo, il più vecchio ${materialReplacementPrompt.batch_lot ?? ''}${lotActivityText(materialReplacementPrompt) ? ` (${lotActivityText(materialReplacementPrompt)})` : ''} verrà chiuso e la quantità residua (${materialReplacementPrompt.available.toFixed(2)} ${materialReplacementPrompt.uom}) registrata in uscita. Lo storico resterà consultabile. Vuoi continuare?`
            : ''}
          buttons={[
            {
              text: 'No, mantieni i lotti',
              role: 'cancel',
              handler: () => {
                confirmedMaterialReplacementKey.current = null;
                setMaterialReplacementPrompt(null);
              },
            },
            {
              text: 'Sì, chiudi il più vecchio',
              role: 'destructive',
              handler: () => {
                if (!materialReplacementPrompt) return;
                confirmedMaterialReplacementKey.current = materialReplacementPrompt.key;
                setMaterialReplacementPrompt(null);
                void commitMaterialOperation();
              },
            },
          ]}
        />

        <IonAlert
          isOpen={!!cancelTarget}
          header="Annulla preparazione"
          message={`Vuoi annullare "${(cancelTarget?.payload.data?.product as string) ?? 'questa preparazione'}"? Le giacenze dei lotti consumati verranno ripristinate.`}
          buttons={[
            { text: 'No', role: 'cancel', handler: () => setCancelTarget(null) },
            { text: 'Sì, annulla', role: 'destructive', handler: () => { if (cancelTarget) void cancelLabel(cancelTarget); } },
          ]}
          onDidDismiss={() => setCancelTarget(null)}
        />
        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={2500}
          onDidDismiss={() => setToast(null)}
        />

        {/* Editor template etichetta */}
        <LabelEditorModal
          isOpen={editorOpen}
          template={template}
          previewData={previewData}
          rasterOptions={labelRasterOptions}
          tenantId={labelTenantId}
          onDismiss={() => setEditorOpen(false)}
          onReset={() => { clearCustomTemplate(DEFAULT_TEMPLATE.id, labelTenantId); setTemplateVersion(v => v + 1); setToast("Layout predefinito ripristinato"); }}
          onSaved={(updated) => {
            // `pickTemplate` rilegge SEMPRE da `loadCustomTemplate(DEFAULT_TEMPLATE.id)`:
            // se l'editor stava modificando un template DB (id = uuid diverso),
            // salvare con `updated.id` scriveva una chiave che la load non leggeva
            // mai → "non si salva". Normalizziamo l'id alla chiave letta dalla load.
            saveCustomTemplate({ ...updated, id: DEFAULT_TEMPLATE.id }, labelTenantId);
            setEditorOpen(false);
            setTemplateVersion((v) => v + 1);
            setToast('Layout etichetta salvato');
          }}
          canSaveGlobal={!!profile?.is_platform_owner}
          onSaveGlobal={async (format, updated) => {
            try {
              await saveGlobalPreset(format, updated as unknown as Record<string, unknown>);
              setTemplateVersion((v) => v + 1); // rilegge i preset globali + ri-risolve
              setToast(`Default globale ${format} salvato per tutti gli account`);
            } catch (err) {
              setToast(err instanceof Error ? err.message : 'Salvataggio default globale fallito');
            }
          }}
        />
      </IonContent>
    </IonPage>
  );
}
